<template>
  <transition name="fade" @enter="handleEnter">
    <el-container v-if="isReady" class="new-collection-container" ref="container" :style="{ width: containerWidth, maxWidth: '100vw' }">
      <el-header>
        <div class="header-content">
          <h2>{{ isEditing ? 'Edit Course' : 'Create A New Course' }}</h2>
          <div class="button-group">
            <!-- <el-button type="primary" @click="savePublish">Publish</el-button> -->
            <!-- <el-button type="primary" @click="submitForm">Save</el-button> -->
            <el-button 
              type="primary" 
              plain
              @click="previewRead" 
              style="margin-left: 10px; border-color: #409eff; color: #409eff;"
            >Preview in Read Mode</el-button>
            
            <!-- <el-button @click="cancel">Cancel</el-button> -->
            <el-dropdown trigger="click" style="margin-left: 10px">
              <el-button type="text">
                <i class="el-icon-more" style="color: #409eff; transform: rotate(90deg);"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-if="isEditing && loadedCollectionStatus === 'PUBLISHED'" @click.native="retryLaunchEmails">
                  <i class="el-icon-message"></i> Retry launch emails
                </el-dropdown-item>
                <el-dropdown-item v-if="isEditing" :disabled="!collectionForm.collection_name_url_identifier" @click.native="shareCollection('slug')">
                  <i class="el-icon-link"></i> Copy slug link
                </el-dropdown-item>
                <el-dropdown-item v-if="isEditing" @click.native="shareCollection('id')">
                  <i class="el-icon-link"></i> Copy ID link
                </el-dropdown-item>
                <el-dropdown-item v-if="isEditing">
                  <div @click="showTransferDialog" style="color: #409eff;">
                    <i class="el-icon-user"></i>
                    Transfer to Other User
                  </div>
                </el-dropdown-item>
                <el-dropdown-item v-if="isEditing">
                  <div @click="confirmDelete" style="color: #f56c6c;">
                    <i class="el-icon-delete"></i>
                    Delete Course
                  </div>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </el-header>
      <el-main>
        <div class="form-wrapper">
          <el-form ref="collectionForm" :model="collectionForm" :rules="rules">
            <el-tabs v-model="activeTab">
              <el-tab-pane label="Basic Info" name="info">
                <div class="top-section">
                  <!-- Cover image section -->
                  <div class="cover-section">
                    <div class="cover-upload">
                      <el-upload
                        ref="coverUpload"
                        class="cover-uploader"
                        action="/"
                        :show-file-list="false"
                        :before-upload="beforeCoverUpload"
                      >
                        <img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="cover-image">
                        <div v-else class="cover-placeholder">
                          <i class="el-icon-plus cover-uploader-icon"></i>
                          <div class="cover-label">Cover Image</div>
                        </div>
                      </el-upload>
                      <div class="cover-actions">
                        <el-button
                          v-if="['Pro','Supreme'].includes(creator_functionality_level)"
                          size="mini"
                          type="primary"
                          class="ai-generate-btn"
                          @click.stop="handleAITask('Collection Cover Generation')"
                        >
                          <i class="el-icon-magic-stick"></i>
                          Generate Cover by AI
                        </el-button>
                        <div class="cover-hint">Recommended size: 600 × 840px (5:7)</div>
                      </div>
                      <div class="cover-prompt-section">
                        <label for="cover_prompt">Cover Prompt</label>
                        <textarea
                          v-model="collectionForm.cover_prompt"
                          id="cover_prompt"
                          rows="8"
                          placeholder="Enter the image-generation prompt used for this cover..."
                        ></textarea>
                        <div class="cover-hint">
                          Stored with the course so the cover can be reproduced or revised later.
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Title and description section -->
                  <div class="details-section">
                    <div class="title-section">
                      <label for="title">Title</label>
                      <input v-model="collectionForm.title" id="title" placeholder="Enter course title">
                    </div>

                    <div class="title-section">
                      <label for="sub_title">Subtitle</label>
                      <input v-model="collectionForm.sub_title" id="sub_title" placeholder="Enter course subtitle (optional)">
                    </div>
                    
                    <div class="description-section">
                      <div class="description-header">
                        <label for="description">Description</label>
                        <el-button
                          size="mini"
                          type="primary"
                          class="ai-polish-btn"
                          @click="handleAITask('Collection Description Polish')"
                        >
                          <i class="el-icon-magic-stick"></i>
                          AI Polish
                        </el-button>
                      </div>
                      <textarea
                        v-model="collectionForm.description"
                        id="description"
                        rows="8"
                        placeholder="Write a description for your course..."
                      ></textarea>
                    </div>
                    <div class="description-section">
                      <div class="description-header">
                        <label for="grand_take_away">Grand Takeaway</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          The highly distilled, single-most-important insight of the whole collection (2–3 sentences).
                        </span>
                      </div>
                      <textarea
                        v-model="collectionForm.grand_take_away"
                        id="grand_take_away"
                        rows="4"
                        placeholder="What is the one thing a reader should walk away from this collection having understood?"
                      ></textarea>
                    </div>
                    <div class="description-section">
                      <div class="description-header">
                        <label for="collection_knowledge">Certificate Knowledge Phrase</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          A short noun phrase describing what the course covers. Used on all three certificate types (Mastery / Completion / Record). e.g. "key concepts of cognitive psychology", "principles of sustainable business", "applications of AI in education".
                        </span>
                      </div>
                      <input
                        v-model="collectionForm.collection_knowledge"
                        id="collection_knowledge"
                        type="text"
                        maxlength="120"
                        placeholder="e.g. principles of sustainable business"
                      >
                    </div>

                    <!-- Author bio — short intro rendered in the subscriber "About the Curator" card -->
                    <div class="description-section">
                      <div class="description-header">
                        <label for="author_bio">About the Curator (short intro)</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          1–2 sentence introduction to the channel / editorial voice behind this collection. Rendered in the small "About the Curator" card at the bottom of the subscriber page.
                        </span>
                      </div>
                      <textarea
                        v-model="collectionForm.author_bio"
                        id="author_bio"
                        rows="3"
                        placeholder="A short introduction to the editorial voice behind this collection."
                      ></textarea>
                    </div>

                    <!-- Genre Tags — 1–2 free-form descriptive words about the collection's content paradigm -->
                    <div class="description-section">
                      <div class="description-header">
                        <label for="genre_tags">Genre Tags</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Two best descriptive words for this collection's paradigm (e.g. Primer, Field Guide, Playbook, Manifesto, Reframe, Discipline, Workbook, Anthology, Paradigm, Companion, Toolkit, Reportage, Industry Map…). Free-form — type any phrase and press Enter; vector search will handle synonymy. Cap at 2.
                        </span>
                      </div>
                      <el-select
                        v-model="collectionForm.genre_tags"
                        multiple
                        filterable
                        allow-create
                        default-first-option
                        :multiple-limit="2"
                        placeholder="Type a genre and press Enter (e.g. 'Field Guide')"
                        style="width: 100%;"
                        id="genre_tags"
                      />
                    </div>

                    <!-- Stable catalogue codes; labels are presentation copy. -->
                    <div class="description-section">
                      <div class="description-header">
                        <label for="catalogue_sections">Catalogue Sections</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Select every catalogue section this collection belongs to. Multiple selections are allowed.
                        </span>
                      </div>
                      <el-select
                        v-model="collectionForm.catalogue_section_codes"
                        multiple
                        filterable
                        placeholder="Select catalogue sections"
                        style="width: 100%;"
                        id="catalogue_sections"
                      >
                        <el-option
                          v-for="section in catalogueSectionOptions"
                          :key="section.value"
                          :label="section.label"
                          :value="section.value"
                        />
                      </el-select>
                    </div>
                    <div class="description-section">
                      <div class="description-header">
                        <label for="industry_codes">Industries</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Select the industries this collection is about, independently of its catalogue sections.
                        </span>
                      </div>
                      <el-select
                        v-model="collectionForm.industry_codes"
                        multiple
                        filterable
                        placeholder="Select industries"
                        style="width: 100%;"
                        id="industry_codes"
                      >
                        <el-option
                          v-for="industry in industryOptions"
                          :key="industry.value"
                          :label="industry.label"
                          :value="industry.value"
                        />
                      </el-select>
                    </div>

                    <!-- Key Questions — 3–5 critical questions the collection answers -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Key Questions</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          3–5 critical questions this collection answers. Sharp, curiosity-driven — designed for promotional copy and social cards.
                        </span>
                      </div>
                      <div v-if="(collectionForm.key_questions || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addKeyQuestion">+ Add question</el-button>
                      </div>
                      <div
                        v-for="(q, qi) in collectionForm.key_questions"
                        :key="'kq-' + qi"
                        style="display: flex; gap: 8px; margin-bottom: 6px;"
                      >
                        <input
                          v-model="collectionForm.key_questions[qi]"
                          placeholder="e.g. Why do most people fail at self-assessment — and what does the research say?"
                          style="flex: 1;"
                        />
                        <el-button size="mini" type="danger" plain @click="removeKeyQuestion(qi)">Remove</el-button>
                      </div>
                      <div v-if="(collectionForm.key_questions || []).length > 0 && (collectionForm.key_questions || []).length < 5">
                        <el-button size="mini" @click="addKeyQuestion">+ Add another question</el-button>
                      </div>
                    </div>

                    <!-- Key Concepts — 5–10 concept-level knowledge phrases -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Key Concepts</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          5–10 concept-level knowledge phrases users will learn. Concise terms, not full sentences (e.g. "Performance Identity", "Feedback Integration Loop").
                        </span>
                      </div>
                      <div v-if="(collectionForm.key_concepts || []).length === 0" style="margin-bottom: 8px;">
                        <el-button size="mini" @click="addKeyConcept">+ Add concept</el-button>
                      </div>
                      <div
                        v-for="(c, ci) in collectionForm.key_concepts"
                        :key="'kc-' + ci"
                        style="display: flex; gap: 8px; margin-bottom: 6px;"
                      >
                        <input
                          v-model="collectionForm.key_concepts[ci]"
                          placeholder="e.g. Cognitive Bias Blind Spot"
                          style="flex: 1;"
                        />
                        <el-button size="mini" type="danger" plain @click="removeKeyConcept(ci)">Remove</el-button>
                      </div>
                      <div v-if="(collectionForm.key_concepts || []).length > 0 && (collectionForm.key_concepts || []).length < 10">
                        <el-button size="mini" @click="addKeyConcept">+ Add another concept</el-button>
                      </div>
                    </div>

                    <div class="description-section">
                      <div class="description-header"><label>Special Labels</label></div>
                      <CollectionSpecialLabelPicker v-model="collectionForm.special_label_ids" />
                    </div>
                    <!-- Tracks — career/skill tracks this collection contributes credits toward -->
                    <div class="description-section">
                      <div class="description-header">
                        <label for="tracks">Tracks</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Which career / skill tracks this collection contributes credits toward when learners complete it. Pick up to 3. (e.g. a collection on AI management maps to both Management and Artificial Intelligence.)
                        </span>
                      </div>
                      <el-form-item prop="track_ids" class="no-margin">
                        <el-select
                          v-model="collectionForm.track_ids"
                          multiple
                          filterable
                          :multiple-limit="3"
                          placeholder="Select at least one track"
                          style="width: 100%;"
                          :loading="tracksLoading"
                          id="tracks"
                        >
                          <el-option
                            v-for="t in availableTracks"
                            :key="trackIdString(t)"
                            :label="t.track_name"
                            :value="trackIdString(t)"
                          />
                        </el-select>
                        <el-button type="text" :loading="tracksLoading" @click="createTrack">Can't find a track? Add one</el-button>
                      </el-form-item>
                    </div>

                    <!-- Editorial Brief (B4b) — structured fit qualifier -->
                    <div class="description-section">
                      <div class="description-header">
                        <label>Editorial Brief</label>
                        <span style="color: #909399; font-size: 12px; margin-left: 8px;">
                          Structured fit qualifier. Three single-sentence answers that help a reader decide whether this collection is for them.
                        </span>
                      </div>
                      <div style="margin-bottom: 8px;">
                        <label style="font-size: 13px; color: #606266;">Who it's for</label>
                        <textarea
                          v-model="collectionForm.editorial_brief.who_its_for"
                          rows="2"
                          placeholder="Describe the ideal reader in 1 sentence."
                          style="width: 100%;"
                        ></textarea>
                      </div>
                      <div style="margin-bottom: 8px;">
                        <label style="font-size: 13px; color: #606266;">What stands out</label>
                        <textarea
                          v-model="collectionForm.editorial_brief.what_stands_out"
                          rows="2"
                          placeholder="What makes this collection distinctive from alternatives? 1–2 sentences."
                          style="width: 100%;"
                        ></textarea>
                      </div>
                      <div style="margin-bottom: 8px;">
                        <label style="font-size: 13px; color: #606266;">Read if</label>
                        <textarea
                          v-model="collectionForm.editorial_brief.read_if"
                          rows="2"
                          placeholder="A single 'Read if…' sentence that snaps the fit question into focus."
                          style="width: 100%;"
                        ></textarea>
                      </div>
                      <label style="font-size: 12px; color: #606266;">
                        <input type="checkbox" v-model="collectionForm.editorial_brief.is_ai_generated" /> AI-generated (disclosure shown on the public page)
                      </label>
                    </div>
                    <div class="description-section" style="margin-bottom: 16px;">
                      <label>Announcement Blurb</label>
                      <span class="field-hint">
                        2–3 sentence teaser for batch announcement emails. Sentence 1: the angle. Sentence 2: the reader payoff. Sentence 3 (optional): a sharpening detail.
                      </span>
                      <textarea
                        v-model="collectionForm.announcement_blurb"
                        rows="3"
                        placeholder="E.g. Every number you see in a meeting has already been shaped by decisions you didn't make. This collection teaches the interrogation moves that let you catch what doesn't hold before you act on it."
                        style="width: 100%;"
                      ></textarea>
                    </div>
                    <div class="version-section">
                      <label for="version">Version Identifier</label>
                      <input
                        v-model="collectionForm.version_identifier"
                        id="version"
                        placeholder="Enter version identifier (if necessary)"
                      >
                    </div>
                    <div class="tag-section">
                      <label for="tags">Tags</label>
                      <div class="tags-input-container">
                        <div 
                          v-for="(tag, index) in collectionForm.tags" 
                          :key="index" 
                          class="tag-item"
                        >
                          {{ tag }}
                          <button @click.prevent.stop="removeTag(index)" type="button" class="remove-tag-btn">&times;</button>
                        </div>
                        <input 
                          v-model="currentTag" 
                          id="tags" 
                          placeholder="Add a tag, press Enter to add..."
                          @keydown.enter.prevent="addTag"
                          @keydown.backspace="handleBackspace"
                        >
                      </div>
                    </div>

                  </div>
                  <div class="save-section">
                    <el-button
                      type="primary"
                      size="medium"
                      @click="submitForm"
                      :loading="saving"
                    >
                      Save
                    </el-button>
                  </div>
                </div>

                <div class="illustration-section">
                  <div class="illustration-header">
                    <label>Module Master Illustration</label>
                    <span class="illustration-hint">Optional · 10 : 1 banner (e.g. 2000 × 200 px)</span>
                  </div>
                  <el-upload
                    ref="moduleIllustrationUpload"
                    class="illustration-uploader"
                    action="/"
                    :show-file-list="false"
                    :before-upload="beforeModuleIllustrationUpload"
                  >
                    <img v-if="collectionForm.module_illustration_url" :src="collectionForm.module_illustration_url" class="illustration-image">
                    <div v-else class="illustration-placeholder">
                      <i class="el-icon-plus illustration-uploader-icon"></i>
                      <div class="illustration-label">Click to upload (10 : 1)</div>
                    </div>
                  </el-upload>
                </div>
              </el-tab-pane>

              

              <el-tab-pane label="Groups" name="groups">
                <div class="content-header">
                  <h3>Card Groups</h3>
                  <div class="content-stats">
                    <span v-if="collectionForm.phases.length" class="stat-item">
                      <i class="el-icon-guide"></i>
                      {{ collectionForm.phases.length }} Phases
                    </span>
                    <span class="stat-item">
                      <i class="el-icon-folder"></i>
                      {{ collectionForm.groups.length }} Groups
                    </span>
                    <span class="stat-item">
                      <i class="el-icon-collection"></i>
                      {{ totalKnowits }} Stacks
                    </span>
                    <span class="stat-item">
                      <i class="el-icon-document"></i>
                      {{ collectionForm.content_count || 0 }} Cards
                    </span>
                  </div>
                </div>
                <div class="content-actions">
                  <el-button 
                    v-if="['Pro','Supreme'].includes(creator_functionality_level)"
                    size="small" 
                    type="primary" 
                    class="ai-structure-btn"
                    @click="handleAITask('Collection Structure Generation')"
                  >
                    <i class="el-icon-magic-stick"></i>
                    Get Structure from AI
                  </el-button>
                  <el-button 
                    size="small"
                    type="default"
                    @click="showReorderDialog"
                  >
                    <i class="el-icon-sort"></i>
                    Reorder Groups
                  </el-button>

                  <!-- Add AI Generation button for Supreme users -->
                  <el-button
                    v-if="creator_functionality_level === 'Supreme'"
                    size="small"
                    type="primary"
                    @click="showAIGenerationDialog"
                  >
                    <i class="el-icon-magic-stick"></i>
                    Arrange AI Generation for Stacks
                  </el-button>
                  <el-button type="default" size="small" @click="showBatchDeleteDialog">
                    <i class="el-icon-delete" style="color: red;"></i>
                    <span style="color: red;">Batch Delete Stacks</span>
                  </el-button>

                  <el-divider direction="vertical"></el-divider>

                  <el-button size="small" type="warning" plain @click="makeAllSubscribersOnly">
                    <i class="el-icon-lock"></i>
                    All Behind Paywall
                  </el-button>
                  <el-button size="small" plain @click="makeAllFree">
                    <i class="el-icon-unlock"></i>
                    All Free
                  </el-button>
                  <el-button size="small" :type="selectMode ? 'primary' : 'default'" @click="toggleSelectMode">
                    <i class="el-icon-finished"></i>
                    {{ selectMode ? 'Exit Select Mode' : 'Select Mode' }}
                  </el-button>
                  <template v-if="selectMode">
                    <el-button size="small" @click="selectAll">Select All</el-button>
                    <el-button size="small" @click="deselectAll">Deselect All</el-button>
                    <el-button size="small" type="warning" plain @click="makeSelectedSubscribersOnly" :disabled="selectedGroups.length === 0 && selectedKnowitsList.length === 0">
                      <i class="el-icon-lock"></i>
                      Lock Selected
                    </el-button>
                    <el-button size="small" plain @click="makeSelectedFree" :disabled="selectedGroups.length === 0 && selectedKnowitsList.length === 0">
                      <i class="el-icon-unlock"></i>
                      Unlock Selected
                    </el-button>
                  </template>

                </div>
                <div v-if="collectionForm.phases.length" class="phases-overview">
                  <div class="phases-overview-header">
                    <div>
                      <h4>Phases</h4>
                      <p>How the course groups are organized into learning phases.</p>
                    </div>
                  </div>
                  <div class="phase-list">
                    <div
                      v-for="(phase, phaseIndex) in collectionForm.phases"
                      :key="'phase-' + phaseIndex"
                      class="phase-card"
                    >
                      <div class="phase-number">Phase {{ phaseIndex + 1 }}</div>
                      <div class="phase-details">
                        <h5>{{ phase.title || 'Untitled Phase' }}</h5>
                        <p v-if="phase.description">{{ phase.description }}</p>
                        <div v-if="phase.group_indices.length" class="phase-groups">
                          <span
                            v-for="(groupIndex, groupPosition) in phase.group_indices"
                            :key="'phase-' + phaseIndex + '-group-' + groupIndex + '-' + groupPosition"
                            :class="['phase-group-chip', { 'is-missing': !collectionForm.groups[groupIndex] }]"
                          >
                            Group {{ groupIndex + 1 }}
                            <template v-if="collectionForm.groups[groupIndex]">
                              · {{ collectionForm.groups[groupIndex].title || 'Untitled Group' }}
                            </template>
                            <template v-else> · Not found</template>
                          </span>
                        </div>
                        <span v-else class="phase-empty-groups">No groups assigned</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="groups-container">
                  <!-- Groups List -->
                  <div class="groups-list">
                    <!-- Add insert button before the first group -->
                    <div class="insert-group-container">
                      <button 
                        class="insert-group-button"
                        @click="insertGroup(0)"
                      >
                        <i class="el-icon-plus"></i>
                        Add Group Here
                      </button>
                    </div>

                    <div 
                      v-for="(group, groupIndex) in collectionForm.groups" 
                      :key="groupIndex"
                      class="group-wrapper"
                    >
                      <div class="group-card">
                        <div class="group-card-header">
                          <span class="group-number">Group #{{ groupIndex + 1 }}</span>
                          <template v-if="group.isEditing">
                            <input
                              v-model="group.title"
                              placeholder="Group Title"
                              class="group-title-input"
                              @keydown.enter.prevent.stop="finishEditingTitle(group)"
                              @blur="handleGroupTitleBlur(group, $event)"
                              ref="titleInput"
                            >
                            <input
                              v-model="group.group_sub_title"
                              placeholder="Group Subtitle (optional)"
                              class="group-title-input group-subtitle-input"
                              @keydown.enter.prevent.stop="finishEditingTitle(group)"
                              @blur="handleGroupTitleBlur(group, $event)"
                            >
                          </template>
                          <span
                            v-else
                            class="group-title-label"
                            @dblclick="startEditingTitle(group)"
                          >
                            {{ group.title || 'Untitled Group' }}
                            <span v-if="group.group_sub_title" class="group-subtitle-label">— {{ group.group_sub_title }}</span>
                            <el-tag size="mini" type="info" style="margin-left: 8px;">
                              {{ getTotalCardsForGroup(group) }} stacks
                            </el-tag>
                          </span>
                          <el-tooltip :content="group.subscribers_only ? 'Click to make free' : 'Click to make subscribers only'" placement="top">
                            <el-button
                              :type="group.subscribers_only ? 'warning' : 'default'"
                              size="mini"
                              :icon="group.subscribers_only ? 'el-icon-lock' : 'el-icon-unlock'"
                              @click="toggleGroupPaywall(groupIndex)"
                              circle
                              class="paywall-toggle-btn"
                            ></el-button>
                          </el-tooltip>
                          <el-tag v-if="group.subscribers_only" type="warning" size="mini" effect="plain" style="margin-right: 4px;">
                            Subscribers Only
                          </el-tag>
                          <el-checkbox
                            v-if="selectMode"
                            :value="selectedGroups.includes(groupIndex)"
                            @change="toggleGroupSelection(groupIndex)"
                            style="margin-right: 4px;"
                          ></el-checkbox>
                          <el-button
                            type="danger"
                            size="mini"
                            icon="el-icon-delete"
                            @click="removeGroup(groupIndex)"
                            circle
                            class="delete-group-btn"
                          ></el-button>
                        </div>

                        <div class="group-card-content">
                          <div class="knowit-list">
                            <draggable 
                              v-model="group.knowits"
                              group="knowits"
                              handle=".drag-handle"
                              @end="handleKnowitOrderEnd"
                            >
                              <div class="knowit-item" v-for="(knowit, knowitIndex) in group.knowits" :key="getKnowitDragKey(knowit, groupIndex, knowitIndex)">
                                <div class="drag-handle">
                                  <i class="el-icon-rank"></i>
                                </div>
                                <div class="knowit-number">Stack #{{ knowitIndex + 1 }}</div>
                                <div class="knowit-title">
                                  {{ (knowit.post_info && knowit.post_info.title) || 'Untitled Stack' }}
                                  <el-tag size="mini" type="info" v-if="knowit.post_info && knowit.post_info.answers && knowit.post_info.answers.length">
                                    {{ knowit.post_info.answers.length }} cards
                                  </el-tag>
                                </div>
                                <el-tooltip :content="group.subscribers_only ? 'Locked by group' : (knowit.subscribers_only ? 'Click to make free' : 'Click to make subscribers only')" placement="top">
                                  <button
                                    class="knowit-lock-btn"
                                    :class="{ 'is-locked': knowit.subscribers_only || group.subscribers_only, 'is-inherited': group.subscribers_only }"
                                    @click.stop.prevent="!group.subscribers_only && toggleKnowitPaywall(groupIndex, knowitIndex)"
                                    type="button"
                                    :disabled="group.subscribers_only"
                                  >
                                    <i :class="(knowit.subscribers_only || group.subscribers_only) ? 'el-icon-lock' : 'el-icon-unlock'"></i>
                                  </button>
                                </el-tooltip>
                                <el-checkbox
                                  v-if="selectMode"
                                  :value="isKnowitSelected(groupIndex, knowitIndex)"
                                  @change="toggleKnowitSelection(groupIndex, knowitIndex)"
                                  style="margin-right: 4px;"
                                ></el-checkbox>
                                <button
                                  class="knowit-edit-btn"
                                  @click.stop.prevent="handleKnowitEdit(knowit.knowit_id, groupIndex, knowitIndex)"
                                  type="button"
                                >
                                  <i class="el-icon-edit"></i>
                                </button>
                                <button class="knowit-delete-btn" @click="removeKnowit(groupIndex, knowitIndex)" type="button">
                                  <i class="el-icon-delete"></i>
                                </button>
                              </div>
                            </draggable>

                            <div style="display: flex; gap: 10px; width: 100%;">
                              <button class="knowit-add-button" @click.prevent="addKnowit(groupIndex)" type="button">
                                <i class="el-icon-plus"></i>
                                Add Stack
                              </button>

                              <button
                                v-if="['Pro','Supreme'].includes(creator_functionality_level)"
                                class="knowit-add-button ai-suggest-button"
                                @click.prevent="handleAITask('Group Knowits Suggestion', groupIndex)"
                                type="button"
                              >
                                <i class="el-icon-magic-stick"></i>
                                Get AI Suggestions for this group's stacks
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Only show insert button if not the last group -->
                      <div class="insert-group-container" v-if="groupIndex < collectionForm.groups.length - 1">
                        <button 
                          class="insert-group-button"
                          @click="insertGroup(groupIndex + 1)"
                          type="button"
                        >
                          <i class="el-icon-plus"></i>
                          Add Group Here
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Add Group Button -->
                  <button
                    @click="addGroup"
                    type="button"
                    class="knowit-add-button"
                  >
                      <i class="el-icon-plus"></i>
                    Add New Group
                  </button>
                </div>
              </el-tab-pane>

              <el-tab-pane label="Course Settings" name="settings">
                <div class="settings-flex-row">
                  <div class="settings-container">
                    <div class="setting-item">
                      <span class="setting-label">
                        Entire Course Behind Paywall
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="When enabled, non-subscribers cannot view any content in this course. They will be prompted to subscribe before entering."
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <el-switch
                        v-model="collectionForm.subscribers_only"
                        active-color="#e6a23c"
                        inactive-color="#dcdfe6"
                      ></el-switch>
                    </div>
                    <el-alert
                      v-if="collectionForm.subscribers_only"
                      title="This entire course is restricted to platform subscribers. Non-subscribers cannot view any content. Group and post-level paywall settings are still applied for finer control."
                      type="warning"
                      :closable="false"
                      show-icon
                      style="margin-bottom: 16px;"
                    ></el-alert>
                    <div class="setting-item">
                      <span class="setting-label">
                        Publicity Status
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="Current publicity status of your course. Attention: if you take your course private from published, users who have already purchased this course will still be able to read it."
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <el-select
                        v-model="collectionForm.status"
                        class="publicity-status-select"
                        placeholder="Select publicity status"
                      >
                        <el-option
                          label="Private"
                          value="PRIVATE"
                        >
                          <span class="status-option-title">Private</span>
                          <span class="status-option-description">Hidden from visitors</span>
                        </el-option>
                        <el-option
                          label="Pre-order"
                          value="PREORDER"
                        >
                          <span class="status-option-title">Pre-order</span>
                          <span class="status-option-description">Public intro page, follow + 50% pre-order, content locked</span>
                        </el-option>
                        <el-option
                          label="Published"
                          value="PUBLISHED"
                        >
                          <span class="status-option-title">Published</span>
                          <span class="status-option-description">Public page and readable content</span>
                        </el-option>
                      </el-select>
                    </div>
                    <el-alert
                      v-if="isPreorderStatus(collectionForm.status)"
                      :title="'Pre-order keeps the course page visible, but visitors cannot read the course content until it is published. Buyers reserve at 50% off (' + formatMoney(getPreorderCollectionPrice()) + ') and subscribers can reserve with their plan.'"
                      type="info"
                      :closable="false"
                      show-icon
                      style="margin-bottom: 16px;"
                    ></el-alert>
                    <div v-if="isPreorderStatus(collectionForm.status)" class="setting-item teaser-release-date-setting">
                      <span class="setting-label">Estimated launch date</span>
                      <el-date-picker
                        v-model="collectionForm.teaser_release_date"
                        type="date"
                        value-format="yyyy-MM-dd"
                        placeholder="Select an estimated launch date"
                        :picker-options="{ disabledDate: date => date.getTime() < Date.now() - 86400000 }"
                      ></el-date-picker>
                    </div>
                    <div class="setting-item">
                      <span class="setting-label">
                        Show in public search?
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="If turned on, your collection can appear in users' public search results"
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <el-switch
                        v-model="collectionForm.settings.shown_in_public_search"
                        active-color="#13ce66"
                        inactive-color="#ff4949">
                      </el-switch>
                    </div>
                    <div class="setting-block country-scope-setting">
                      <div class="setting-block-header">
                        <span class="setting-label">
                          Country availability
                          <el-tooltip
                            class="item"
                            effect="dark"
                            content="Leave open to all countries, allow only selected countries, or block selected countries. Country checks use both the reader's selected country and IP-origin country when available."
                            placement="top"
                          >
                            <i class="el-icon-info setting-info-icon"></i>
                          </el-tooltip>
                        </span>
                        <span class="setting-helper">Controls public visibility, search, and read access by country.</span>
                      </div>
                      <el-radio-group v-model="collectionForm.country_scope.mode" class="country-scope-mode">
                        <el-radio-button label="ALL">All countries</el-radio-button>
                        <el-radio-button label="WHITELIST">Whitelist</el-radio-button>
                        <el-radio-button label="BLACKLIST">Blacklist</el-radio-button>
                      </el-radio-group>
                      <el-select
                        v-if="collectionForm.country_scope.mode !== 'ALL'"
                        v-model="collectionForm.country_scope.country_codes"
                        multiple
                        filterable
                        collapse-tags
                        placeholder="Select countries"
                        class="country-scope-select"
                      >
                        <el-option
                          v-for="country in countryOptions"
                          :key="country.value"
                          :label="country.label"
                          :value="country.value"
                        >
                          <span>{{ country.label }}</span>
                          <span class="country-option-code">{{ country.value }}</span>
                        </el-option>
                      </el-select>
                      <div class="audience-all-note" v-if="collectionForm.country_scope.mode === 'ALL'">
                        Currently available in all countries.
                      </div>
                    </div>
                    <div class="setting-item">
                      <span class="setting-label">
                        Visible in recommendation?
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="If turned off, this collection will not appear proactively in the Plaza, but it can still be found through public search when public search is enabled"
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <el-switch
                        v-model="collectionForm.settings.visible_in_recommendation"
                        active-color="#13ce66"
                        inactive-color="#ff4949">
                      </el-switch>
                    </div>
                    <div class="setting-block narrative-mode-setting">
                      <div class="setting-block-header">
                        <span class="setting-label">
                          Narrative mode
                          <el-tooltip
                            class="item"
                            effect="dark"
                            content="Controls whether each card presents its answer before or after the supporting explanation."
                            placement="top"
                          >
                            <i class="el-icon-info setting-info-icon"></i>
                          </el-tooltip>
                        </span>
                        <span class="setting-helper">Choose how the course reveals answers in Read Mode.</span>
                      </div>
                      <el-radio-group v-model="collectionForm.narrative_mode" class="narrative-mode-options">
                        <el-radio label="answer_first">
                          <span class="narrative-mode-label">Answer first</span>
                          <span class="narrative-mode-helper">Lead with the answer, then provide the supporting explanation.</span>
                        </el-radio>
                        <el-radio label="answer_last">
                          <span class="narrative-mode-label">Answer last</span>
                          <span class="narrative-mode-helper">Build through the explanation, then reveal the answer at the end.</span>
                        </el-radio>
                      </el-radio-group>
                    </div>
                    <div class="setting-block">
                      <div class="setting-block-header">
                        <span class="setting-label">
                          Target learner level
                          <el-tooltip
                            class="item"
                            effect="dark"
                            content="Leave all unchecked to make this course suitable for all learners. Select levels only when discovery should be limited to those users."
                            placement="top"
                          >
                            <i class="el-icon-info setting-info-icon"></i>
                          </el-tooltip>
                        </span>
                        <span class="setting-helper">Controls search and recommendation targeting.</span>
                      </div>
                      <el-checkbox-group v-model="collectionForm.target_audience_stages" class="audience-checkbox-group">
                        <el-checkbox
                          v-for="option in educationAudienceOptions"
                          :key="option.value"
                          :label="option.value"
                        >
                          <span class="audience-option-label">{{ option.label }}</span>
                          <span class="audience-option-helper">{{ option.helper }}</span>
                        </el-checkbox>
                      </el-checkbox-group>
                      <div class="audience-all-note" v-if="!(collectionForm.target_audience_stages || []).length">
                        Currently visible to all learner levels.
                      </div>
                    </div>
                    <div class="setting-item">
                      <span class="setting-label">
                        Notify Followers
                        <el-tooltip
                          class="item"
                          effect="dark"
                          content="Email followers of this curator about this course. The backend skips followers already notified for this course."
                          placement="top"
                        >
                          <i class="el-icon-info setting-info-icon"></i>
                        </el-tooltip>
                      </span>
                      <el-button
                        type="primary"
                        plain
                        size="small"
                        :loading="notifyingFollowers"
                        :disabled="!isEditing || collectionForm.status !== 'PUBLISHED'"
                        @click="notifyFollowers"
                      >
                        Notify Followers
                      </el-button>
                    </div>

                  </div>
                  <div class="save-section">
                      <el-button 
                        type="primary" 
                        size="medium"
                        @click="submitForm"
                        :loading="saving"
                      >
                        Save
                      </el-button>
                    </div>
                </div>
              </el-tab-pane>
              <el-tab-pane label="Version Updates" name="version-updates" v-if="isEditing">
                <div class="versions-panel">
                  <div class="versions-header">
                    <div>
                      <h3>Version Updates</h3>
                      <p>Record what changed in each published deck version, then notify followers, current readers, and users who starred the course.</p>
                    </div>
                    <div class="versions-toolbar">
                      <el-button
                        icon="el-icon-refresh"
                        :loading="versionLoading"
                        @click="fetchVersionUpdates"
                      >
                        Refresh
                      </el-button>
                      <el-button
                        icon="el-icon-message"
                        :loading="versionNotifyLoading"
                        :disabled="selectedVersionUpdateIds.length === 0"
                        @click="notifySelectedVersionUpdates"
                      >
                        Notify audience
                      </el-button>
                      <el-button
                        type="primary"
                        icon="el-icon-plus"
                        @click="openVersionDialog()"
                      >
                        Add Update
                      </el-button>
                    </div>
                  </div>

                  <div v-if="versionLoading" class="version-loading">
                    <i class="el-icon-loading"></i> Loading version updates...
                  </div>
                  <div v-else-if="versionUpdates.length === 0" class="no-data versions-empty">
                    <i class="el-icon-document-copy"></i>
                    <p>No version updates yet</p>
                  </div>
                  <div v-else class="version-list">
                    <div v-for="item in versionUpdates" :key="item.id" class="version-item">
                      <div class="version-dot"></div>
                      <div class="version-card">
                        <div class="version-card-header">
                          <div class="version-meta">
                            <el-checkbox
                              v-model="selectedVersionUpdateIds"
                              :label="item.id"
                              class="version-select"
                            >
                              Select
                            </el-checkbox>
                            <el-tag type="primary" size="small">{{ item.version }}</el-tag>
                            <span class="version-date">{{ formatVersionTimestamp(item.release_time || item.create_time) }}</span>
                          </div>
                          <div class="version-actions">
                            <el-button type="text" size="mini" @click="openVersionDialog(item)">Edit</el-button>
                            <el-button type="text" size="mini" class="delete-link" @click="deleteVersionUpdate(item)">Delete</el-button>
                          </div>
                        </div>
                        <h4 v-if="item.title">{{ item.title }}</h4>
                        <div class="version-notes" v-html="renderVersionMarkdown(item.updates)"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </el-tab-pane>
              <el-tab-pane label="Pricing" name="pricing">
                <div class="pricing-flex-row">
                  <div class="pricing-container">
                    <div class="price-section">
                      <label for="has_custom_price">Custom Price</label>
                      <el-switch
                        v-model="collectionForm.has_custom_price"
                        id="has_custom_price"
                        active-text="Use custom USD price"
                        inactive-text="Use universal price"
                      ></el-switch>
                    </div>
                    <div class="price-section">
                      <label>Universal Price</label>
                      <span class="computed-price">{{ formatMoney(getUniversalCollectionPrice()) }}</span>
                      <span style="margin-left: 10px; color: #909399">
                        Based on course credits: 1 credit = $1 USD.
                      </span>
                    </div>
                    <div v-if="collectionForm.has_custom_price" class="price-section">
                      <label for="price">Price</label>
                      <input
                        v-model="collectionForm.usd_price"
                        id="price"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Enter price"
                      >
                      <span style="margin-left: 10px; color: #909399">USD</span>
                    </div>
                    <div class="price-section single-purchase-section">
                      <label for="single_purchase_enabled">Disable Single Purchase</label>
                      <el-switch
                        id="single_purchase_enabled"
                        v-model="collectionForm.single_purchase_enabled"
                        :active-value="false"
                        :inactive-value="true"
                        active-color="#409eff"
                        inactive-color="#dcdfe6"
                      ></el-switch>
                    </div>
                    <div class="price-section" style="margin-top: 16px;">
                      <label for="base_credits">Credits</label>
                      <input
                        v-model.number="collectionForm.base_credits"
                        id="base_credits"
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Enter base credits"
                      >
                      <span style="margin-left: 10px; color: #909399">
                        Awarded on completion (multiplied by completion score). Suggested: 1 credit per ~30 min of reading.
                      </span>
                    </div>
                  </div>
                  <div class="save-section">
                      <el-button
                        type="primary"
                        size="medium"
                        @click="submitForm"
                        :loading="saving"
                      >
                        Save
                      </el-button>
                    </div>
                </div>
              </el-tab-pane>
              <el-tab-pane label="Expersist" name="skill-files" v-if="isEditing">
                <div class="skill-files-panel">
                  <h3>Expersist</h3>
                  <p>Files are saved as private Git versions in Dao of Knowing's Expersist account, in the Learning First Space, and compiled into Experiences. Learners with access to this collection can download the Skill and give their AI assistant a personal read link.</p>
                  <p v-if="expersistStatus && !expersistStatus.configured" class="skill-file-error">Expersist is not configured on the server yet, so files cannot be saved.</p>

                  <section v-for="part in expersistParts" :key="part.kind" class="expersist-block">
                    <h4>{{ part.title }}</h4>
                    <p class="skill-file-hint">{{ part.hint }}</p>
                    <div class="skill-file-actions">
                      <label class="skill-file-picker" :class="{ disabled: !!expersistUploading }">
                        {{ expersistUploading === part.kind ? 'Saving…' : 'Choose files' }}
                        <input type="file" accept=".md,.markdown,.txt" multiple :disabled="!!expersistUploading" @change="publishExpersistFiles($event, part.kind)" />
                      </label>
                      <label v-if="part.kind === 'skill'" class="skill-file-picker" :class="{ disabled: !!expersistUploading }">
                        Choose folder
                        <input type="file" webkitdirectory directory multiple :disabled="!!expersistUploading" @change="publishExpersistFiles($event, part.kind)" />
                      </label>
                      <el-button v-if="part.kind === 'skill' && expersistEntry('skill')" size="small" @click="downloadCollectionSkill">Download Skill</el-button>
                    </div>
                    <div v-if="expersistEntry(part.kind)" class="skill-file-status">
                      <div>Saved in Git{{ expersistEntry(part.kind).commit ? ' · ' + expersistEntry(part.kind).commit.slice(0, 8) : '' }}: {{ (expersistEntry(part.kind).files || []).join(', ') }}</div>
                      <div :class="{ 'skill-file-error': expersistCompileState(part.kind).error }">{{ expersistCompileState(part.kind).label }}</div>
                    </div>
                  </section>

                  <section class="expersist-block">
                    <h4>AI link</h4>
                    <p class="skill-file-hint">Learners get a personal link, valid for seven days, that their AI assistant can read: the Skill text followed by the compiled Experiences. Open yours to preview it.</p>
                    <el-button size="small" :disabled="!expersistEntry('skill') && !expersistEntry('experiences')" @click="openExpersistAILink">Open AI link</el-button>
                  </section>
                  <p class="skill-file-hint">Each upload replaces that part with a new Git version: up to 100 files and 2 MiB. Experience compilation reads up to 64 KiB of Markdown and text.</p>
                </div>
              </el-tab-pane>
              <el-tab-pane label="Test Bank" name="testbank" v-if="isEditing">
                <TestBankManager
                  ref="testBankManager"
                  :collection-id="collectionId"
                  :collection-form="collectionForm"
                  @stats-updated="handleTestBankStatsUpdated"
                />
              </el-tab-pane>
              <el-tab-pane label="Examination" name="examination" v-if="isEditing">
                <ExamConfigurationEditor
                  :collection-id="collectionId"
                  :test-bank-count="testBankCount"
                />
              </el-tab-pane>
              <el-tab-pane label="Exam Analytics" name="exam-analytics" v-if="isEditing">
                <ExamAnalyticsDashboard
                  :collection-id="collectionId"
                />
              </el-tab-pane>
              <el-tab-pane label="Social Cards" name="social-cards">
                <div class="social-cards-container" ref="socialCardsContainer" @mouseover="handleCardMouseOver" @mouseout="handleCardMouseOut">
                  <p style="color: #909399; font-size: 13px; margin-bottom: 20px;">
                    Edit card data on the right, preview renders live on the left. Hover any card to download.
                  </p>

                  <!-- ==================== Title Card ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <h4 class="card-row-title">Title Card</h4>
                    <div class="card-row-content">
                      <div class="card-row-preview">
                        <div class="sns-card sns-card-title" :ref="'titleCard'" :class="cardDensityClass((collectionForm.title || '') + ' ' + (collectionForm.sub_title || ''))">
                          <div class="sns-card-inner">
                            <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                            <div class="sns-title-main">{{ collectionForm.title }}</div>
                            <div class="sns-title-sub" v-if="collectionForm.sub_title">{{ collectionForm.sub_title }}</div>
                            <div class="sns-card-footer">
                              <div class="sns-title-stats">
                                <span v-if="collectionForm.groups && collectionForm.groups.length">{{ collectionForm.groups.length }} {{ collectionForm.groups.length === 1 ? 'Section' : 'Sections' }}</span>
                                <span v-if="collectionForm.groups && collectionForm.groups.length && totalKnowits"> · </span>
                                <span v-if="totalKnowits">{{ totalKnowits }} {{ totalKnowits === 1 ? 'Session' : 'Sessions' }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="card-row-editor">
                        <p style="color: #909399; font-size: 12px;">Title and subtitle are edited in the Basic Info tab. This card auto-renders from that data.</p>
                      </div>
                    </div>
                  </div>

                  <!-- ==================== Cover Card ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;" v-if="collectionForm.cover_url">
                    <h4 class="card-row-title">Cover Card</h4>
                    <div class="card-row-content">
                      <div class="card-row-preview">
                        <div class="sns-card sns-card-cover" ref="coverCard">
                          <div class="sns-card-inner sns-card-inner--cover">
                            <div class="sns-cover-image-area">
                              <img :src="collectionForm.cover_url" class="sns-cover-main-img" />
                            </div>
                            <div class="sns-cover-footer">
                              <div class="sns-cover-brand">
                                <img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="card-row-editor">
                        <p style="color: #909399; font-size: 12px;">The cover image rendered inside a branded card frame. Hover to download in different aspect ratios for each platform.</p>
                      </div>
                    </div>
                  </div>

                  <!-- ==================== Gold Quote Cards ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <h4 class="card-row-title">Gold Quote Cards <span style="color:#909399; font-weight:normal;" v-if="collectionForm.gold_quotes && collectionForm.gold_quotes.length">({{ collectionForm.gold_quotes.length }})</span></h4>
                    <div
                      v-for="(gq, gi) in collectionForm.gold_quotes"
                      :key="'gq-' + gi"
                      class="card-row-content"
                      style="margin-bottom: 16px;"
                    >
                      <div class="card-row-preview">
                        <div class="sns-card sns-card-quote" :ref="'goldCard' + gi" :class="cardDensityClass((gq.insight || '') + ' ' + (gq.detail || ''))">
                          <div class="sns-card-inner">
                            <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                            <div class="sns-card-footer">
                              <div class="sns-collection-title"><svg class="sns-collection-icon" viewBox="0 0 24 28" fill="none" stroke="#606266" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="19.6" rx="2"/><path d="M5 2h12a2 2 0 0 1 2 2v0"/><path d="M3.5 4h13a2 2 0 0 1 2 2v0"/></svg>{{ collectionForm.title }}</div>
                              <div class="sns-review-label-corner">Gold Quote</div>
                            </div>
                            <div class="sns-quote-insight"><span class="sns-quote-mark-inline">&ldquo;</span><span v-html="renderMd(gq.insight)"></span></div>
                            <div class="sns-quote-detail" v-if="gq.detail" v-html="renderMd(gq.detail)"></div>
                          </div>
                        </div>
                      </div>
                      <div class="card-row-editor">
                        <textarea v-model="gq.insight" placeholder="Insight — the golden sentence (≤25 words)" rows="2" style="width: 100%; margin-bottom: 8px;"></textarea>
                        <textarea v-model="gq.detail" placeholder="Detail — 2–3 sentences unpacking the insight" rows="3" style="width: 100%; margin-bottom: 8px;"></textarea>
                        <div style="text-align: right;">
                          <el-button size="mini" type="danger" plain @click="removeGoldQuote(gi)">Remove</el-button>
                        </div>
                      </div>
                    </div>
                    <div v-if="!collectionForm.gold_quotes || collectionForm.gold_quotes.length === 0" class="card-row-content">
                      <div class="card-row-preview card-row-empty">No gold quotes yet</div>
                      <div class="card-row-editor"></div>
                    </div>
                    <el-button v-if="!collectionForm.gold_quotes || collectionForm.gold_quotes.length < 3" size="mini" @click="addGoldQuote">+ Add gold quote</el-button>
                  </div>

                  <!-- ==================== Walkaway List Card ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <h4 class="card-row-title">Walkaway List Card</h4>
                    <div class="card-row-content">
                      <div class="card-row-preview">
                        <div v-if="collectionForm.walkaway_artifacts && collectionForm.walkaway_artifacts.length > 0" class="sns-card sns-card-list" :ref="'walkawayCard'" :class="walkawayDensityClass">
                          <div class="sns-card-inner">
                            <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                            <div class="sns-card-footer">
                              <div class="sns-collection-title"><svg class="sns-collection-icon" viewBox="0 0 24 28" fill="none" stroke="#606266" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="19.6" rx="2"/><path d="M5 2h12a2 2 0 0 1 2 2v0"/><path d="M3.5 4h13a2 2 0 0 1 2 2v0"/></svg>{{ collectionForm.title }}</div>
                              <div class="sns-review-label-corner">Walkaway</div>
                            </div>
                            <div class="sns-list-title">What You'll Walk Away With</div>
                            <div class="sns-list-items">
                              <div v-for="(line, li) in collectionForm.walkaway_artifacts" :key="'wa-' + li" class="sns-list-item">
                                <span class="sns-list-number">{{ extractCount(line) }}</span>
                                <span class="sns-list-text">{{ extractRest(line) }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="card-row-empty">No walkaway lines yet</div>
                      </div>
                      <div class="card-row-editor">
                        <div style="margin-bottom: 4px; font-size: 12px; color: #909399;">Format: [count] [artifact-noun] [what-for]</div>
                        <div v-for="(line, li) in collectionForm.walkaway_artifacts" :key="'wai-' + li" style="display: flex; gap: 8px; margin-bottom: 6px;">
                          <input v-model="collectionForm.walkaway_artifacts[li]" placeholder="e.g. 5 mental models for thinking with AI" style="flex: 1;" />
                          <el-button size="mini" type="danger" plain @click="removeWalkawayArtifact(li)">Remove</el-button>
                        </div>
                        <el-button v-if="!collectionForm.walkaway_artifacts || collectionForm.walkaway_artifacts.length < 8" size="mini" @click="addWalkawayArtifact">+ Add line</el-button>
                      </div>
                    </div>
                  </div>

                  <!-- ==================== Editorial Review Cards ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <h4 class="card-row-title">Editorial Review Cards <span style="color:#909399; font-weight:normal;" v-if="collectionForm.editorial_reviews && collectionForm.editorial_reviews.length">({{ collectionForm.editorial_reviews.length }})</span></h4>
                    <div
                      v-for="(er, eri) in collectionForm.editorial_reviews"
                      :key="'er-' + eri"
                      class="card-row-content"
                      style="margin-bottom: 16px;"
                    >
                      <div class="card-row-preview">
                        <div class="sns-card sns-card-review" :class="cardDensityClass((er.headline || '') + ' ' + (er.body || ''))">
                          <div class="sns-card-inner" style="position: relative;">
                            <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                            <div class="sns-card-footer">
                              <div class="sns-collection-title"><svg class="sns-collection-icon" viewBox="0 0 24 28" fill="none" stroke="#606266" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="19.6" rx="2"/><path d="M5 2h12a2 2 0 0 1 2 2v0"/><path d="M3.5 4h13a2 2 0 0 1 2 2v0"/></svg>{{ collectionForm.title }}</div>
                              <div class="sns-review-label-corner">Editor's Note</div>
                            </div>
                            <div class="sns-review-headline"><span class="sns-quote-mark-inline">&ldquo;</span><span v-html="renderMd(er.headline)"></span></div>
                            <div class="sns-review-body" v-if="er.body" v-html="renderMd(er.body)"></div>
                          </div>
                        </div>
                      </div>
                      <div class="card-row-editor">
                        <div style="display: flex; gap: 8px; margin-bottom: 8px;">
                          <input v-model="er.reviewer" placeholder="Reviewer name" style="flex: 1;" />
                          <input v-model="er.reviewer_role" placeholder="Role (optional)" style="flex: 1;" />
                        </div>
                        <input v-model="er.headline" placeholder="Pull-quote headline" style="width: 100%; margin-bottom: 8px;" />
                        <textarea v-model="er.body" placeholder="2–3 supporting sentences" rows="3" style="width: 100%; margin-bottom: 8px;"></textarea>
                        <div style="display: flex; align-items: center; justify-content: space-between;">
                          <label style="font-size: 12px; color: #606266;">
                            <input type="checkbox" v-model="er.is_ai_generated" /> AI-generated
                          </label>
                          <el-button size="mini" type="danger" plain @click="removeEditorialReview(eri)">Remove</el-button>
                        </div>
                      </div>
                    </div>
                    <div v-if="!collectionForm.editorial_reviews || collectionForm.editorial_reviews.length === 0" class="card-row-content">
                      <div class="card-row-preview card-row-empty">No editorial reviews yet</div>
                      <div class="card-row-editor"></div>
                    </div>
                    <el-button v-if="!collectionForm.editorial_reviews || collectionForm.editorial_reviews.length < 3" size="mini" @click="addEditorialReview">+ Add editorial review</el-button>
                  </div>

                  <!-- ==================== Long-form Video ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <h4 class="card-row-title">
                      Long-form Video
                      <span v-if="collectionForm.video && collectionForm.video.status" :class="['video-status-badge', 'video-status-' + (collectionForm.video.status || '').toLowerCase()]">{{ collectionForm.video.status }}</span>
                    </h4>
                    <div class="video-youtube-field">
                      <label for="collection-youtube-url">Promotional video YouTube URL</label>
                      <el-input
                        id="collection-youtube-url"
                        :value="collectionForm.video && collectionForm.video.youtube_url || ''"
                        placeholder="https://www.youtube.com/watch?v=..."
                        clearable
                        @input="setVideoYoutubeUrl"
                      />
                      <p>Paste the video link after publishing it. It will appear on the public collection page when you save.</p>
                    </div>
                    <div v-if="!collectionForm.video || (!collectionForm.video.script && !collectionForm.video.slides && !collectionForm.video.visual_spec)" class="card-row-content">
                      <div class="card-row-preview card-row-empty">No long-form video has been generated for this collection yet.</div>
                      <div class="card-row-editor"></div>
                    </div>
                    <div v-else class="video-summary-block">
                      <!-- Top row: visual spec preview + key stats -->
                      <div class="video-summary-row">
                        <div
                          v-if="collectionForm.video.visual_spec && collectionForm.video.visual_spec.palette"
                          class="video-spec-card"
                          :style="{
                            background: collectionForm.video.visual_spec.palette.bg,
                            color: collectionForm.video.visual_spec.palette.fg,
                            fontFamily: (collectionForm.video.visual_spec.typography && collectionForm.video.visual_spec.typography.serif) || 'serif',
                          }"
                        >
                          <div class="video-spec-card-label" :style="{ color: collectionForm.video.visual_spec.palette.muted, fontFamily: (collectionForm.video.visual_spec.typography && collectionForm.video.visual_spec.typography.mono) || 'monospace' }">VISUAL SPEC</div>
                          <div class="video-spec-card-title">{{ collectionForm.video.script && collectionForm.video.script.thesis ? truncate(collectionForm.video.script.thesis, 90) : 'Visual identity' }}</div>
                          <div class="video-spec-swatch-row">
                            <span v-for="(hex, key) in pickPaletteForSwatch(collectionForm.video.visual_spec.palette)" :key="key" class="video-spec-swatch" :style="{ background: hex }" :title="key + ': ' + hex"></span>
                          </div>
                          <div class="video-spec-card-meta" :style="{ color: collectionForm.video.visual_spec.palette.muted, fontFamily: (collectionForm.video.visual_spec.typography && collectionForm.video.visual_spec.typography.mono) || 'monospace' }">
                            <span v-if="collectionForm.video.visual_spec.typography">{{ collectionForm.video.visual_spec.typography.serif }} · {{ collectionForm.video.visual_spec.typography.sans }} · {{ collectionForm.video.visual_spec.typography.mono }}</span>
                          </div>
                        </div>
                        <div class="video-stats-grid">
                          <div class="video-stat">
                            <div class="video-stat-num">{{ (collectionForm.video.slides && collectionForm.video.slides.length) || 0 }}</div>
                            <div class="video-stat-label">slides</div>
                          </div>
                          <div class="video-stat">
                            <div class="video-stat-num">{{ formatVideoDuration(collectionForm.video.slides) }}</div>
                            <div class="video-stat-label">runtime</div>
                          </div>
                          <div class="video-stat">
                            <div class="video-stat-num">{{ (collectionForm.video.section_selection && Math.round((collectionForm.video.section_selection.coverage_pct || 0) * 100)) || 0 }}%</div>
                            <div class="video-stat-label">coverage</div>
                          </div>
                          <div class="video-stat">
                            <div class="video-stat-num">{{ (collectionForm.video.extension_preview && collectionForm.video.extension_preview.length) || 0 }}</div>
                            <div class="video-stat-label">teasers</div>
                          </div>
                        </div>
                      </div>

                      <!-- Section selection summary -->
                      <div v-if="collectionForm.video.section_selection" class="video-section-summary">
                        <span class="video-section-label">Picked groups:</span>
                        <span v-for="(gi, idx) in (collectionForm.video.section_selection.picked_group_indices || [])" :key="'pg-' + gi" class="video-picked-chip">
                          {{ groupLabel(gi) }}
                        </span>
                        <div v-if="collectionForm.video.section_selection.rationale" class="video-section-rationale">{{ collectionForm.video.section_selection.rationale }}</div>
                      </div>

                      <!-- Extension preview teaser list -->
                      <div v-if="collectionForm.video.extension_preview && collectionForm.video.extension_preview.length" class="video-preview-block">
                        <div class="video-preview-label">Extension preview (teases unpicked groups):</div>
                        <ol class="video-preview-list">
                          <li v-for="(item, pi) in collectionForm.video.extension_preview" :key="'pv-' + pi">
                            <span class="video-preview-from">{{ groupLabel(item.group_index) }}</span>
                            <span class="video-preview-promise">{{ item.promise }}</span>
                          </li>
                        </ol>
                      </div>

                      <!-- Action row -->
                      <div class="video-action-row">
                        <el-button type="primary" size="small" icon="el-icon-video-play" @click="openVideoPlayer">Open in Video Player</el-button>
                        <el-button v-if="collectionForm.video.youtube_url" size="small" icon="el-icon-link" @click="openYoutubeUrl">View on YouTube</el-button>
                        <span v-else class="video-action-hint">YouTube URL is creator-controlled — paste it here once published.</span>
                      </div>

                      <!-- Inline bundle preview (collapsed by default) -->
                      <div v-if="collectionForm.video.bundle_html" class="video-bundle-preview-wrap">
                        <el-button size="mini" plain type="text" @click="videoBundlePreviewOpen = !videoBundlePreviewOpen">
                          {{ videoBundlePreviewOpen ? '▾ Hide inline preview' : '▸ Show inline preview' }} ({{ Math.round(collectionForm.video.bundle_html.length / 1024) }} KB)
                        </el-button>
                        <iframe
                          v-if="videoBundlePreviewOpen"
                          :srcdoc="collectionForm.video.bundle_html"
                          class="video-bundle-preview-iframe"
                          sandbox="allow-scripts allow-same-origin"
                          referrerpolicy="no-referrer"
                        ></iframe>
                      </div>
                    </div>
                  </div>

                  <!-- ==================== Video Shorts (9:16) ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <VideoShortsPanel :collection-form="collectionForm" :collection-id="collectionId" />
                  </div>

                  <!-- ==================== Launch Post ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <LaunchPostPanel :collection-form="collectionForm" :collection-id="collectionId" />
                  </div>

                  <!-- ==================== Social Scripts ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <h4 class="card-row-title">Social Scripts <span style="color:#909399; font-weight:normal;" v-if="collectionForm.social_scripts && collectionForm.social_scripts.scripts && collectionForm.social_scripts.scripts.length">({{ collectionForm.social_scripts.scripts.length }})</span></h4>
                    <SocialScriptsPanel :collection-form="collectionForm" :collection-id="collectionId" />
                  </div>

                  <!-- ==================== SNS Promotion Cards (legacy) ==================== -->
                  <div class="card-row" style="margin-bottom: 32px;">
                    <h4 class="card-row-title">SNS Promotion Cards <span style="color:#909399; font-weight:normal;">(legacy)</span> <span style="color:#909399; font-weight:normal;" v-if="collectionForm.sns_cards && collectionForm.sns_cards.length">({{ collectionForm.sns_cards.length }})</span></h4>
                    <div
                      v-for="(card, ci) in collectionForm.sns_cards"
                      :key="'snsc-' + ci"
                      style="margin-bottom: 24px; border: 1px solid #ebeef5; border-radius: 8px; padding: 16px;"
                    >
                      <!-- Editor inputs -->
                      <div style="margin-bottom: 16px;">
                        <div style="display: flex; gap: 8px; margin-bottom: 8px; align-items: center;">
                          <el-select v-model="card.card_type" size="mini" style="width: 160px;">
                            <el-option label="Summary Pack" value="summary_pack" />
                            <el-option label="Step by Step" value="step_by_step" />
                            <el-option label="Reframe" value="contrast" />
                            <el-option label="Checklist" value="checklist" />
                          </el-select>
                          <input v-model="card.source_post" placeholder="Source post title" style="flex: 1;" />
                          <el-button size="mini" type="danger" plain @click="removeSnsCard(ci)">Remove</el-button>
                        </div>
                        <input v-model="card.hook" placeholder="Hook — attention headline (≤15 words)" style="width: 100%; margin-bottom: 8px;" />
                        <div style="margin-bottom: 4px; font-size: 12px; color: #909399;">Slides:</div>
                        <div v-for="(page, pi) in card.pages" :key="'snsi-' + ci + '-p-' + pi" style="display: flex; gap: 8px; margin-bottom: 4px;">
                          <span style="color: #909399; font-size: 12px; min-width: 16px; padding-top: 6px;">{{ pi + 1 }}</span>
                          <input v-model="card.pages[pi]" placeholder="Slide content" style="flex: 1;" />
                          <el-button size="mini" type="text" style="color: #f56c6c;" @click="removeSnsCardPage(ci, pi)">×</el-button>
                        </div>
                        <el-button size="mini" @click="addSnsCardPage(ci)" style="margin-bottom: 8px;">+ Add slide</el-button>
                        <input v-model="card.cta" placeholder="CTA — closing call-to-action" style="width: 100%;" />
                      </div>

                      <!-- Per-platform captions (read-only) — only shown if generated -->
                      <div v-if="card.captions && hasAnyCaption(card.captions)" class="sns-captions-block">
                        <div class="sns-captions-header">
                          <span class="sns-captions-title">Per-platform captions</span>
                          <span class="sns-captions-hint">Generated by the marketing-content step. Edit by re-running generation.</span>
                        </div>
                        <div class="sns-captions-grid">
                          <div
                            v-for="platform in captionPlatforms"
                            :key="'cap-' + ci + '-' + platform.key"
                            v-show="card.captions[platform.key]"
                            class="sns-caption-cell"
                          >
                            <div class="sns-caption-platform-row">
                              <span class="sns-caption-platform">{{ platform.label }}</span>
                              <span
                                class="sns-caption-count"
                                :class="{ 'sns-caption-count-over': isCaptionOver(card.captions[platform.key], platform.limit) }"
                              >
                                {{ (card.captions[platform.key] && card.captions[platform.key].char_count) || 0 }} / {{ platform.limit }}
                              </span>
                            </div>
                            <div class="sns-caption-rendered">{{ (card.captions[platform.key] && card.captions[platform.key].rendered) || '' }}</div>
                            <div
                              v-if="card.captions[platform.key] && card.captions[platform.key].hashtags && card.captions[platform.key].hashtags.length"
                              class="sns-caption-hashtags"
                            >
                              <span v-for="(h, hi) in card.captions[platform.key].hashtags" :key="'h-' + ci + '-' + platform.key + '-' + hi" class="sns-caption-hashtag">#{{ h }}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Stacked mode (summary_pack, step_by_step, checklist, contrast) -->
                      <template v-if="['summary_pack', 'step_by_step', 'checklist', 'contrast'].includes(card.card_type)">
                        <div class="sns-mode-label-row">
                          <span class="sns-mode-label">Stacked View</span>
                          <el-button size="mini" plain icon="el-icon-download" @click="openBulkDownload('snsStacked' + ci, snsCardTypeLabel(card.card_type) + '_stacked')">Download All</el-button>
                        </div>
                        <div class="sns-slides-carousel" :ref="'snsStacked' + ci">
                          <div
                            v-for="(chunk, chunkIdx) in summaryPackChunks(card)"
                            :key="'stk-' + ci + '-' + chunkIdx"
                            class="sns-card sns-card-sns-slide sns-words-sm" :class="'sns-slide-' + card.card_type"
                          >
                            <div class="sns-card-inner">
                              <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                              <div class="sns-card-footer">
                                <div class="sns-collection-title"><svg class="sns-collection-icon" viewBox="0 0 24 28" fill="none" stroke="#606266" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="19.6" rx="2"/><path d="M5 2h12a2 2 0 0 1 2 2v0"/><path d="M3.5 4h13a2 2 0 0 1 2 2v0"/></svg>{{ collectionForm.title }}</div>
                                <div class="sns-review-label-corner">{{ snsCardTypeLabel(card.card_type) }}</div>
                              </div>
                              <div v-if="summaryPackChunks(card).length > 1" class="sns-page-number">{{ chunkIdx + 1 }} / {{ summaryPackChunks(card).length }}</div>
                              <div class="sns-summary-hook" v-html="renderMd(card.hook)"></div>
                              <div class="sns-summary-list">
                                <div v-for="(item, ii) in chunk" :key="'stki-' + ii" class="sns-summary-item">
                                  <span class="sns-summary-num">
                                    <template v-if="card.card_type === 'step_by_step'">{{ item.idx }}</template>
                                    <template v-else-if="card.card_type === 'checklist'">✓</template>
                                    <template v-else>{{ item.idx }}</template>
                                  </span>
                                  <span class="sns-summary-text" v-html="renderMd(item.text)"></span>
                                </div>
                              </div>
                              <div v-if="card.cta && chunkIdx === summaryPackChunks(card).length - 1" class="sns-summary-cta" v-html="renderMd(card.cta)"></div>
                              <div v-if="chunkIdx < summaryPackChunks(card).length - 1" class="sns-summary-continued">To be continued...</div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- One-point-per-card mode (all types) -->
                      <div class="sns-mode-label-row" style="margin-top: 16px;">
                        <span class="sns-mode-label">One-Point-Per-Card View</span>
                        <el-button size="mini" plain icon="el-icon-download" @click="openBulkDownload('snsSlides' + ci, snsCardTypeLabel(card.card_type) + '_slides')">Download All</el-button>
                      </div>
                      <div class="sns-slides-carousel" :ref="'snsSlides' + ci">
                        <!-- Hook slide -->
                        <div class="sns-card sns-card-sns-slide" :class="['sns-slide-' + card.card_type, cardDensityClass(card.hook)]">
                          <div class="sns-card-inner">
                            <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                            <div class="sns-card-footer">
                              <div class="sns-collection-title"><svg class="sns-collection-icon" viewBox="0 0 24 28" fill="none" stroke="#606266" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="19.6" rx="2"/><path d="M5 2h12a2 2 0 0 1 2 2v0"/><path d="M3.5 4h13a2 2 0 0 1 2 2v0"/></svg>{{ collectionForm.title }}</div>
                              <div class="sns-review-label-corner">{{ snsCardTypeLabel(card.card_type) }}</div>
                            </div>
                            <div class="sns-slide-hook" v-html="renderMd(card.hook)"></div>
                          </div>
                        </div>
                        <!-- Content slides -->
                        <div v-for="(page, pi) in card.pages" :key="'snsc-' + ci + '-p-' + pi" class="sns-card sns-card-sns-slide" :class="['sns-slide-' + card.card_type, snsCardDensityClass(card)]">
                          <div class="sns-card-inner">
                            <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                            <div class="sns-card-footer">
                              <div class="sns-collection-title"><svg class="sns-collection-icon" viewBox="0 0 24 28" fill="none" stroke="#606266" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="19.6" rx="2"/><path d="M5 2h12a2 2 0 0 1 2 2v0"/><path d="M3.5 4h13a2 2 0 0 1 2 2v0"/></svg>{{ collectionForm.title }}</div>
                              <div class="sns-review-label-corner">{{ snsCardTypeLabel(card.card_type) }}</div>
                            </div>
                            <div class="sns-slide-step-num" v-if="card.card_type === 'step_by_step'">Step {{ pi + 1 }}</div>
                            <div class="sns-slide-step-num" v-else-if="card.card_type === 'checklist'"><span class="sns-check-icon">✓</span> {{ pi + 1 }}</div>
                            <div class="sns-slide-step-num" v-else>{{ pi + 1 }}</div>
                            <div class="sns-slide-content" v-html="renderMd(page)"></div>
                          </div>
                        </div>
                        <!-- CTA slide -->
                        <div v-if="card.cta" class="sns-card sns-card-sns-slide sns-slide-cta" :class="['sns-slide-' + card.card_type, cardDensityClass(card.cta)]">
                          <div class="sns-card-inner">
                            <div class="sns-card-bottom-bar"><img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="sns-collection-cover" /><div class="sns-brand-tag"><img src="/LearningFirst_logo.png" class="sns-logo-icon" alt="" /> LearningFirst</div></div>
                            <div class="sns-card-footer">
                              <div class="sns-collection-title"><svg class="sns-collection-icon" viewBox="0 0 24 28" fill="none" stroke="#606266" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="14" height="19.6" rx="2"/><path d="M5 2h12a2 2 0 0 1 2 2v0"/><path d="M3.5 4h13a2 2 0 0 1 2 2v0"/></svg>{{ collectionForm.title }}</div>
                              <div class="sns-review-label-corner">{{ snsCardTypeLabel(card.card_type) }}</div>
                            </div>
                            <div class="sns-slide-cta-text" v-html="renderMd(card.cta)"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-if="!collectionForm.sns_cards || collectionForm.sns_cards.length === 0" class="card-row-content">
                      <div class="card-row-preview card-row-empty">No SNS cards yet</div>
                      <div class="card-row-editor"></div>
                    </div>
                    <el-button size="mini" @click="addSnsCard">+ Add SNS card</el-button>
                  </div>

                  <!-- Download overlay — shown on card hover -->
                  <div
                    v-if="downloadOverlay.visible"
                    class="sns-download-overlay"
                    :style="downloadOverlay.style"
                    @mouseenter="keepDownloadOverlay"
                    @mouseleave="hideDownloadOverlay"
                  >
                    <div class="sns-download-title">Download as</div>
                    <button class="sns-download-btn" @click.stop.prevent="downloadCardV2('5:7', '5:7', 1080)">LearningFirst (5:7)</button>
                    <button class="sns-download-btn" @click.stop.prevent="downloadCardV2('1:1', '1:1', 1080)">Instagram Square (1:1)</button>
                    <button class="sns-download-btn" @click.stop.prevent="downloadCardV2('3:4', '3:4', 1080)">Instagram / Xiaohongshu (3:4)</button>
                    <button class="sns-download-btn" @click.stop.prevent="downloadCardV2('5:7', '9:16', 1080)">TikTok / Stories (9:16)</button>
                    <button class="sns-download-btn" @click.stop.prevent="downloadCardV2('1:1', '16:9', 1280)">Twitter / X (16:9)</button>
                  </div>

                </div>
              </el-tab-pane>

              <el-tab-pane label="Marketing" name="marketing">
                <el-collapse v-model="marketingActive">
                  <el-collapse-item name="editorial-essays">
                    <template slot="title">
                      <span class="marketing-collapse-title">Editorial Essays</span>
                      <span class="marketing-status-badge"
                            :class="(collectionForm.editorial_essay || collectionForm.thesis_essay || collectionForm.collection_brief) ? 'is-set' : 'is-empty'">
                        {{ (collectionForm.editorial_essay || collectionForm.thesis_essay || collectionForm.collection_brief) ? 'Set' : 'Not generated' }}
                      </span>
                    </template>
                    <EditorialEssaysPanel :collection-form="collectionForm" :collection-id="collectionId" />
                  </el-collapse-item>

                  <el-collapse-item name="email">
                    <template slot="title">
                      <span class="marketing-collapse-title">Marketing Email</span>
                      <span class="marketing-status-badge"
                            :class="(collectionForm.marketing_email && collectionForm.marketing_email.subject) ? 'is-set' : 'is-empty'">
                        {{ (collectionForm.marketing_email && collectionForm.marketing_email.subject) ? 'Set' : 'Not generated' }}
                      </span>
                    </template>
                    <MarketingEmailPanel :collection-form="collectionForm" :collection-id="collectionId" />
                  </el-collapse-item>

                  <el-collapse-item name="substack">
                    <template slot="title">
                      <span class="marketing-collapse-title">Substack Post</span>
                      <span class="marketing-status-badge"
                            :class="(collectionForm.substack_post && collectionForm.substack_post.title) ? 'is-set' : 'is-empty'">
                        {{ (collectionForm.substack_post && collectionForm.substack_post.title) ? 'Set' : 'Not generated' }}
                      </span>
                      <span v-if="collectionForm.substack_post && collectionForm.substack_post.estimated_read_minutes"
                            class="marketing-side-meta">
                        {{ collectionForm.substack_post.estimated_read_minutes }} min read
                      </span>
                    </template>
                    <SubstackPostPanel :collection-form="collectionForm" :collection-id="collectionId" />
                  </el-collapse-item>

                  <el-collapse-item name="podcast">
                    <template slot="title">
                      <span class="marketing-collapse-title">Podcast Script</span>
                      <span class="marketing-status-badge"
                            :class="(collectionForm.podcast_script && collectionForm.podcast_script.episode_title) ? 'is-set' : 'is-empty'">
                        {{ (collectionForm.podcast_script && collectionForm.podcast_script.episode_title) ? 'Set' : 'Not generated' }}
                      </span>
                      <span v-if="collectionForm.podcast_script && collectionForm.podcast_script.total_estimated_seconds"
                            class="marketing-side-meta">
                        ~{{ Math.round(collectionForm.podcast_script.total_estimated_seconds / 60) }} min
                      </span>
                    </template>
                    <PodcastScriptPanel
                      :collection-form="collectionForm"
                      :collection-id="collectionId"
                      @update="onPodcastScriptUpdate" />
                  </el-collapse-item>
                </el-collapse>
              </el-tab-pane>
            </el-tabs>
          </el-form>
        </div>
      </el-main>

      <el-dialog title="Download All Cards" :visible.sync="bulkDownload.visible" width="420px" :modal="true" append-to-body>
        <p style="font-size: 13px; color: #606266; margin-bottom: 12px;">Choose aspect ratio for all cards in this row:</p>
        <div style="display: flex; flex-direction: column; gap: 6px;">
          <button class="sns-download-btn" @click="bulkDownloadAt('5:7', '5:7', 1080)">LearningFirst (5:7)</button>
          <button class="sns-download-btn" @click="bulkDownloadAt('1:1', '1:1', 1080)">Instagram Square (1:1)</button>
          <button class="sns-download-btn" @click="bulkDownloadAt('3:4', '3:4', 1080)">Instagram / Xiaohongshu (3:4)</button>
          <button class="sns-download-btn" @click="bulkDownloadAt('5:7', '9:16', 1080)">TikTok / Stories (9:16)</button>
          <button class="sns-download-btn" @click="bulkDownloadAt('1:1', '16:9', 1280)">Twitter / X (16:9)</button>
        </div>
      </el-dialog>

      <el-dialog
        title="Confirm Delete"
        :visible.sync="deleteDialogVisible"
        width="400px"
        :modal="false"
      >
        <div>
          <p style="margin-bottom: 20px; font-size: 14px; color: #606266;">Are you sure you want to delete this course?</p>
          <el-radio-group v-model="deleteOption" style="display: flex; flex-direction: column; gap: 12px;">
            <el-radio label="collection_only" style="line-height: 1.8;">
              <span style="font-weight: 500;">Delete course only</span>
              <div style="font-size: 12px; color: #909399; margin-left: 24px; margin-top: 4px;">
                Posts in this course will remain but won't be grouped
              </div>
            </el-radio>
            <el-radio label="collection_and_posts" style="line-height: 1.8;">
              <span style="font-weight: 500; color: #f56c6c;">Delete course and all its posts</span>
              <div style="font-size: 12px; color: #909399; margin-left: 24px; margin-top: 4px;">
                ⚠️ This will permanently delete all posts in this course
              </div>
            </el-radio>
          </el-radio-group>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">Cancel</el-button>
          <el-button type="danger" @click="deleteCollection">Delete</el-button>
        </span>
      </el-dialog>

      <el-dialog
        :title="versionForm.id ? 'Edit Version Update' : 'Add Version Update'"
        :visible.sync="versionDialogVisible"
        width="560px"
        :close-on-click-modal="false"
        :modal="false"
      >
        <el-form label-position="top">
          <el-form-item label="Version">
            <el-input v-model="versionForm.version" placeholder="e.g. v1.2 or 2026.05" />
          </el-form-item>
          <el-form-item label="Title">
            <el-input v-model="versionForm.title" placeholder="Short update title (optional)" />
          </el-form-item>
          <el-form-item label="Release date">
            <el-date-picker
              v-model="versionForm.releaseDate"
              type="date"
              placeholder="Choose date"
              style="width: 100%;"
            />
          </el-form-item>
          <el-form-item label="What changed">
            <el-input
              v-model="versionForm.updates"
              type="textarea"
              :rows="7"
              placeholder="- Added new sessions&#10;- Updated examples&#10;- Fixed quiz explanations"
            />
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="versionDialogVisible = false">Cancel</el-button>
          <el-button type="primary" :loading="versionSaving" @click="saveVersionUpdate">Save</el-button>
        </span>
      </el-dialog>

      <!-- Transfer Collection Dialog -->
      <el-dialog
        title="Transfer Course to Other User"
        :visible.sync="transferDialogVisible"
        width="450px"
        :modal="false"
      >
        <div>
          <p style="margin-bottom: 20px; font-size: 14px; color: #606266;">
            Transfer this course and all its posts to another user by entering their email address.
          </p>
          <el-alert
            title="Warning"
            type="warning"
            :closable="false"
            style="margin-bottom: 20px;"
          >
            <div style="font-size: 13px;">
              This action will transfer ownership of the course and all its posts. You will no longer have access to edit or manage this course after the transfer.
            </div>
          </el-alert>
          <el-form :model="transferForm" :rules="transferRules" ref="transferForm">
            <el-form-item label="User Email" prop="user_email">
              <el-input
                v-model="transferForm.user_email"
                placeholder="Enter the recipient's email address"
                clearable
              ></el-input>
            </el-form-item>
          </el-form>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="transferDialogVisible = false">Cancel</el-button>
          <el-button type="primary" @click="transferCollection" :loading="transferring">Transfer</el-button>
        </span>
      </el-dialog>

      <AIDialog
        :visible.sync="aiDialogVisible"
        :title="aiDialogTitle"
        :task-type="aiTaskType"
        :initial-input="aiDialogInitialInput"
        :context-string="aiContextString"
        @use="handleAIOutput"
      />

      <!-- AI Assistant Drawer for Cover Generation -->
      <el-drawer
        title="AI Collection Cover Generator"
        :visible.sync="aiDrawerVisible"
        direction="rtl"
        size="80%"
        :modal="false"
        :show-close="true"
        :wrapperClosable="false"
        :before-close="handleDrawerClose"
        custom-class="ai-cover-drawer"
      >
        <UserAIDialogue 
          :context_string="getAIContextString()"
          :context_pot="globalContextPot"
          :task-type="currentAITask"
          :current_chain_id="chain_id"
          @cover-generated="handleCoverGenerated"
          @clear-context-pot="clearContextPot"
          @remove-from-pot="removeFromContextPot"
          @use="handleAIOutput"
        />
      </el-drawer>

      <el-dialog
        title="Reorder Groups"
        :visible.sync="reorderDialogVisible"
        width="50%"
      >
        <draggable 
          v-model="tempGroups"
          handle=".group-drag-handle"
          class="reorder-list"
          @end="handleGroupOrderEnd"
        >
          <div 
            v-for="(group, index) in tempGroups" 
            :key="getGroupDragKey(group, index)"
            class="reorder-item"
          >
            <i class="el-icon-rank group-drag-handle"></i>
            <span class="group-number">Group #{{ index + 1 }}</span>
            <span class="group-title">{{ group.title || 'Untitled Group' }}</span>
          </div>
        </draggable>
        <span slot="footer" class="dialog-footer">
          <el-button type="primary" @click="reorderDialogVisible = false">Done</el-button>
        </span>
      </el-dialog>

      <!-- Add new dialog component before closing el-container -->
      <el-dialog
        title="AI Content Generation"
        :visible.sync="aiGenerationDialogVisible"
        width="70%"
        :modal="false"
      >
        <div class="dialog-header">
          <el-button
            type="primary"
            @click="confirmArrangeAll"
            style="margin-bottom: 20px;"
          >
            Arrange AI Generation for All
          </el-button>
        </div>
        
        <div v-for="(group, groupIndex) in collectionForm.groups" :key="groupIndex" class="group-section">
          <div class="group-header">
            <h3>{{ group.title || `Group #${groupIndex + 1}` }}</h3>
          </div>
          
          <el-table :data="group.knowits" style="width: 100%">
            <el-table-column label="Knowit" min-width="200">
              <template slot-scope="scope">
                {{ (scope.row.post_info && scope.row.post_info.title) || 'Untitled Knowit' }}
              </template>
            </el-table-column>
            <el-table-column label="Answers" width="100">
              <template slot-scope="scope">
                <span>{{ scope.row.post_info?.answers?.length || 0 }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Generation Status" width="150">
              <template slot-scope="scope">
                <span :class="getStatusClass(scope.row.post_info?.ai_content_generation_status)">
                  {{ scope.row.post_info?.ai_content_generation_status || 'N/A' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Actions" width="200">
              <template slot-scope="scope">
                <el-button
                  v-if="canArrangeGeneration(scope.row.post_info?.ai_content_generation_status)"
                  size="mini"
                  type="primary"
                  @click="arrangeGeneration(
                    scope.row.knowit_id,
                    groupIndex,
                    scope.$index || scope.row.$index || group.knowits.indexOf(scope.row),
                    scope.row.post_info?.title
                  )"
                >
                  Arrange AI Generation
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-dialog>

      <el-dialog
        title="Batch Delete Knowits"
        :visible.sync="batchDeleteDialogVisible"
        width="600px"
      >
        <div style="max-height: 400px; overflow-y: auto;">
          <el-table
            :data="allKnowitsForBatchDelete"
            border
            @selection-change="handleBatchDeleteSelectionChange"
          >
            <el-table-column type="selection" width="55"></el-table-column>
            <el-table-column label="Group" prop="groupTitle" width="180"></el-table-column>
            <el-table-column label="Knowit Title" prop="knowitTitle"></el-table-column>
          </el-table>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="batchDeleteDialogVisible = false">Cancel</el-button>
          <el-button type="danger" @click="confirmBatchDeleteKnowits" :disabled="batchDeleteSelection.length === 0">Delete Selected</el-button>
        </span>
      </el-dialog>
      
      <!-- AI Assistant Toggle Button at Left Edge -->
      <div 
        class="ai-assistant-toggle"
        @click="toggleAIDrawer"
        :class="{ 'active': aiDrawerVisible }"
      >
        <i class="el-icon-chat-dot-round"></i>
        <span>AI Assistant</span>
      </div>
    </el-container>
  </transition>
</template>

<script>
import { showCollectionLaunchNotification } from '@/utils/collectionLaunchNotification'
import CollectionSpecialLabelPicker from "@/components/CollectionSpecialLabelPicker.vue";
import { postRequest } from "@/apis/front";
import * as qiniu from "qiniu-js"; // Add qiniu import
import draggable from "vuedraggable";
import html2canvas from "html2canvas";
const JSZip = require("jszip");
import { marked } from "marked";
import DOMPurify from "dompurify";
import AIDialog from "@/components/AIDialog.vue";
import UserAIDialogue from "@/components/UserAIDialogue.vue";
import TestBankManager from "@/components/TestBank/TestBankManager.vue";
import ExamConfigurationEditor from "@/components/Examination/ExamConfigurationEditor.vue";
import ExamAnalyticsDashboard from "@/components/Examination/ExamAnalyticsDashboard.vue";
import MarketingEmailPanel from "@/components/CollectionMarketing/MarketingEmailPanel.vue";
import SubstackPostPanel from "@/components/CollectionMarketing/SubstackPostPanel.vue";
import PodcastScriptPanel from "@/components/CollectionMarketing/PodcastScriptPanel.vue";
import VideoShortsPanel from "@/components/CollectionMarketing/VideoShortsPanel.vue";
import LaunchPostPanel from "@/components/CollectionMarketing/LaunchPostPanel.vue";
import EditorialEssaysPanel from "@/components/CollectionMarketing/EditorialEssaysPanel.vue";
import SocialScriptsPanel from "@/components/CollectionMarketing/SocialScriptsPanel.vue";
import { CATALOGUE_SECTION_CODES, CATALOGUE_SECTION_OPTIONS } from "@/constants/catalogueSections";
import { INDUSTRY_CODES, INDUSTRY_OPTIONS } from "@/constants/industries";
import { getCountryOptions, normalizeCountryScope } from "@/utils/countryAvailability";
import { copyPublicContentUrl, publicContentUrl } from "@/utils/publicContentLinks";
import { prepareCollectionCover } from "@/utils/collectionCoverImage";

const COVER_IMAGE_MIME_TYPE = "image/jpeg";
const COVER_IMAGE_DOMAIN = "https://ap.learningfirst.io/";
const ILLUSTRATION_MAX_WIDTH = 2000;
const ILLUSTRATION_MAX_SIZE = 300 * 1024;
const ILLUSTRATION_MAX_QUALITY = 0.92;
const ILLUSTRATION_MIN_QUALITY = 0.25;

export default {
  name: "NewCollection",
  components: {
    CollectionSpecialLabelPicker,
    draggable,
    AIDialog,
    UserAIDialogue,
    TestBankManager,
    ExamConfigurationEditor,
    ExamAnalyticsDashboard,
    MarketingEmailPanel,
    SubstackPostPanel,
    PodcastScriptPanel,
    VideoShortsPanel,
    LaunchPostPanel,
    EditorialEssaysPanel,
    SocialScriptsPanel,
  },
  data() {
    return {
      marketingActive: [],
      expersistUploading: '',
      expersistStatus: null,
      expersistPollTimer: null,
      expersistPollCount: 0,
      expersistParts: [
        { kind: 'skill', title: 'Skill', hint: 'One SKILL.md is usual. A folder may add references, scripts, and assets; learners download a folder as a ZIP.' },
        { kind: 'experiences', title: 'Experience data', hint: 'Markdown or text notes that add Experiences for AI assistants. They are not part of the Skill download.' },
      ],
      downloadOverlay: {
        visible: false,
        style: {},
        cardEl: null,
        hideTimer: null,
      },
      bulkDownload: {
        visible: false,
        carouselEl: null,
        zipName: '',
      },
      availableTracks: [],
      tracksLoading: false,
      videoBundlePreviewOpen: false,
      // Per-platform caption metadata used by the SNS captions display block.
      // Char limits mirror the platform rules in Collection Content Generation Procedures.
      captionPlatforms: [
        { key: 'x',              label: 'X / Twitter',     limit: 280 },
        { key: 'instagram',      label: 'Instagram',       limit: 2200 },
        { key: 'youtube_shorts', label: 'YouTube Shorts',  limit: 5000 },
        { key: 'tiktok',         label: 'TikTok',          limit: 2200 },
        { key: 'substack_notes', label: 'Substack Notes',  limit: 1000 },
      ],
      creator_functionality_level: this.$store.state.user_info.creator_functionality_level,
      isEditing: false,
      collectionId: "",
      loadedCollectionStatus: "PRIVATE",
      testBankCount: 0,
      deleteDialogVisible: false,
      deleteOption: 'collection_only',
      transferDialogVisible: false,
      transferring: false,
      transferForm: {
        user_email: ''
      },
      transferRules: {
        user_email: [
          { required: true, message: 'Please enter user email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email address', trigger: 'blur' }
        ]
      },
      educationAudienceOptions: [
        { value: "primary", label: "Primary", helper: "Elementary / early foundational learners" },
        { value: "secondary", label: "Secondary", helper: "Middle-school and high-school learners" },
        { value: "collegiate", label: "Collegiate", helper: "Undergraduate / college-level learners" },
        { value: "professional", label: "Professional", helper: "Adult learners and working professionals" },
      ],
      catalogueSectionOptions: CATALOGUE_SECTION_OPTIONS,
      industryOptions: INDUSTRY_OPTIONS,
      countryOptions: getCountryOptions(),
      collectionForm: {
        title: "",
        sub_title: "",
        description: "",
        grand_take_away: "",
        collection_knowledge: "",
        walkaway_artifacts: [],
        author_bio: "",
        track_ids: [],
        special_label_ids: [],
        gold_quotes: [],
        key_questions: [],
        key_concepts: [],
        sns_cards: [],
        genre_tags: [],
        catalogue_section_codes: [],
        industry_codes: [],
        target_audience_stages: [],
        country_scope: {
          mode: "ALL",
          country_codes: [],
        },
        editorial_reviews: [],
        editorial_brief: {
          who_its_for: "",
          what_stands_out: "",
          read_if: "",
          is_ai_generated: false,
          created_at: 0,
        },
        phases: [],
        groups: [],
        announcement_blurb: "",
        narrative_mode: "answer_first",
        status: "PRIVATE",
        teaser_release_date: "",
        has_custom_price: false,
        usd_price: 0,
        base_credits: 0,
        cover_prompt: "",
        cover_url: "",
        module_illustration_url: "",
        subscribers_only: false,
        single_purchase_enabled: true,
        settings: {
          shown_in_public_search: true,
          visible_in_recommendation: true,
        },
        tags: [], // Changed from string to array
        video: null, // long-form YouTube video object: section_selection / script / extension_preview / slides / bundle_html / visual_spec / youtube_url / status
      },
      currentTag: '', // New property for the tag input
      rules: {
        title: [
          { required: true, message: "Please input a title", trigger: "blur" },
          {
            min: 3,
            max: 100,
            message: "Length should be 3 to 100 characters",
            trigger: "blur",
          },
        ],
        description: [
          {
            required: true,
            message: "Please input a description",
            trigger: "blur",
          },
        ],
        groups: [
          {
            type: "array",
            required: true,
            message: "Please add at least one group",
            trigger: "change",
          },
        ],
        usd_price: [
          {
            validator: (rule, value, callback) => {
              if (!this.collectionForm.has_custom_price) {
                callback();
                return;
              }
              if (value === null || value === undefined || value === '') {
                callback(new Error("Please input a price"));
                return;
              }
              const price = Number(value);
              if (!Number.isFinite(price) || price < 0) {
                callback(new Error("Price must be 0 or greater"));
                return;
              }
              callback();
            },
            trigger: "blur"
          },
        ],
        cover_url: [
          {
            required: true,
            message: "Please upload a cover image",
            trigger: "change",
          },
        ],
        track_ids: [
          {
            validator: (rule, value, callback) => {
              if (this.normalizeTrackIds(value).length === 0) {
                callback(new Error("Please select at least one track"));
              } else {
                callback();
              }
            },
            trigger: "change",
          },
        ],
      },
      activeTab: "info",
      aiDialogVisible: false,
      aiDialogTitle: "",
      aiDialogInitialInput: "",
      aiTaskType: "",
      aiContextString: "",
      aiDrawerVisible: false,
      currentAITask: null,
      reorderDialogVisible: false,
      tempGroups: [],
      containerWidth: '100%',
      defaultWidth: '100%',
      isReady: false,
      aiGenerationDialogVisible: false,
      editGroupIndex: null,
      autoSaveDebounceTimer: null,
      isLoadingData: false, // Flag to prevent auto-save during initial data load
      saving: false,
      isOrderSyncing: false,
      orderSyncPromise: null,
      pendingOrderSync: false,
      notifyingFollowers: false,
      versionLoading: false,
      versionSaving: false,
      versionNotifyLoading: false,
      versionDialogVisible: false,
      versionUpdates: [],
      selectedVersionUpdateIds: [],
      versionForm: {
        id: "",
        version: "",
        title: "",
        releaseDate: null,
        updates: "",
      },
      selectedKnowits: {}, // { groupIndex: [knowitIndex, ...], ... }
      batchDeleteDialogVisible: false,
      batchDeleteGroupIndex: null,
      batchDeleteSelection: [], // Array of { groupIndex, knowitIndex }
      chain_id: '', // Chain ID for AI task
      selectMode: false,
      selectedGroups: [],
      selectedKnowitsList: [], // Array of {groupIndex, knowitIndex}
    };
  },
  created() {
    console.log('Component created');
    // Hide container initially
    this.isReady = false;

    // Clean up old auto-save caches from other users
    this.$nextTick(() => {
      this.cleanupOldAutoSaveCaches();
    });

    // Get collection ID from store, query, or localStorage (in order of preference)
    const collectionId = this.$store.state.tempCollectionId || this.$route.query.id || localStorage.getItem('editCollectionId');
    if (collectionId) {
      this.isEditing = true;
      this.collectionId = collectionId;
      this.fetchCollectionData(collectionId).then(() => {
        this.checkAutoSavePrompt();
      });
    } else {
      this.checkAutoSavePrompt();
    }

    // Set initial width before mounting
    this.containerWidth = `${this.defaultWidth}px`;
  },
  mounted() {
    console.log('Component mounted');
    setTimeout(() => {
      this.isReady = true;
      // Force a width update after component is ready
      this.updateContainerWidth();
    }, 100);

    // Add resize listener to handle window size changes
    window.addEventListener('resize', this.updateContainerWidth);

    // Load available career/skill tracks so the multi-select has options
    this.fetchAvailableTracks();
  },
  beforeDestroy() {
    clearTimeout(this.expersistPollTimer);
    // Clean up storage when leaving the page normally
    if (!this.isEditing) {
      localStorage.removeItem("editCollectionId");
    }

    // Clean up the temporary collection ID from store
    this.$store.dispatch('setTempCollectionId', null)

    // Clean up resize listener
    window.removeEventListener('resize', this.updateContainerWidth);
  },
  methods: {
    expersistEntry(kind) {
      return this.expersistStatus && this.expersistStatus[kind] ? this.expersistStatus[kind] : null;
    },
    expersistCompileState(kind) {
      const entry = this.expersistEntry(kind);
      const compile = entry && entry.compile;
      if (entry && entry.status_error) return { label: entry.status_error, error: true };
      if (!compile || compile.status === 'off') return { label: 'Not compiled into Experiences', error: false };
      if (compile.status === 'syncing') return { label: 'Compiling into Experiences…', error: false };
      if (compile.status === 'error') return { label: `Experience compilation failed: ${compile.error || 'unknown error'}`, error: true };
      const count = compile.experience_count || 0;
      return { label: `${count} Experience${count === 1 ? '' : 's'} in the Learning First Space`, error: false };
    },
    async loadExpersistStatus() {
      if (!this.collectionId) return;
      try {
        const response = await postRequest('getCollectionExpersistStatus', { collection_id: this.collectionId });
        if (response.data.code !== 0) throw new Error(response.data.msg || 'Could not load Expersist status');
        this.expersistStatus = response.data.data;
      } catch (error) {
        this.$message.error(error.message || 'Could not load Expersist status');
      }
      const compiling = ['skill', 'experiences'].some(kind => {
        const entry = this.expersistEntry(kind);
        return entry && entry.compile && entry.compile.status === 'syncing';
      });
      clearTimeout(this.expersistPollTimer);
      if (compiling && this.expersistPollCount < 60) {
        this.expersistPollCount += 1;
        this.expersistPollTimer = setTimeout(() => this.loadExpersistStatus(), 5000);
      }
    },
    readSkillFileBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
        reader.readAsDataURL(file);
      });
    },
    async publishExpersistFiles(event, kind) {
      // Hidden files such as .DS_Store are never part of a Skill.
      const chosen = Array.from(event.target.files || [])
        .filter(file => !(file.webkitRelativePath || file.name).split('/').some(part => part.startsWith('.')));
      event.target.value = '';
      if (!chosen.length || !this.collectionId || this.expersistUploading) return;
      const totalBytes = chosen.reduce((sum, file) => sum + file.size, 0);
      if (chosen.length > 100 || totalBytes > 2 * 1024 * 1024) {
        this.$message.error('Choose up to 100 files totaling 2 MiB or less');
        return;
      }
      // A chosen folder's own name is not part of the Skill's paths.
      let paths = chosen.map(file => file.webkitRelativePath || file.name);
      const top = paths[0].includes('/') ? paths[0].split('/')[0] + '/' : '';
      if (top && paths.every(path => path.startsWith(top))) paths = paths.map(path => path.slice(top.length));
      this.expersistUploading = kind;
      try {
        const files = await Promise.all(chosen.map(async (file, index) => ({
          path: paths[index],
          content_base64: await this.readSkillFileBase64(file),
        })));
        const response = await postRequest(kind === 'skill' ? 'publishCollectionSkill' : 'publishCollectionExperienceData', {
          collection_id: this.collectionId,
          files,
        });
        if (response.data.code !== 0) throw new Error(response.data.msg || 'Could not save to Expersist');
        this.$message.success(response.data.msg || 'Saved to Expersist');
        this.expersistPollCount = 0;
        await this.loadExpersistStatus();
      } catch (error) {
        this.$message.error(error.message || 'Could not save to Expersist');
      } finally {
        this.expersistUploading = '';
      }
    },
    async downloadCollectionSkill() {
      try {
        const response = await postRequest('getCollectionSkillDownload', { collection_id: this.collectionId });
        if (response.data.code !== 0 || !response.data.data || !response.data.data.url) {
          throw new Error(response.data.msg || 'Could not prepare download');
        }
        window.location.assign(response.data.data.url);
      } catch (error) {
        this.$message.error(error.message || 'Could not prepare download');
      }
    },
    async openExpersistAILink() {
      // Open the tab during the click so the browser does not block it.
      const tab = window.open('', '_blank');
      try {
        const response = await postRequest('getCollectionExpersistAILink', { collection_id: this.collectionId });
        if (response.data.code !== 0 || !response.data.data || !response.data.data.url) {
          throw new Error(response.data.msg || 'Could not prepare the AI link');
        }
        if (tab) {
          tab.opener = null;
          tab.location.href = response.data.data.url;
        } else {
          window.location.assign(response.data.data.url);
        }
      } catch (error) {
        if (tab) tab.close();
        this.$message.error(error.message || 'Could not prepare the AI link');
      }
    },
    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString();
    },
    renderVersionMarkdown(text) {
      if (!text) return "";
      marked.setOptions({ breaks: true, gfm: true });
      return DOMPurify.sanitize(marked(text));
    },
    formatVersionTimestamp(timestamp) {
      if (!timestamp) return "-";
      return new Date(timestamp * 1000).toLocaleDateString();
    },
    async fetchVersionUpdates() {
      if (!this.collectionId) return;
      this.versionLoading = true;
      try {
        const response = await postRequest("getCollectionVersionUpdates", {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
        });
        if (response.data.code === 0) {
          const data = response.data.data || {};
          this.versionUpdates = data.version_updates || [];
          this.pruneSelectedVersionUpdates();
        } else {
          this.$message.error(response.data.msg || "Failed to load version updates");
        }
      } catch (error) {
        console.error("Error loading version updates:", error);
        this.$message.error("Failed to load version updates");
      } finally {
        this.versionLoading = false;
      }
    },
    openVersionDialog(item) {
      if (item) {
        this.versionForm = {
          id: item.id,
          version: item.version || "",
          title: item.title || "",
          releaseDate: item.release_time ? new Date(item.release_time * 1000) : null,
          updates: item.updates || "",
        };
      } else {
        this.versionForm = {
          id: "",
          version: this.collectionForm.version_identifier || "",
          title: "",
          releaseDate: new Date(),
          updates: "",
        };
      }
      this.versionDialogVisible = true;
    },
    async saveVersionUpdate() {
      if (!this.collectionId) {
        this.$message.warning("Save the deck before adding version updates");
        return;
      }
      if (!this.versionForm.version.trim()) {
        this.$message.warning("Please enter a version");
        return;
      }
      if (!this.versionForm.updates.trim()) {
        this.$message.warning("Please enter update notes");
        return;
      }
      const releaseTime = this.versionForm.releaseDate
        ? Math.floor(new Date(this.versionForm.releaseDate).getTime() / 1000)
        : Math.floor(Date.now() / 1000);
      this.versionSaving = true;
      try {
        const fn = this.versionForm.id ? "updateCollectionVersionUpdate" : "addCollectionVersionUpdate";
        const payload = {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          version: this.versionForm.version.trim(),
          title: this.versionForm.title.trim(),
          updates: this.versionForm.updates.trim(),
          release_time: releaseTime,
        };
        if (this.versionForm.id) payload.version_update_id = this.versionForm.id;
        const response = await postRequest(fn, payload);
        if (response.data.code === 0) {
          const data = response.data.data || {};
          this.versionUpdates = data.version_updates || [];
          this.pruneSelectedVersionUpdates();
          this.versionDialogVisible = false;
          this.$message.success("Version update saved");
        } else {
          this.$message.error(response.data.msg || "Failed to save version update");
        }
      } catch (error) {
        console.error("Error saving version update:", error);
        this.$message.error("Failed to save version update");
      } finally {
        this.versionSaving = false;
      }
    },
    async deleteVersionUpdate(item) {
      try {
        await this.$confirm("Delete this version update?", "Confirm Delete", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
          confirmButtonClass: "el-button--danger",
        });
        const response = await postRequest("deleteCollectionVersionUpdate", {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          version_update_id: item.id,
        });
        if (response.data.code === 0) {
          const data = response.data.data || {};
          this.versionUpdates = data.version_updates || [];
          this.pruneSelectedVersionUpdates();
          this.$message.success("Version update deleted");
        } else {
          this.$message.error(response.data.msg || "Failed to delete version update");
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error deleting version update:", error);
        }
      }
    },
    pruneSelectedVersionUpdates() {
      const validIds = {};
      this.versionUpdates.forEach(item => {
        if (item.id) validIds[item.id] = true;
      });
      this.selectedVersionUpdateIds = this.selectedVersionUpdateIds.filter(id => validIds[id]);
    },
    async ensureCollectionPublishedBeforeFollowerNotification() {
      if (!this.collectionId) return false;
      const response = await postRequest("updatePostCollectionParams", {
        collection_id: this.collectionId,
        params: { status: "PUBLISHED" },
      });
      if (response.data.code !== 0) {
        throw new Error(response.data.msg || "Failed to publish course before notifying followers");
      }
      return true;
    },
    async notifySelectedVersionUpdates() {
      if (this.selectedVersionUpdateIds.length === 0) {
        this.$message.warning("Select at least one version update to notify the audience");
        return;
      }
      if (this.collectionForm.status !== "PUBLISHED") {
        this.$message.warning("Publish the course before notifying the audience.");
        return;
      }
      const payload = {
        collection_id: this.collectionId,
        user_id: this.$store.state.user_id,
        version_update_ids: this.selectedVersionUpdateIds,
      };
      this.versionNotifyLoading = true;
      try {
        await this.ensureCollectionPublishedBeforeFollowerNotification();
        const previewResponse = await postRequest("notifyCollectionVersionUpdatesFollowers", {
          ...payload,
          dry_run: "1",
        });
        if (previewResponse.data.code !== 0) {
          this.$message.error(previewResponse.data.msg || "Failed to preview notification");
          return;
        }
        const previewData = previewResponse.data.data || {};
        const recipientCount = previewData.recipient_count || 0;
        if (recipientCount <= 0) {
          this.$message.warning("No eligible followers, current readers, or users who starred this course to notify");
          return;
        }
        const updateCount = this.selectedVersionUpdateIds.length;
        const sourceCounts = previewData.source_counts || {};
        const sourceSummary = [];
        const followerCount = sourceCounts.followers || 0;
        const readerCount = sourceCounts.in_progress_readers || 0;
        const starredCount = sourceCounts.starred_users || 0;
        if (followerCount) sourceSummary.push(`${followerCount} follower${followerCount === 1 ? "" : "s"}`);
        if (readerCount) sourceSummary.push(`${readerCount} current reader${readerCount === 1 ? "" : "s"}`);
        if (starredCount) sourceSummary.push(`${starredCount} user${starredCount === 1 ? "" : "s"} who starred it`);
        const sourceBreakdown = sourceSummary.length ? `, including ${sourceSummary.join(", ")}` : "";
        await this.$confirm(
          `Send ${updateCount} selected version update${updateCount === 1 ? "" : "s"} to ${recipientCount} unique recipient${recipientCount === 1 ? "" : "s"}${sourceBreakdown} by email?`,
          "Notify version-update audience",
          {
            confirmButtonText: "Send",
            cancelButtonText: "Cancel",
            type: "warning",
          }
        );
        const sendResponse = await postRequest("notifyCollectionVersionUpdatesFollowers", payload);
        if (sendResponse.data.code === 0) {
          const data = sendResponse.data.data || {};
          this.$message.success(`Notification sent to ${data.sent || 0} recipients${data.skipped ? `, ${data.skipped} skipped` : ""}`);
        } else {
          this.$message.error(sendResponse.data.msg || "Failed to notify the audience");
        }
      } catch (error) {
        if (error !== "cancel") {
          console.error("Error notifying version-update audience:", error);
          this.$message.error("Failed to notify the audience");
        }
      } finally {
        this.versionNotifyLoading = false;
      }
    },
    onPodcastScriptUpdate(updated) {
      // Keep local collectionForm in sync after the PodcastScriptPanel
      // saves voice_assignments via repairPostCollection.
      if (this.collectionForm) {
        this.$set(this.collectionForm, 'podcast_script', updated);
      }
    },
    // ── Video block helpers ─────────────────────────────────
    pickPaletteForSwatch(palette) {
      // Expose only the named hex tokens; skip nested objects like prose_descriptors.
      const out = {};
      if (!palette) return out;
      ['accent', 'fg', 'bg', 'muted', 'dim', 'line', 'line_soft'].forEach((k) => {
        if (typeof palette[k] === 'string' && palette[k].startsWith('#')) out[k] = palette[k];
      });
      return out;
    },
    formatVideoDuration(slides) {
      if (!Array.isArray(slides) || !slides.length) return '—';
      const totalMs = slides.reduce((sum, s) => sum + (Number(s.duration_ms) || 0), 0);
      const totalSec = Math.round(totalMs / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
    groupLabel(groupIndex) {
      const groups = (this.collectionForm && this.collectionForm.groups) || [];
      const g = groups[groupIndex];
      if (g && g.title) return `[${groupIndex}] ${g.title}`;
      return `Group ${groupIndex}`;
    },
    truncate(text, n) {
      if (!text) return '';
      return text.length > n ? text.slice(0, n - 1).trimEnd() + '…' : text;
    },
    openVideoPlayer() {
      if (!this.collectionId) {
        this.$message.warning('Collection id missing — save the collection first.');
        return;
      }
      const route = this.$router.resolve({ path: '/video-player', query: { collection_id: this.collectionId } });
      window.open(route.href, '_blank');
    },
    openYoutubeUrl() {
      const url = this.collectionForm.video && this.collectionForm.video.youtube_url;
      if (url) window.open(url, '_blank');
    },
    setVideoYoutubeUrl(value) {
      if (!this.collectionForm.video) this.$set(this.collectionForm, 'video', {});
      this.$set(this.collectionForm.video, 'youtube_url', value);
    },
    isValidYoutubeUrl(value) {
      let url;
      try {
        url = new URL(value);
      } catch (error) {
        return false;
      }
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.port) return false;
      const host = url.hostname.toLowerCase();
      const parts = url.pathname.split('/').filter(Boolean);
      let id = '';
      if (['youtu.be', 'www.youtu.be'].includes(host) && parts.length === 1) {
        id = parts[0];
      } else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host)) {
        if (parts.length === 1 && parts[0] === 'watch') id = url.searchParams.get('v') || '';
        else if (parts.length === 2 && ['shorts', 'live', 'embed'].includes(parts[0])) id = parts[1];
      }
      return /^[a-zA-Z0-9_-]{11}$/.test(id);
    },
    // ── SNS card captions helpers ───────────────────────────
    hasAnyCaption(captions) {
      if (!captions || typeof captions !== 'object') return false;
      return ['x', 'instagram', 'youtube_shorts', 'tiktok', 'substack_notes'].some(
        (k) => captions[k] && captions[k].rendered
      );
    },
    isCaptionOver(caption, limit) {
      if (!caption || typeof caption.char_count !== 'number') return false;
      return caption.char_count > limit;
    },
    async createTrack() {
      if (this.collectionForm.track_ids.length >= 3) {
        this.$message.info('Select up to 3 tracks. Remove one before adding another.');
        return;
      }
      try {
        const { value } = await this.$prompt('New tracks are available for selection. Plaza placement is managed by the platform.', 'Add a track', { inputPlaceholder: 'Track name', inputValidator: value => !!(value && value.trim()) || 'Enter a track name' });
        this.tracksLoading = true;
        const response = await postRequest('createCreatorTrack', { user_id: this.$store.state.user_id, track_name: value.trim() });
        if (response.data.code !== 0) throw new Error(response.data.msg || 'Could not create track');
        const track = response.data.data;
        const id = this.trackIdString(track);
        if (!this.availableTracks.some(t => this.trackIdString(t) === id)) this.availableTracks.unshift(track);
        if (!this.collectionForm.track_ids.includes(id)) this.collectionForm.track_ids.push(id);
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || 'Could not create track');
      } finally { this.tracksLoading = false; }
    },
    async fetchAvailableTracks() {
      this.tracksLoading = true;
      try {
        const response = await postRequest("listCertTracks", {});
        if (response.data && response.data.code === 0) {
          this.availableTracks = response.data.data || [];
        }
      } catch (e) {
        console.error("Failed to load tracks:", e);
      } finally {
        this.tracksLoading = false;
      }
    },
    trackIdString(t) {
      const id = t && t._id;
      if (!id) return "";
      if (typeof id === "string") return id;
      return id.$oid || id.$id || "";
    },
    cardDensityClass(text) {
      const words = (text || '').trim().split(/\s+/).filter(w => w.length > 0).length;
      if (words <= 15) return 'sns-words-xxl';
      if (words <= 25) return 'sns-words-xl';
      if (words <= 40) return 'sns-words-lg';
      if (words <= 70) return 'sns-words-md';
      if (words <= 120) return 'sns-words-sm';
      return 'sns-words-xs';
    },
    snsCardDensityClass(card) {
      if (!card) return '';
      var pages = (card.pages && card.pages.length) ? card.pages : [];
      var longest = (card.hook || '');
      if ((card.cta || '').length > longest.length) longest = card.cta;
      for (var i = 0; i < pages.length; i++) {
        var p = pages[i];
        if (typeof p === 'string' && p.length > longest.length) longest = p;
      }
      return this.cardDensityClass(longest);
    },
    renderMd(text) {
      if (!text) return '';
      return text
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>');
    },
    summaryPackChunks(card) {
      if (!card) return [[]];
      const rawPages = Array.isArray(card.pages) ? card.pages : [];
      const pages = rawPages.map((text, i) => ({ idx: i + 1, text: typeof text === 'string' ? text : '' }));
      if (pages.length === 0) return [[]];

      // Count total words to decide how to split
      const pageWords = pages.map(p => (p.text || '').split(/\s+/).filter(w => w.length > 0).length);
      const totalWords = pageWords.reduce((a, b) => a + b, 0);
      const hookWords = (card.hook || '').split(/\s+/).length;
      const ctaWords = (card.cta || '').split(/\s+/).length;

      // Per-card word budget (accounting for 20% bottom buffer, title area, etc.)
      const perCardBudget = 75;
      const firstCardBudget = Math.max(45, perCardBudget - hookWords);

      // If everything fits in one card, don't split
      if (totalWords + ctaWords <= firstCardBudget) return [pages];

      // Calculate how many cards we need, then distribute evenly
      const totalBudget = totalWords + ctaWords;
      const numCards = Math.ceil((totalBudget - firstCardBudget) / perCardBudget) + 1;
      const evenLimit = Math.ceil(pages.length / numCards);

      // Split by item count for even distribution
      const chunks = [];
      for (let i = 0; i < pages.length; i += evenLimit) {
        chunks.push(pages.slice(i, i + evenLimit));
      }
      return chunks;
    },
    extractCount(line) {
      const m = (line || '').replace(/\*\*/g, '').match(/^(\d+\+?)\s/);
      return m ? m[1] : '';
    },
    extractRest(line) {
      return (line || '').replace(/\*\*/g, '').replace(/^\d+\+?\s+/, '');
    },
    addGoldQuote() {
      if (!Array.isArray(this.collectionForm.gold_quotes)) {
        this.$set(this.collectionForm, "gold_quotes", []);
      }
      if (this.collectionForm.gold_quotes.length >= 3) return;
      this.collectionForm.gold_quotes.push({
        insight: "",
        detail: "",
        created_at: Math.floor(Date.now() / 1000),
      });
    },
    removeGoldQuote(index) {
      this.collectionForm.gold_quotes.splice(index, 1);
    },
    addWalkawayArtifact() {
      if (!Array.isArray(this.collectionForm.walkaway_artifacts)) {
        this.$set(this.collectionForm, "walkaway_artifacts", []);
      }
      if (this.collectionForm.walkaway_artifacts.length >= 8) return;
      this.collectionForm.walkaway_artifacts.push("");
    },
    removeWalkawayArtifact(index) {
      this.collectionForm.walkaway_artifacts.splice(index, 1);
    },
    addKeyQuestion() {
      if (!Array.isArray(this.collectionForm.key_questions)) {
        this.$set(this.collectionForm, "key_questions", []);
      }
      if (this.collectionForm.key_questions.length >= 5) return;
      this.collectionForm.key_questions.push("");
    },
    removeKeyQuestion(index) {
      this.collectionForm.key_questions.splice(index, 1);
    },
    addKeyConcept() {
      if (!Array.isArray(this.collectionForm.key_concepts)) {
        this.$set(this.collectionForm, "key_concepts", []);
      }
      if (this.collectionForm.key_concepts.length >= 10) return;
      this.collectionForm.key_concepts.push("");
    },
    removeKeyConcept(index) {
      this.collectionForm.key_concepts.splice(index, 1);
    },
    addSnsCard() {
      if (!Array.isArray(this.collectionForm.sns_cards)) {
        this.$set(this.collectionForm, "sns_cards", []);
      }
      this.collectionForm.sns_cards.push({
        card_type: "summary_pack",
        hook: "",
        pages: [""],
        cta: "",
        source_post: "",
        created_at: Math.floor(Date.now() / 1000),
      });
    },
    removeSnsCard(index) {
      this.collectionForm.sns_cards.splice(index, 1);
    },
    addSnsCardPage(cardIndex) {
      this.collectionForm.sns_cards[cardIndex].pages.push("");
    },
    removeSnsCardPage(cardIndex, pageIndex) {
      this.collectionForm.sns_cards[cardIndex].pages.splice(pageIndex, 1);
    },
    snsCardTypeLabel(type) {
      const labels = {
        summary_pack: 'Summary Pack',
        step_by_step: 'Step-by-Step Guide',
        contrast: 'Reframe',
        checklist: 'Checklist'
      };
      return labels[type] || type || 'SNS Card';
    },
    handleCardMouseOver(event) {
      const card = event.target.closest('.sns-card');
      const overlay = event.target.closest('.sns-download-overlay');
      if (overlay) {
        this.keepDownloadOverlay();
        return;
      }
      if (!card) return;
      if (card === this.downloadOverlay.cardEl) {
        this.keepDownloadOverlay();
        return;
      }
      this.showDownloadOverlay({ currentTarget: card });
    },
    handleCardMouseOut(event) {
      const related = event.relatedTarget;
      if (related) {
        const inCard = related.closest('.sns-card');
        const inOverlay = related.closest('.sns-download-overlay');
        if ((inCard && inCard === this.downloadOverlay.cardEl) || inOverlay) return;
      }
      this.hideDownloadOverlay();
    },
    showDownloadOverlay(event) {
      const card = event.currentTarget;
      const rect = card.getBoundingClientRect();
      const container = card.closest('.social-cards-container');
      const containerRect = container.getBoundingClientRect();
      clearTimeout(this.downloadOverlay.hideTimer);
      this.downloadOverlay = {
        visible: true,
        cardEl: card,
        hideTimer: null,
        style: {
          position: 'absolute',
          top: (rect.top - containerRect.top + rect.height / 2 - 100) + 'px',
          left: (rect.left - containerRect.left + rect.width + 12) + 'px',
          zIndex: 100,
        }
      };
    },
    keepDownloadOverlay() {
      clearTimeout(this.downloadOverlay.hideTimer);
    },
    hideDownloadOverlay() {
      this.downloadOverlay.hideTimer = setTimeout(() => {
        this.downloadOverlay.visible = false;
        this.downloadOverlay.cardEl = null;
      }, 400);
    },
    async renderCardToBlob(cardEl, cardAR, exportAR, baseWidth) {
      const parseAR = (ar) => { const [w, h] = ar.split(':').map(Number); return { w, h, ratio: w / h }; };
      const cardAr = parseAR(cardAR);
      const exportAr = parseAR(exportAR);
      const isPortrait = exportAr.ratio < 1;

      // Render everything at natural scale (500px-based), then use html2canvas scale to upsize
      const NATURAL_BASE = 500;
      const naturalExportWidth = NATURAL_BASE;
      const naturalExportHeight = Math.round(naturalExportWidth * exportAr.h / exportAr.w);
      const padding = isPortrait ? 0.025 : 0;
      const naturalCardWidth = Math.round(naturalExportWidth * (1 - padding * 2));
      const naturalCardHeight = Math.round(naturalCardWidth * cardAr.h / cardAr.w);

      // Scale factor: render output at baseWidth, scale up from natural 500px
      const renderScale = baseWidth / NATURAL_BASE;

      const bgGradient = isPortrait
        ? 'linear-gradient(165deg, #e8f0fe 0%, #f5f7fa 50%, #eef2f7 100%)'
        : 'linear-gradient(90deg, #e8f0fe 0%, #f5f7fa 30%, #f5f7fa 70%, #eef2f7 100%)';

      const wrapper = document.createElement('div');
      wrapper.style.cssText = `
        width: ${naturalExportWidth}px;
        height: ${naturalExportHeight}px;
        position: fixed;
        left: -9999px;
        top: 0;
        z-index: -1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${isPortrait || exportAr.ratio > 1 ? bgGradient : '#ffffff'};
        overflow: hidden;
      `;

      const clone = cardEl.cloneNode(true);
      const decoration = isPortrait
        ? `border-radius: ${Math.round(16 / renderScale)}px; box-shadow: 0 ${8 / renderScale}px ${32 / renderScale}px rgba(0,0,0,0.12);`
        : 'border-radius: 0;';
      clone.style.cssText = `
        width: ${naturalCardWidth}px !important;
        height: ${naturalCardHeight}px !important;
        aspect-ratio: auto !important;
        ${decoration}
        overflow: hidden;
        flex-shrink: 0;
      `;
      const inner = clone.querySelector('.sns-card-inner');
      if (inner) inner.style.height = '100%';

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      try {
        const canvas = await html2canvas(wrapper, {
          width: naturalExportWidth,
          height: naturalExportHeight,
          scale: renderScale * 2, // *2 for retina sharpness
          useCORS: true,
          allowTaint: true,
          backgroundColor: null,
        });
        return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      } finally {
        document.body.removeChild(wrapper);
      }
    },
    triggerBlobDownload(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = fileName;
      link.href = url;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async downloadCardV2(cardAR, exportAR, baseWidth) {
      const card = this.downloadOverlay.cardEl;
      if (!card) return;
      if (card.classList.contains('sns-card-cover')) cardAR = '5:7';
      try {
        const blob = await this.renderCardToBlob(card, cardAR, exportAR, baseWidth);
        const fileName = `${this.collectionForm.title || 'card'}_${exportAR.replace(':', 'x')}.png`;
        this.triggerBlobDownload(blob, fileName);
        this.$message.success(`Downloaded as ${exportAR}`);
      } catch (err) {
        console.error('Download failed:', err);
        this.$message.error('Failed to download card');
      }
    },
    async downloadAllCards(carouselEl, cardAR, exportAR, baseWidth, zipName) {
      if (!carouselEl) return;
      const cards = carouselEl.querySelectorAll('.sns-card');
      if (cards.length === 0) return;
      const loading = this.$loading({ lock: true, text: `Rendering ${cards.length} cards...`, background: 'rgba(0,0,0,0.5)' });
      try {
        const zip = new JSZip();
        for (let i = 0; i < cards.length; i++) {
          loading.text = `Rendering card ${i + 1}/${cards.length}...`;
          const blob = await this.renderCardToBlob(cards[i], cardAR, exportAR, baseWidth);
          const safeName = `${zipName}_${String(i + 1).padStart(2, '0')}_${exportAR.replace(':', 'x')}.png`;
          zip.file(safeName, blob);
        }
        loading.text = 'Packaging zip...';
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        this.triggerBlobDownload(zipBlob, `${zipName}_${exportAR.replace(':', 'x')}.zip`);
        this.$message.success(`Downloaded ${cards.length} cards as ZIP`);
      } catch (err) {
        console.error('Bulk download failed:', err);
        this.$message.error('Failed to download cards');
      } finally {
        loading.close();
      }
    },
    openBulkDownload(refName, zipName) {
      const carouselEl = Array.isArray(this.$refs[refName]) ? this.$refs[refName][0] : this.$refs[refName];
      this.bulkDownload = { visible: true, carouselEl, zipName };
    },
    async bulkDownloadAt(cardAR, exportAR, baseWidth) {
      this.bulkDownload.visible = false;
      await this.downloadAllCards(this.bulkDownload.carouselEl, cardAR, exportAR, baseWidth, this.bulkDownload.zipName || 'cards');
    },
    addEditorialReview() {
      if (!Array.isArray(this.collectionForm.editorial_reviews)) {
        this.$set(this.collectionForm, "editorial_reviews", []);
      }
      if (this.collectionForm.editorial_reviews.length >= 3) return;
      this.collectionForm.editorial_reviews.push({
        reviewer: "LearningFirst Editorial Team",
        reviewer_role: "",
        headline: "",
        body: "",
        is_ai_generated: true,
        created_at: Math.floor(Date.now() / 1000),
      });
    },
    removeEditorialReview(index) {
      this.collectionForm.editorial_reviews.splice(index, 1);
    },
    handleTestBankStatsUpdated(stats) {
      this.testBankCount = stats && stats.total ? stats.total : 0;
    },
    async fetchCollectionData(collectionId) {
      // Set flag to prevent auto-save during data load
      this.isLoadingData = true;

      // Show loading modal before making request
      const loadingInstance = this.$loading({
        lock: true,
        text: 'Loading collection...',
        spinner: 'el-icon-loading',
        background: 'rgba(255, 255, 255, 0.7)'
      })
      try {
        const response = await postRequest("getPostCollection", {
          id: collectionId,
          user_id: this.$store.state.user_id,
        });

        if (response.data.code === 0) {
          const collectionData = response.data.data;

          // Verify the collection belongs to the current user
          const currentUserId = this.$store.state.user_id;
          if (collectionData.user_id && collectionData.user_id !== currentUserId) {
            // Collection belongs to a different user, clear stored IDs and reset
            this.$message.warning("This collection belongs to another user. Starting a new collection.");
            localStorage.removeItem('editCollectionId');
            this.$store.dispatch('setTempCollectionId', null);
            this.isEditing = false;
            this.collectionId = '';
            loadingInstance.close();
            return;
          }

          // Set default settings if none exist
          const defaultSettings = {
            shown_in_public_search: true,
            visible_in_recommendation: true,
            // Add any other default settings here
          };

          // Initialize subscribers_only on groups and knowits if not present
          if (collectionData.groups) {
            collectionData.groups.forEach(group => {
              if (group.subscribers_only === undefined) group.subscribers_only = false;
              if (group.knowits) {
                group.knowits.forEach(knowit => {
                  if (knowit.subscribers_only === undefined) knowit.subscribers_only = false;
                });
              }
            });
          }

          this.collectionForm = {
            ...collectionData,
            status: this.normalizePublicityStatus(collectionData.status || "PRIVATE"),
            teaser_release_date: collectionData.teaser_release_date || "",
            sub_title: collectionData.sub_title || collectionData.subtitle || "",
            grand_take_away: collectionData.grand_take_away || "",
            collection_knowledge: collectionData.collection_knowledge || "",
            walkaway_artifacts: Array.isArray(collectionData.walkaway_artifacts) ? collectionData.walkaway_artifacts : [],
            author_bio: collectionData.author_bio || "",
            track_ids: this.normalizeTrackIds(collectionData.track_ids),
            special_label_ids: Array.isArray(collectionData.special_label_ids) ? collectionData.special_label_ids : [],
            gold_quotes: Array.isArray(collectionData.gold_quotes) ? collectionData.gold_quotes : [],
            key_questions: Array.isArray(collectionData.key_questions) ? collectionData.key_questions : [],
            key_concepts: Array.isArray(collectionData.key_concepts) ? collectionData.key_concepts : [],
            sns_cards: Array.isArray(collectionData.sns_cards) ? collectionData.sns_cards : [],
            genre_tags: Array.isArray(collectionData.genre_tags) ? collectionData.genre_tags : [],
            catalogue_section_codes: this.normalizeCatalogueSectionCodes(collectionData.catalogue_section_codes),
            industry_codes: this.normalizeIndustryCodes(collectionData.industry_codes),
            target_audience_stages: this.normalizeAudienceStages(collectionData.target_audience_stages),
            country_scope: normalizeCountryScope(collectionData),
            editorial_reviews: Array.isArray(collectionData.editorial_reviews) ? collectionData.editorial_reviews : [],
            announcement_blurb: collectionData.announcement_blurb || "",
            editorial_brief: collectionData.editorial_brief || {
              who_its_for: "",
              what_stands_out: "",
              read_if: "",
              is_ai_generated: false,
              created_at: 0,
            },
            phases: Array.isArray(collectionData.phases)
              ? collectionData.phases.map(phase => ({
                  ...phase,
                  title: phase.title || "",
                  description: phase.description || "",
                  group_indices: Array.isArray(phase.group_indices)
                    ? phase.group_indices.filter(index => Number.isInteger(index) && index >= 0)
                    : [],
                }))
              : [],
            narrative_mode: collectionData.narrative_mode === "answer_last" ? "answer_last" : "answer_first",
            has_custom_price: collectionData.has_custom_price === true,
            base_credits: collectionData.base_credits || 0,
            cover_prompt: collectionData.cover_prompt || "",
            subscribers_only: collectionData.subscribers_only || false,
            single_purchase_enabled: collectionData.single_purchase_enabled !== false,
            tags: collectionData.tags || [], // This ensures 'tags' is always a reactive array
            settings: {
              ...defaultSettings,
              ...(collectionData.settings || {}),
            },
            video: collectionData.video || null,
          };
          this.loadedCollectionStatus = collectionData.status || "PRIVATE";
          this.fetchVersionUpdates();
        } else {
          throw new Error(response.data.msg || "Failed to load collection");
        }
      } catch (error) {
        this.$message.error("Failed to load course data");
        console.error("Error fetching collection:", error);
        // Clear invalid collection ID
        localStorage.removeItem('editCollectionId');
        this.$store.dispatch('setTempCollectionId', null);
        this.isEditing = false;
        this.collectionId = '';
      }

      // Close loading modal
      loadingInstance.close()

      // Reset flag after a short delay to allow watchers to settle
      this.$nextTick(() => {
        setTimeout(() => {
          this.isLoadingData = false;
        }, 100);
      });
    },
    getGroupsForSync() {
      return this.collectionForm.groups.map(group => ({
        ...group,
        subscribers_only: group.subscribers_only || false,
        knowits: group.knowits.map(knowit => ({
          knowit_id: knowit.knowit_id,
          subscribers_only: knowit.subscribers_only || false
        }))
      }));
    },

    async addGroup() {
        this.collectionForm.groups.push({
          title: "",
          group_sub_title: "",
          knowits: [],
          subscribers_only: false,
        });
        // Only sync to backend if we're editing an existing collection
        if (this.isEditing && this.collectionId) {
          try {
            await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
          } catch (error) {
            console.error('Failed to sync group addition:', error);
          }
        }
      },

    async removeGroup(groupIndex) {
      this.collectionForm.groups.splice(groupIndex, 1);
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
    },

    async addKnowit(groupIndex) {
      this.collectionForm.groups[groupIndex].knowits.push({
        knowit_id: "",
      });
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
      this.editKnowit(
        "new",
        groupIndex,
        this.collectionForm.groups[groupIndex].knowits.length - 1
      );
    },

    async removeKnowit(groupIndex, knowitIndex) {
      try {
        await this.$confirm(
          'Are you sure you want to remove this knowit?',
          'Warning',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No', 
            type: 'warning'
          }
        );

        // Only runs if user clicks Yes
        this.collectionForm.groups[groupIndex].knowits.splice(knowitIndex, 1);
        await this.updateCollectionPartial({ groups: this.getGroupsForSync() });

      } catch (error) {
        // Only show error if not a cancel
        if (!(error && error.message === 'cancel')) {
          this.$message.error('Failed to remove knowit');
        }
        // If cancel, do nothing
      }
    },
    normalizeAudienceStage(stage) {
      if (stage === "college") return "collegiate";
      return stage;
    },
    normalizeAudienceStages(stages) {
      if (!Array.isArray(stages)) return [];
      const validStages = ["primary", "secondary", "collegiate", "professional"];
      const seen = {};
      return stages
        .map(this.normalizeAudienceStage)
        .filter(stage => {
          if (!validStages.includes(stage) || seen[stage]) return false;
          seen[stage] = true;
          return true;
        });
    },
    normalizeCatalogueSectionCodes(codes) {
      if (!Array.isArray(codes)) return [];
      const validCodes = new Set(CATALOGUE_SECTION_CODES);
      return [...new Set(codes
        .map(code => typeof code === "string" ? code.trim().toLowerCase() : "")
        .filter(code => validCodes.has(code)))];
    },
    normalizeIndustryCodes(codes) {
      if (!Array.isArray(codes)) return [];
      const validCodes = new Set(INDUSTRY_CODES);
      return [...new Set(codes
        .map(code => typeof code === "string" ? code.trim().toLowerCase() : "")
        .filter(code => validCodes.has(code)))];
    },
    normalizeTrackIds(trackIds) {
      if (!Array.isArray(trackIds)) return [];
      return trackIds
        .map(id => {
          if (typeof id === "string") return id.trim();
          return (id && (id.$oid || id.$id || "")).toString().trim();
        })
        .filter(Boolean);
    },
    isPreorderStatus(status) {
      return ["PREORDER", "TEASER", "REQUEST"].includes(status);
    },
    normalizePublicityStatus(status) {
      return this.isPreorderStatus(status) ? "PREORDER" : (status || "PRIVATE");
    },
    getEffectiveCollectionPrice(collection = this.collectionForm) {
      if (!collection) return 0;
      const rawPrice = collection.has_custom_price === true
        ? collection.usd_price
        : collection.base_credits;
      const price = Number(rawPrice);
      return Number.isFinite(price) && price > 0 ? Math.round(price * 100) / 100 : 0;
    },
    getPreorderCollectionPrice(collection = this.collectionForm) {
      if (!collection) return 0;
      const backendPrice = Number(collection.preorder_price);
      if (Number.isFinite(backendPrice) && backendPrice > 0) {
        return Math.round(backendPrice * 100) / 100;
      }
      const price = this.getEffectiveCollectionPrice(collection);
      return price > 0 ? Math.round(price * 50) / 100 : 0;
    },
    getUniversalCollectionPrice(collection = this.collectionForm) {
      if (!collection) return 0;
      const price = Number(collection.base_credits);
      return Number.isFinite(price) && price > 0 ? Math.round(price * 100) / 100 : 0;
    },
    formatMoney(value) {
      const amount = Number(value);
      return amount > 0 ? `$${amount.toFixed(2)}` : 'FREE';
    },
    cleanFormData() {
      const trackIds = this.normalizeTrackIds(this.collectionForm.track_ids);
      return {
        ...this.collectionForm,
        cover_url: this.collectionForm.cover_url,
        status: this.normalizePublicityStatus(this.collectionForm.status),
        track_ids: trackIds,
        catalogue_section_codes: this.normalizeCatalogueSectionCodes(this.collectionForm.catalogue_section_codes),
        industry_codes: this.normalizeIndustryCodes(this.collectionForm.industry_codes),
        country_scope: normalizeCountryScope(this.collectionForm),
        has_custom_price: this.collectionForm.has_custom_price === true,
        usd_price: Number(this.collectionForm.usd_price || 0),
        base_credits: Number(this.collectionForm.base_credits || 0),
        subscribers_only: this.collectionForm.subscribers_only || false,
        single_purchase_enabled: this.collectionForm.single_purchase_enabled !== false,
        target_audience_stages: this.normalizeAudienceStages(this.collectionForm.target_audience_stages),
        groups: this.collectionForm.groups.map((group) => ({
          ...group,
          subscribers_only: group.subscribers_only || false,
          knowits: group.knowits.map((knowit) => ({
            knowit_id: knowit.knowit_id,
            subscribers_only: knowit.subscribers_only || false,
            post_info:
              knowit.knowit_id === "" && knowit.post_info
                ? knowit.post_info
                : undefined,
          })),
        })),
      };
    },
    async submitForm() {
      this.saving = true;
      const youtubeUrl = (this.collectionForm.video && this.collectionForm.video.youtube_url || '').trim();
      if (youtubeUrl && !this.isValidYoutubeUrl(youtubeUrl)) {
        this.$message.error('Enter a valid YouTube video URL before saving.');
        this.saving = false;
        return;
      }
      if (this.collectionForm.video) this.$set(this.collectionForm.video, 'youtube_url', youtubeUrl);
      const trackIds = this.normalizeTrackIds(this.collectionForm.track_ids);
      if (trackIds.length === 0) {
        this.$message.error("Please select at least one track before saving");
        this.$set(this.collectionForm, "track_ids", []);
        this.saving = false;
        return;
      }
      this.$set(this.collectionForm, "track_ids", trackIds);

      this.$refs.collectionForm.validate(async (valid) => {
        if (valid) {
          try {
            const cleanedData = this.cleanFormData();

            const params = {
              ...cleanedData,
              user_id: this.$store.state.user_id,
            };

            if (this.isEditing) {
              params.id = this.collectionId;
            } else {
              params.id = "";
            }

            // Check the effective purchase price before publishing.
            if (this.collectionForm.status === "PUBLISHED") {
              const purchasePrice = this.getEffectiveCollectionPrice();
              if (purchasePrice <= 0) {
                // Show confirmation dialog for free publication
                this.$confirm(
                  'You are about to publish this course for free. Are you sure you want to make it publicly available without charging?',
                  'Confirm Free Publication',
                  {
                    confirmButtonText: 'Yes, Publish for Free',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                  }
                ).then(() => {
                  // User confirmed, continue with submission
                  this.submitToAPI(params);
                }).catch(() => {
                  // User cancelled
                  this.$message.info('Publication cancelled');
                  this.saving = false;
                });
                return;
              }
            }
            
            // If not public or price is set, proceed normally
            await this.submitToAPI(params);
          } catch (error) {
            this.$message.error(error.message || "Operation failed");
          } finally {
            this.saving = false;
          }
        } else {
          this.saving = false;
        }
      });
    },
    async submitToAPI(params) {
      this.saving = true;
      try {
        const response = await postRequest("updatePostCollection", params);

        if (response.data.code === 0) {
          this.clearAutoSaveCache();
          // Remove stored ID after successful submission
          // localStorage.removeItem("editCollectionId");
          if (!this.collectionId) {
            this.collectionId = response.data.data && response.data.data._id && (response.data.data._id.$oid || response.data.data._id.$id);
            localStorage.setItem("editCollectionId", this.collectionId);
            this.isEditing = true;
            
          }
          this.$message.success(
            this.isEditing
              ? "Collection updated successfully!"
              : "Collection created successfully!"
          );
          this.loadedCollectionStatus = params.status || this.loadedCollectionStatus;
          showCollectionLaunchNotification(this.$message, response.data.launch_notification);
          this.fetchVersionUpdates();
          // this.$router.push("/my-collections");
        } else {
          throw new Error(response.data.msg || "Operation failed");
        }
      } catch (error) {
        this.$message.error(error.message || "Operation failed");
      } finally {
        this.saving = false;
      }
    },
    async retryLaunchEmails() {
      try {
        const response = await postRequest("notifyCollectionTeaserReleaseReminders", {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
        });
        if (response.data.code !== 0) throw new Error(response.data.msg || "Failed to send launch emails");
        showCollectionLaunchNotification(this.$message, response.data);
        const result = response.data.data || {};
        if (!result.sent && !(result.errors && result.errors.length)) {
          this.$message.info("No unsent launch emails remain.");
        }
      } catch (error) {
        this.$message.error(error.message || "Launch emails could not be sent");
      }
    },

    async savePrivate() {
      this.collectionForm.status = "PRIVATE";
      this.submitForm();
    },
    async savePublish() {
      this.collectionForm.status = "PUBLISHED";
      this.submitForm();
    },
    async notifyFollowers() {
      if (!this.collectionId) {
        this.$message.warning("Please save the course before notifying followers.");
        return;
      }
      if (this.collectionForm.status !== "PUBLISHED") {
        this.$message.warning("Only published courses can be sent to followers.");
        return;
      }

      try {
        await this.$confirm(
          "This will email followers of this curator about this course. Followers already notified for this course will be skipped.",
          "Notify Followers?",
          {
            confirmButtonText: "Send Notification",
            cancelButtonText: "Cancel",
            type: "warning",
          }
        );
      } catch (e) {
        return;
      }

      this.notifyingFollowers = true;
      try {
        await this.ensureCollectionPublishedBeforeFollowerNotification();
        const response = await postRequest("notifyCollectionFollowers", {
          collection_id: this.collectionId,
          event_type: "new_collection",
        });
        if (response.data && response.data.code === 0) {
          const data = response.data.data || {};
          this.$message.success(`Follower notification sent. Sent: ${data.sent || 0}, skipped: ${data.skipped || 0}`);
        } else {
          throw new Error((response.data && response.data.msg) || "Failed to notify followers");
        }
      } catch (error) {
        this.$message.error(error.message || "Failed to notify followers");
      } finally {
        this.notifyingFollowers = false;
      }
    },
    showTransferDialog() {
      this.transferForm.user_email = ''
      this.transferDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.transferForm) {
          this.$refs.transferForm.clearValidate()
        }
      })
    },
    async transferCollection() {
      try {
        // Validate form
        const valid = await this.$refs.transferForm.validate()
        if (!valid) return

        this.transferring = true

        const response = await postRequest("transferCollectionToUser", {
          collection_id: this.collectionId,
          user_email: this.transferForm.user_email,
        })

        if (response.data.code === 0) {
          this.$message.success("Course transferred successfully")
          this.transferDialogVisible = false
          // Go back to collections list after transfer
          setTimeout(() => {
            this.$router.push('/my-collections')
          }, 1000)
        } else {
          throw new Error(response.data.msg || "Failed to transfer collection")
        }
      } catch (error) {
        if (error.message) {
          this.$message.error(error.message)
        } else {
          this.$message.error("Failed to transfer course")
        }
      } finally {
        this.transferring = false
      }
    },
    confirmDelete() {
      this.deleteDialogVisible = true;
    },
    async deleteCollection() {
      try {
        const deletePosts = this.deleteOption === 'collection_and_posts';

        const response = await postRequest("deletePostCollection", {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          delete_posts: deletePosts,
        });

        if (response.data.code === 0) {
          const message = deletePosts
            ? "Course and all its posts deleted successfully"
            : "Course deleted successfully";
          this.$message.success(message);
          this.deleteDialogVisible = false;
          this.$router.go(-1);
        } else {
          throw new Error(response.data.msg || "Failed to delete");
        }
      } catch (error) {
        this.$message.error(error.message || "Failed to delete course");
      }
    },
    cancel() {
      // Remove stored ID when canceling
      localStorage.removeItem("editCollectionId");
      this.$router.go(-1);
    },
    async beforeCoverUpload(file) {
      const isImage = file.type.startsWith("image/");
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isImage) {
        this.$message.error("Cover must be an image file!");
        return false;
      }
      if (!isLt5M) {
        this.$message.error("Cover size can not exceed 5MB!");
        return false;
      }

      try {
        const trimmedFile = await this.trimCoverImage(file);
        const tokenResponse = await postRequest("getLearningfirstQiniuToken", {});
        let token = tokenResponse.data.data;

        // Show preview immediately using FileReader
        const reader = new FileReader();
        reader.onload = (e) => {
          this.collectionForm.cover_url = e.target.result; // Set temporary local preview
        };
        reader.readAsDataURL(trimmedFile);

        let observable = qiniu.upload(
          trimmedFile,
          new Date().getTime() + trimmedFile.name,
          token
        );

        observable.subscribe({
          complete: (res) => {
            let url = COVER_IMAGE_DOMAIN + res.key;
            console.log('URL to update: '+url)
            this.updateCollectionCover(url);
            // this.collectionForm.cover_url = url; // Update with actual uploaded URL
            this.$message.success("Cover uploaded successfully");
          },
          error: (err) => {
            console.error("Upload error:", err);
            this.$message.error("Failed to upload cover");
            this.collectionForm.cover_url = ""; // Clear preview on error
          },
        });
      } catch (error) {
        this.$message.error(error.message || "Failed to upload cover");
        console.error("Error uploading cover:", error);
      }

      return false; // Prevent default upload
    },
    async trimCoverImage(file) {
      return prepareCollectionCover(file);
    },
    loadCoverImage(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
          URL.revokeObjectURL(url);
          resolve(image);
        };
        image.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Failed to load cover image"));
        };
        image.src = url;
      });
    },
    canvasToBlob(canvas, quality) {
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to compress cover image"));
            }
          },
          COVER_IMAGE_MIME_TYPE,
          quality
        );
      });
    },

    async trimIllustrationImage(file) {
      const image = await this.loadCoverImage(file);
      const width = Math.min(image.width, ILLUSTRATION_MAX_WIDTH);
      const height = Math.round((image.height / image.width) * width);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Failed to prepare illustration image");
      }

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      const blob = await this.createSizedIllustrationBlob(canvas);
      const baseName = file.name.replace(/\.[^/.]+$/, "") || "illustration";
      return new File([blob], `${baseName}.jpg`, {
        type: COVER_IMAGE_MIME_TYPE,
        lastModified: Date.now(),
      });
    },

    async createSizedIllustrationBlob(canvas) {
      let low = ILLUSTRATION_MIN_QUALITY;
      let high = ILLUSTRATION_MAX_QUALITY;
      let bestBlob = await this.canvasToBlob(canvas, high);

      if (bestBlob.size <= ILLUSTRATION_MAX_SIZE) {
        return bestBlob;
      }

      for (let i = 0; i < 7; i++) {
        const quality = (low + high) / 2;
        const blob = await this.canvasToBlob(canvas, quality);

        if (blob.size <= ILLUSTRATION_MAX_SIZE) {
          bestBlob = blob;
          low = quality;
        } else {
          high = quality;
        }
      }

      if (bestBlob.size > ILLUSTRATION_MAX_SIZE) {
        bestBlob = await this.canvasToBlob(canvas, ILLUSTRATION_MIN_QUALITY);
      }

      if (bestBlob.size > ILLUSTRATION_MAX_SIZE) {
        throw new Error("Unable to compress illustration below 300KB");
      }

      return bestBlob;
    },

    async beforeModuleIllustrationUpload(file) {
      const isImage = file.type.startsWith("image/");
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isImage) {
        this.$message.error("Illustration must be an image file!");
        return false;
      }
      if (!isLt5M) {
        this.$message.error("Illustration size can not exceed 5MB!");
        return false;
      }

      try {
        const trimmedFile = await this.trimIllustrationImage(file);
        const tokenResponse = await postRequest("getLearningfirstQiniuToken", {});
        let token = tokenResponse.data.data;

        const reader = new FileReader();
        reader.onload = (e) => {
          this.collectionForm.module_illustration_url = e.target.result;
        };
        reader.readAsDataURL(trimmedFile);

        let observable = qiniu.upload(
          trimmedFile,
          new Date().getTime() + trimmedFile.name,
          token
        );

        observable.subscribe({
          complete: async (res) => {
            const url = COVER_IMAGE_DOMAIN + res.key;
            this.collectionForm.module_illustration_url = url;
            if (this.isEditing && this.collectionId) {
              try {
                await this.updateCollectionPartial({ module_illustration_url: url });
              } catch (e) {
                console.error("Error persisting module illustration:", e);
              }
            }
            this.$message.success("Module master illustration uploaded successfully");
          },
          error: (err) => {
            console.error("Upload error:", err);
            this.$message.error("Failed to upload module master illustration");
            this.collectionForm.module_illustration_url = "";
          },
        });
      } catch (error) {
        this.$message.error("Failed to upload module master illustration");
        console.error("Error uploading module illustration:", error);
      }

      return false;
    },

    async uploadBase64ImageAsCover(dataUrl) {
      try {
        // Show a loading message
        this.$message.info('Uploading cover image...');

        // Convert Base64 data URL to Blob
        const base64Response = await fetch(dataUrl);
        const blob = await base64Response.blob();

        // Create a File object from Blob
        const file = new File([blob], `cover_${Date.now()}.png`, { type: blob.type });

        // Compress to the same 600x840 / <=300KB JPEG format as manual uploads
        const trimmedFile = await this.trimCoverImage(file);

        // Get Qiniu upload token
        const tokenResponse = await postRequest("getLearningfirstQiniuToken", {});
        const token = tokenResponse.data.data;

        // Upload to Qiniu
        const observable = qiniu.upload(
          trimmedFile,
          `${Date.now()}_${trimmedFile.name}`,
          token
        );

        observable.subscribe({
          complete: (res) => {
            const url = COVER_IMAGE_DOMAIN + res.key;
            console.log('Gemini cover uploaded to:', url);
            this.updateCollectionCover(url);
            this.$message.success("Cover image uploaded and applied successfully!");
          },
          error: (err) => {
            console.error("Upload error:", err);
            this.$message.error("Failed to upload cover image");
          },
        });
      } catch (error) {
        console.error("Error uploading Base64 image:", error);
        this.$message.error("Failed to process cover image");
      }
    },

    async handleKnowitEdit(knowitId, groupIndex, knowitIndex) {
      if (this.orderSyncPromise) {
        try {
          await this.orderSyncPromise;
        } catch (error) {
          return;
        }
      }

      if (!knowitId || knowitId.trim() === "") {
        this.editKnowit("", groupIndex, knowitIndex);
        return;
      }

      this.editKnowit(knowitId, groupIndex, knowitIndex);
    },
    editKnowit(knowitId, groupIndex, knowitIndex) {
      try {
        if (this.isEditing) {
          localStorage.setItem("editCollectionId", this.collectionId);
        }

        if (knowitId) {
          localStorage.setItem("editPostId", knowitId);
        }
        console.log("Collection ID:", this.collectionId);
        console.log("Group Index:", groupIndex);
        console.log("Knowit Index:", knowitIndex);
        console.log("Knowit ID:", knowitId);
        console.log(
          "Post Form:",
          this.collectionForm.groups[groupIndex].knowits[knowitIndex].post_info
        );
        this.$router
          .push({
            name: knowitId ? "EditPost" : "NewPost",
            params: {
              collectionId: this.collectionId,
              groupIndex: groupIndex,
              knowitIndex: knowitIndex,
              postId: knowitId,
              postForm:
                this.collectionForm.groups[groupIndex].knowits[knowitIndex]
                  .post_info,
              collectionContextString: JSON.stringify(this.collectionForm),
            },
          })
          .catch((err) => {
            if (err.name !== "NavigationDuplicated") {
              throw err;
            }
          });
      } catch (error) {
        console.error("Navigation error:", error);
      }
    },
    handleAITask(taskType, groupIndex) {
      this.editGroupIndex = groupIndex;
      this.currentAITask = taskType;

      this.openAIDrawer(taskType);
      // Handle Collection Cover Generation with AiffectChain
      
      
      // // Keep existing logic for other AI tasks
      // this.aiDialogTitle = taskType;
      // this.aiTaskType = taskType;

      // // Set initial input based on task type
      // if (taskType === "Group Knowits Suggestion") {
      //   const group = this.collectionForm.groups[groupIndex];
      //   const params = {
      //     collection_id: this.collectionId,
      //     collection_name: this.collectionForm.title,
      //     collection_description: this.collectionForm.description,
      //     group_index: groupIndex,
      //     group_title: group.title,
      //     user_id: this.$store.state.user_id,
      //     collection_contents: this.collectionForm.groups.map(group => ({
      //       group_title: group.title,
      //       knowits: group.knowits.map(knowit => knowit.post_info?.title || 'Untitled Knowit')
      //     })),
      //   };
      //   this.aiContextString = JSON.stringify(params);
      // } else if (
      //   taskType === "Collection Description Polish" ||
      //   taskType === "Collection Structure Generation"
      // ) {
      //   var params = {
      //     collection_id: this.collectionId,
      //     collection_name: this.collectionForm.title,
      //     collection_description: this.collectionForm.description,
      //     user_id: this.$store.state.user_id,
      //   };
      //   this.aiContextString = JSON.stringify(params);
      // }

      // this.aiDialogVisible = true;
    },

    // === Paywall / Access Control Methods ===
    toggleGroupPaywall(groupIndex) {
      const group = this.collectionForm.groups[groupIndex];
      this.$set(group, 'subscribers_only', !group.subscribers_only);
      this.submitForm();
    },
    toggleKnowitPaywall(groupIndex, knowitIndex) {
      const knowit = this.collectionForm.groups[groupIndex].knowits[knowitIndex];
      this.$set(knowit, 'subscribers_only', !knowit.subscribers_only);
      this.submitForm();
    },
    makeAllSubscribersOnly() {
      this.collectionForm.groups.forEach(group => {
        this.$set(group, 'subscribers_only', true);
        group.knowits.forEach(knowit => {
          this.$set(knowit, 'subscribers_only', true);
        });
      });
      this.$message.success('All groups and stacks set to Subscribers Only');
      this.submitForm();
    },
    makeAllFree() {
      this.collectionForm.groups.forEach(group => {
        this.$set(group, 'subscribers_only', false);
        group.knowits.forEach(knowit => {
          this.$set(knowit, 'subscribers_only', false);
        });
      });
      this.$message.success('All groups and stacks set to Free');
      this.submitForm();
    },
    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) {
        this.selectedGroups = [];
        this.selectedKnowitsList = [];
      }
    },
    toggleGroupSelection(groupIndex) {
      const idx = this.selectedGroups.indexOf(groupIndex);
      if (idx === -1) {
        this.selectedGroups.push(groupIndex);
      } else {
        this.selectedGroups.splice(idx, 1);
      }
    },
    toggleKnowitSelection(groupIndex, knowitIndex) {
      const idx = this.selectedKnowitsList.findIndex(
        k => k.groupIndex === groupIndex && k.knowitIndex === knowitIndex
      );
      if (idx === -1) {
        this.selectedKnowitsList.push({ groupIndex, knowitIndex });
      } else {
        this.selectedKnowitsList.splice(idx, 1);
      }
    },
    isKnowitSelected(groupIndex, knowitIndex) {
      return this.selectedKnowitsList.some(
        k => k.groupIndex === groupIndex && k.knowitIndex === knowitIndex
      );
    },
    selectAll() {
      this.selectedGroups = this.collectionForm.groups.map((_, i) => i);
      this.selectedKnowitsList = [];
      this.collectionForm.groups.forEach((group, gi) => {
        group.knowits.forEach((_, ki) => {
          this.selectedKnowitsList.push({ groupIndex: gi, knowitIndex: ki });
        });
      });
    },
    deselectAll() {
      this.selectedGroups = [];
      this.selectedKnowitsList = [];
    },
    makeSelectedSubscribersOnly() {
      this.selectedGroups.forEach(gi => {
        this.$set(this.collectionForm.groups[gi], 'subscribers_only', true);
      });
      this.selectedKnowitsList.forEach(({ groupIndex, knowitIndex }) => {
        this.$set(this.collectionForm.groups[groupIndex].knowits[knowitIndex], 'subscribers_only', true);
      });
      this.$message.success('Selected items set to Subscribers Only');
      this.submitForm();
    },
    makeSelectedFree() {
      this.selectedGroups.forEach(gi => {
        this.$set(this.collectionForm.groups[gi], 'subscribers_only', false);
      });
      this.selectedKnowitsList.forEach(({ groupIndex, knowitIndex }) => {
        this.$set(this.collectionForm.groups[groupIndex].knowits[knowitIndex], 'subscribers_only', false);
      });
      this.$message.success('Selected items set to Free');
      this.submitForm();
    },
    handleAIOutput(output) {
      if (this.aiDialogTitle === "Collection Description Polish") {
        this.collectionForm.description = output;
      } else if (this.aiDialogTitle === "Collection Structure Generation") {
        console.log("output:", output);

        // Transform the new structure into the required format
        const processedOutput = Object.entries(output).map(
          ([lessonTitle, blocks]) => ({
            title: lessonTitle,
            knowits: blocks.map((blockTitle) => ({
              knowit_id: "",
              post_info: { title: blockTitle },
            })),
          })
        );

        this.collectionForm.groups = processedOutput;
      }else if(this.aiDialogTitle === "Group Knowits Suggestion"){
        // Update group knowits
        this.collectionForm.groups[this.editGroupIndex].knowits = output;
        this.$message.success("AI suggestions applied successfully");
        this.editGroupIndex = null;
      }

      this.$message.success("AI content applied successfully");
    },

    async updateCollectionCover(coverUrl) {
      try {
        // Only make API call if we're editing an existing collection
        if (this.isEditing) {
          const response = await postRequest("updatePostCollectionCover", {
            user_id: this.$store.state.user_id,
            collection_id: this.collectionId,
            cover_url: coverUrl
          });

          if (response.data.code !== 0) {
            throw new Error(response.data.msg || "Failed to update cover");
          }
        }
        
        // Update local state
        this.collectionForm.cover_url = coverUrl;
      } catch (error) {
        this.$message.error(error.message || "Failed to update course cover");
        console.error("Error updating collection cover:", error);
      }
    },
    previewRead() {
      this.$store.dispatch('setTempCollectionId', this.collectionId)
      this.$router.push({
        path: '/read-collection',
        query: {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id
        }
      })
    },
    showReorderDialog() {
      this.tempGroups = [...this.collectionForm.groups];
      this.reorderDialogVisible = true;
    },
    getGroupDragKey(group, index) {
      if (!this._groupDragKeys) this._groupDragKeys = new WeakMap();
      if (group && typeof group === 'object') {
        if (!this._groupDragKeys.has(group)) {
          this._groupDragKeys.set(group, `group-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`);
        }
        return this._groupDragKeys.get(group);
      }
      return `group-fallback-${index}`;
    },
    getKnowitDragKey(knowit, groupIndex, knowitIndex) {
      if (knowit && knowit.knowit_id) return knowit.knowit_id;
      if (!this._knowitDragKeys) this._knowitDragKeys = new WeakMap();
      if (knowit && typeof knowit === 'object') {
        if (!this._knowitDragKeys.has(knowit)) {
          this._knowitDragKeys.set(knowit, `knowit-${Date.now()}-${groupIndex}-${knowitIndex}-${Math.random().toString(36).slice(2)}`);
        }
        return this._knowitDragKeys.get(knowit);
      }
      return `knowit-fallback-${groupIndex}-${knowitIndex}`;
    },
    handleGroupOrderEnd() {
      this.collectionForm.groups = [...this.tempGroups];
      if (this.selectMode) this.deselectAll();
      this.$nextTick(() => {
        this.requestOrderSync();
      });
    },
    handleKnowitOrderEnd() {
      if (this.selectMode) this.deselectAll();
      this.$nextTick(() => {
        this.requestOrderSync();
      });
    },
    requestOrderSync() {
      if (!this.isEditing || !this.collectionId) {
        return Promise.resolve();
      }

      if (this.orderSyncPromise) {
        this.pendingOrderSync = true;
        return this.orderSyncPromise;
      }

      this.isOrderSyncing = true;
      const runSync = async () => {
        do {
          this.pendingOrderSync = false;
          await this.updateCollectionPartial({ groups: this.getGroupsForSync() }, { silent: true });
        } while (this.pendingOrderSync);
      };

      this.orderSyncPromise = runSync()
        .catch((error) => {
          console.error('Failed to sync collection order:', error);
          throw error;
        })
        .finally(() => {
          this.isOrderSyncing = false;
          this.orderSyncPromise = null;
          this.pendingOrderSync = false;
        });

      return this.orderSyncPromise;
    },
    async applyReorder() {
      this.collectionForm.groups = [...this.tempGroups];
      this.reorderDialogVisible = false;
      await this.requestOrderSync();
      this.$message.success('Groups reordered successfully');
    },
    async insertGroup(index) {
      this.collectionForm.groups.splice(index, 0, {
        title: "",
        group_sub_title: "",
        knowits: [],
        subscribers_only: false
      });
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
    },
    startEditingTitle(group) {
      // Set editing flag
      this.$set(group, 'isEditing', true);
      // Wait for DOM update then focus input
      this.$nextTick(() => {
        const input = this.$refs.titleInput;
        if (input && input.length > 0) {
          input[0].focus();
        }
      });
    },
    async finishEditingTitle(group) {
      group.isEditing = false;
      await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
    },
    handleGroupTitleBlur(group, event) {
      const next = event && event.relatedTarget;
      if (next && next.classList && (next.classList.contains('group-title-input') || next.classList.contains('group-subtitle-input'))) {
        return;
      }
      this.finishEditingTitle(group);
    },
    getTotalCardsForGroup(group) {
      // Simply return the number of knowits/cards in the group
      return group.knowits ? group.knowits.length : 0;
    },
    updateContainerWidth() {
      // Force initial width before any calculations
      this.containerWidth = `${this.defaultWidth}px`;
      
      this.$nextTick(() => {
        const container = this.$refs.container;
        if (!container) return;

        const element = container.$el || container;
        if (!element) return;

        // Get the parent width to ensure we don't exceed it
        const parentWidth = (element.parentElement && element.parentElement.offsetWidth) || window.innerWidth;
        const targetWidth = Math.min(this.defaultWidth, parentWidth);
        
        console.log('Width calculations:', {
          defaultWidth: this.defaultWidth,
          parentWidth,
          targetWidth,
          currentWidth: element.offsetWidth
        });

        // Update container width if needed
        this.containerWidth = `${targetWidth}px`;
        element.classList.add('full-width');
      });
    },
    handleEnter(el) {
      console.log('Transition enter started', el);
      if (!el) {
        console.log('No element provided to handleEnter');
        return;
      }
      
      // Force a layout recalculation
      el.offsetHeight;
      
      // Add class to transition element
      el.classList.add('full-width');
    },
    showAIGenerationDialog() {
      this.aiGenerationDialogVisible = true;
    },
    getStatusClass(status) {
      return {
        'status-pending': status === 'PENDING',
        'status-processing': status === 'PROCESSING',
        'status-na': !status
      };
    },
    canArrangeGeneration(status) {
      return !status || (status !== 'PENDING');
    },
    async arrangeGeneration(knowitId,groupIndex=null,postIndex=null,postTitle=null) {
      try {
        const response = await postRequest('pendingPostForAIGeneration', {
          post_id: knowitId,
          collection_id: this.collectionId,
          group_index: groupIndex,
          post_index: postIndex,
          post_title: postTitle,

        });
        
        if (response.data.code === 0) {
          this.$message.success('AI generation arranged successfully');
          const new_post = response.data.data;// Refresh data
          
          this.collectionForm.groups[new_post.group_index].knowits[new_post.post_index].post_info = new_post;
          if(knowitId == ''){
            this.collectionForm.groups[new_post.group_index].knowits[new_post.post_index].knowit_id = (new_post._id.$oid || new_post._id.$id);
          }
        } else {
          throw new Error(response.data.msg || 'Failed to arrange AI generation');
        }
      } catch (error) {
        this.$message.error(error.message);
      }
    },
    async confirmArrangeAll() {
      try {
        await this.$confirm(
          'Are you sure you want to arrange AI generation for all knowits?',
          'Confirm',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }
        );

        const response = await postRequest('pendingCollectionPostsForAIGeneration', {
          collection_id: this.collectionId
        });

        if (response.data.code === 0) {
          this.$message.success('AI generation arranged for all knowits');
          await this.fetchCollectionData(this.collectionId); // Refresh data
        } else {
          throw new Error(response.data.msg || 'Failed to arrange AI generation');
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error(error.message);
        }
      }
    },
    async shareCollection(format) {
      try {
        const collection = { ...this.collectionForm, collection_id: this.collectionId };
        await copyPublicContentUrl(publicContentUrl('collection', collection, format));
        this.$message.success(`${format === 'slug' ? 'Slug' : 'ID'} link copied to clipboard`);
      } catch (error) {
        this.$message.error('Failed to copy link');
      }
    },
    addTag(event) {

      console.log("addTag",event);
      event.preventDefault(); // Prevent form submission on enter
      const newTag = this.currentTag.trim();

      if(this.collectionForm.tags == null){
        this.collectionForm.tags = [];
      }
      
      if (newTag && !(this.collectionForm.tags && this.collectionForm.tags.includes(newTag))) {
        this.collectionForm.tags.push(newTag);
      }
      
      this.currentTag = ''; // Clear the input
    },

    removeTag(index) {
      console.log("removeTag",index);
      this.collectionForm.tags.splice(index, 1);
      console.log("collectionForm.tags",this.collectionForm.tags);
    },

    handleBackspace(event) {
      // If the input is empty and there are tags, remove the last tag
      if (event.target.value === '' && this.collectionForm.tags.length > 0) {
        this.removeTag(this.collectionForm.tags.length - 1);
      }
    },
    async updateCollectionPartial(params, options = {}) {
      try {
        const response = await postRequest("updatePostCollectionParams", {
          collection_id: this.collectionId,
          params // only the fields you want to update
        });
        if (response.data.code === 0) {
          if (!options.silent) {
            this.$message.success("Course updated successfully!");
          }
          return response.data.data;
        } else {
          throw new Error(response.data.msg || "Failed to update course");
        }
      } catch (error) {
        this.$message.error(error.message || "Failed to update course");
        throw error;
      }
    },
    getAutoSaveKey() {
      const userId = this.$store.state.user_id || 'anonymous';
      return `collection_auto_save_${userId}_${this.collectionId || 'new'}`;
    },
    cleanupOldAutoSaveCaches() {
      // Clean up old cache entries that don't have user_id prefix
      const oldKey = `collection_auto_save_${this.collectionId || 'new'}`;
      localStorage.removeItem(oldKey);

      // Also clean up any cache entries from other users for this collection
      const currentUserId = this.$store.state.user_id;
      if (!currentUserId) return;

      Object.keys(localStorage).forEach(key => {
        // Match pattern: collection_auto_save_{userId}_{collectionId}
        if (key.startsWith('collection_auto_save_') && key !== this.getAutoSaveKey()) {
          const collectionIdPart = this.collectionId || 'new';
          // If this cache is for the same collection but different user, remove it
          if (key.endsWith(`_${collectionIdPart}`) && !key.includes(`_${currentUserId}_`)) {
            localStorage.removeItem(key);
          }
        }
      });
    },
    saveAutoSaveCache() {
      // Only save non-group fields
      const { title, sub_title, description, grand_take_away, collection_knowledge, walkaway_artifacts, author_bio, editorial_reviews, editorial_brief, track_ids, gold_quotes, key_questions, key_concepts, sns_cards, genre_tags, catalogue_section_codes, industry_codes, target_audience_stages, country_scope, narrative_mode, announcement_blurb, status, has_custom_price, usd_price, base_credits, cover_prompt, cover_url, settings, tags, version_identifier, single_purchase_enabled } = this.collectionForm;
      const videoYoutubeUrl = this.collectionForm.video && this.collectionForm.video.youtube_url || '';
      const cache = { title, sub_title, description, grand_take_away, collection_knowledge, walkaway_artifacts, author_bio, editorial_reviews, editorial_brief, track_ids, gold_quotes, key_questions, key_concepts, sns_cards, genre_tags, catalogue_section_codes, industry_codes, target_audience_stages, country_scope, narrative_mode, announcement_blurb, status, has_custom_price, usd_price, base_credits, cover_prompt, cover_url, settings, tags, version_identifier, single_purchase_enabled, videoYoutubeUrl };
      localStorage.setItem(this.getAutoSaveKey(), JSON.stringify(cache));
    },
    clearAutoSaveCache() {
      localStorage.removeItem(this.getAutoSaveKey());
    },
    loadAutoSaveCache() {
      const cache = localStorage.getItem(this.getAutoSaveKey());
      return cache ? JSON.parse(cache) : null;
    },
    debounceAutoSave() {
      // Skip auto-save during initial data load
      if (this.isLoadingData) return;

      if (this.autoSaveDebounceTimer) clearTimeout(this.autoSaveDebounceTimer);
      this.autoSaveDebounceTimer = setTimeout(() => {
        this.saveAutoSaveCache();
      }, 1000);
    },
    async checkAutoSavePrompt() {
      const cache = this.loadAutoSaveCache();
      if (!cache) return;

      // Check if cache has meaningful data that differs from current form
      const hasValidCache = this.isCacheDifferentFromForm(cache);
      if (!hasValidCache) {
        // Cache is same as current data or empty, clear it
        this.clearAutoSaveCache();
        return;
      }

      try {
        await this.$confirm(
          'An auto-saved version of this course was found. Do you want to load it?',
          'Auto-Save Detected',
          {
            confirmButtonText: 'Load Auto-Saved',
            cancelButtonText: 'Ignore',
            type: 'info'
          }
        );
        // User confirmed, load cache
        const { videoYoutubeUrl, ...cachedFields } = cache;
        Object.assign(this.collectionForm, {
          ...cachedFields,
          status: this.normalizePublicityStatus(cachedFields.status),
        });
        if (typeof videoYoutubeUrl === 'string') this.setVideoYoutubeUrl(videoYoutubeUrl);
        this.$message.success('Auto-saved version loaded!');
      } catch (e) {
        // User cancelled, clear the cache so it doesn't prompt again
        this.clearAutoSaveCache();
      }
    },
    isCacheDifferentFromForm(cache) {
      // Check if cache has any meaningful differences from current form
      const fieldsToCompare = ['title', 'sub_title', 'description', 'grand_take_away', 'collection_knowledge', 'has_custom_price', 'usd_price', 'base_credits', 'cover_prompt', 'cover_url', 'version_identifier', 'editorial_brief', 'author_bio', 'catalogue_section_codes', 'industry_codes', 'target_audience_stages', 'narrative_mode', 'announcement_blurb', 'single_purchase_enabled'];

      for (const field of fieldsToCompare) {
        const cacheValue = cache[field] || '';
        const formValue = this.collectionForm[field] || '';

        // Convert to string for comparison
        const cacheStr = String(cacheValue).trim();
        const formStr = String(formValue).trim();

        if (cacheStr !== formStr && cacheStr !== '') {
          // Found a meaningful difference
          return true;
        }
      }

      if (typeof cache.videoYoutubeUrl === 'string' && cache.videoYoutubeUrl !== (this.collectionForm.video && this.collectionForm.video.youtube_url || '')) return true;

      // No meaningful differences found
      return false;
    },
    showBatchDeleteDialog() {
      this.batchDeleteDialogVisible = true;
      this.batchDeleteSelection = [];
    },
    handleBatchDeleteSelectionChange(selection) {
      this.batchDeleteSelection = selection.map(row => ({
        groupIndex: row.groupIndex,
        knowitIndex: row.knowitIndex
      }));
    },
    async confirmBatchDeleteKnowits() {
      if (!this.batchDeleteSelection.length) {
        this.$message.warning('Please select knowits to delete.');
        return;
      }
      try {
        await this.$confirm(
          `Are you sure you want to delete ${this.batchDeleteSelection.length} knowits?`,
          'Batch Delete',
          { type: 'warning' }
        );
        // Remove from frontend (sort by group and knowit index descending for safe splicing)
        const grouped = {};
        this.batchDeleteSelection.forEach(({ groupIndex, knowitIndex }) => {
          if (!grouped[groupIndex]) grouped[groupIndex] = [];
          grouped[groupIndex].push(knowitIndex);
        });
        Object.keys(grouped).forEach(groupIndex => {
          grouped[groupIndex].sort((a, b) => b - a).forEach(knowitIndex => {
            this.collectionForm.groups[groupIndex].knowits.splice(knowitIndex, 1);
          });
        });
        // Sync to backend
        await this.updateCollectionPartial({ groups: this.getGroupsForSync() });
        this.$message.success('Batch delete successful!');
        this.batchDeleteDialogVisible = false;
        this.batchDeleteSelection = [];
      } catch (e) {
        // Cancelled or error
      }
    },
    
    openAIDrawer(taskType) {
      this.currentAITask = taskType;
      
      // Prepare context pot for this task
      if(taskType === 'Collection Cover Generation') {//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a7350894c7849b5b8b458f'; // Pass the chain_id to UserAIDialogue
      }else if (taskType === "Collection Description Polish"){//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a72f8f94c784ae5b8b4590';
      }else if(taskType === "Collection Structure Generation"){//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a72f8e94c784a85b8b4596';
      
      }else if(taskType === "Group Knowits Suggestion"){//OK
        const collectionContext = { ...this.collectionForm, groups: undefined };
        this.prepareContextPot([collectionContext]);
        this.chain_id = '68a72f8f94c7849d5b8b4594';
      }
      
      this.aiDrawerVisible = true;
    },

    prepareContextPot(elements) {
      // Clear existing context pot
      this.$store.dispatch('contextPot/clearAll');
      
      // Add collection info to context pot 
      elements.forEach(element => {
        this.$store.dispatch('contextPot/addItem', element);
      });
      
      
    },

    clearContextPot() {
      this.$store.dispatch('contextPot/clearAll').then(result => {
        if (result.success) {
          this.$message.success(result.message);
        }
      });
    },

    removeFromContextPot(index) {
      this.$store.dispatch('contextPot/removeItem', index).then(result => {
        if (result.success) {
          this.$message.info(result.message);
        }
      });
    },
    
    handleDrawerClose() {
      this.aiDrawerVisible = false;
      this.currentAITask = null;
    },
    
    toggleAIDrawer() {
      this.aiDrawerVisible = !this.aiDrawerVisible;
      if (this.aiDrawerVisible && !this.currentAITask) {
        // Set a default task when opening via toggle
        this.currentAITask = 'Collection Structure Generation';
        this.$store.dispatch('contextPot/syncItems', [{
          collection_name: this.collectionForm.title || 'New Collection',
          collection_description: this.collectionForm.description || '',
          groups: this.collectionForm.groups || []
        }]);
        this.chain_id = '';
      }
    },
    
    getAIContextString() {
      return JSON.stringify({
        task: this.currentAITask,
        collection_id: this.collectionId,
        collection_title: this.collectionForm.title,
        collection_name: this.collectionForm.title, // Keep for backward compatibility
        collection_description: this.collectionForm.description,
        collection_version: this.collectionForm.version_identifier,
        collection_tags: this.collectionForm.tags,
        collection_groups: this.collectionForm.groups.map(group => ({
          title: group.title,
          knowits_count: group.knowits.length,
          knowits: group.knowits.map(knowit => ({
            title: (knowit.post_info && knowit.post_info.title) || 'Untitled Knowit',
            answers_count: (knowit.post_info && knowit.post_info.answers && knowit.post_info.answers.length) || 0
          }))
        })),
        user_id: this.$store.state.user_id,
      });
    },
    
    
    handleCoverGenerated(coverUrl) {
      if (coverUrl) {
        this.collectionForm.cover_url = coverUrl;
        this.aiDrawerVisible = false;
        this.$message.success('Cover generated and applied successfully!');
        // Auto-save the cover
        this.debounceAutoSave();
      }
    },

    handleAIOutput(result, type) {
      console.log('Received AI result:', result, 'Type:', type);
      
      try {
        switch (type) {
          case 'collectionDescriptionPolish':
            // Apply the polished description to the collection
            this.collectionForm.description = typeof result === 'string' ? result : JSON.stringify(result);
            this.$message.success('Description applied successfully!');
            break;
            
          case 'bundleDescriptionGeneration':
            // Apply the generated description to the collection
            this.collectionForm.description = typeof result === 'string' ? result : JSON.stringify(result);
            this.$message.success('Description generated and applied successfully!');
            break;
            
          case 'createCollectionStructure':
            // Apply the generated structure to the collection
            if (Array.isArray(result)) {
              this.collectionForm.groups = result.map(group => ({
                ...group,
                knowits: group.knowits || []
              }));
            } else if (result.groups && Array.isArray(result.groups)) {
              this.collectionForm.groups = result.groups.map(group => ({
                ...group,
                knowits: group.knowits || []
              }));
            }
            this.$message.success('Collection structure applied successfully!');
            break;
            
          case 'groupKnowitsSuggestion':
            // Apply suggested knowits to the active group
            if (Array.isArray(result) && this.collectionForm.groups.length > 0) {
              const lastGroup = this.collectionForm.groups[this.collectionForm.groups.length - 1];
              const newKnowits = result.map(item => ({
                id: Date.now() + Math.random(),
                post_info: item.post_info,
                status: 'PRIVATE'
              }));
              lastGroup.knowits.push(...newKnowits);
            }
            this.$message.success('Knowits suggestions applied successfully!');
            break;
            
          case 'createCollectionCoverPrompt':
            // Handle cover generation result
            if (typeof result === 'string') {
              if (result.startsWith('http')) {
                // MidJourney URL - use directly
                this.collectionForm.cover_url = result;
                this.$message.success('Cover image applied successfully!');
              } else if (result.startsWith('data:image/')) {
                // Gemini Base64 data URL - need to upload to Qiniu
                this.uploadBase64ImageAsCover(result);
              } else {
                this.$message.info('Cover generation result received');
              }
            }
            break;
            
          case 'general':
          default:
            // For general results, show them to the user
            this.$message.info('AI result received and ready to use');
            console.log('General AI result:', result);
            break;
        }
        
        // Auto-save after applying changes
        this.debounceAutoSave();
        
        // Close the AI drawer
        this.aiDrawerVisible = false;
        
      } catch (error) {
        console.error('Error handling AI output:', error);
        this.$message.error('Failed to apply AI result: ' + error.message);
      }
    },
  },
  computed: {
    walkawayDensityClass() {
      const count = (this.collectionForm.walkaway_artifacts || []).length;
      if (count >= 7) return 'sns-list-dense';
      if (count >= 5) return 'sns-list-compact';
      return '';
    },
    globalContextPot() {
      return this.$store.state.contextPot.items;
    },
    totalKnowits() {
      return this.collectionForm.groups.reduce((total, group) => {
        return total + group.knowits.length;
      }, 0);
    },
    allKnowitsForBatchDelete() {
      // Flatten all knowits with group/knowit index for selection
      const result = [];
      this.collectionForm.groups.forEach((group, groupIndex) => {
        group.knowits.forEach((knowit, knowitIndex) => {
          result.push({
            groupIndex,
            knowitIndex,
            groupTitle: group.title || `Group #${groupIndex + 1}`,
            knowitTitle: (knowit.post_info && knowit.post_info.title) || 'Untitled Knowit'
          });
        });
      });
      return result;
    },
  },
  watch: {
    activeTab(tab) {
      if (tab === 'skill-files') this.loadExpersistStatus();
    },
    'collectionForm.title': 'debounceAutoSave',
    'collectionForm.sub_title': 'debounceAutoSave',
    'collectionForm.description': 'debounceAutoSave',
    'collectionForm.status': 'debounceAutoSave',
    'collectionForm.has_custom_price': 'debounceAutoSave',
    'collectionForm.usd_price': 'debounceAutoSave',
    'collectionForm.single_purchase_enabled': 'debounceAutoSave',
    'collectionForm.base_credits': 'debounceAutoSave',
    'collectionForm.cover_prompt': 'debounceAutoSave',
    'collectionForm.cover_url': 'debounceAutoSave',
    'collectionForm.video.youtube_url': 'debounceAutoSave',
    'collectionForm.settings': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.tags': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.target_audience_stages': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.catalogue_section_codes': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.industry_codes': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.country_scope': {
      handler: 'debounceAutoSave',
      deep: true
    },
    'collectionForm.narrative_mode': 'debounceAutoSave',
    'collectionForm.version_identifier': 'debounceAutoSave',
    'collectionForm.subscribers_only': 'debounceAutoSave',
    // Do NOT watch groups for auto-save
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* AI Cover Drawer Styles */
>>> .ai-cover-drawer {
  z-index: 2000 !important;
}

>>> .el-drawer__wrapper {
  z-index: 2000 !important;
}

>>> .ai-cover-drawer .el-drawer__body {
  padding: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}



.new-collection-container {
  min-width: 100% !important; /* Set minimum width */
  max-width: 100vw !important; /* Prevent overflow */
  width: 100% !important; /* Set default width */
  margin: 0 auto !important;
  padding: 20px;
  height: 100vh;
  overflow-y: auto;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  box-sizing: border-box;
}

.el-container {
  margin: 0 auto !important;
}

.el-header {
  text-align: left;
  padding: 20px 0;
  background: white;
  color: #303133;
  margin-bottom: 20px;
  border-bottom: 2px solid #409eff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.el-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0.5px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #303133;
}

.el-main {
  padding: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 20px;
}

.button-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.button-group .el-button {
  margin: 0;
}

.groups-container {
  width: 100%;
  margin: 20px 0;
}

.phases-overview {
  margin: 20px 0 28px;
  padding: 20px;
  background: #f8f7ff;
  border: 1px solid #ded9f7;
  border-radius: 8px;
}

.phases-overview-header h4 {
  margin: 0;
  color: #303133;
  font-size: 18px;
}

.phases-overview-header p {
  margin: 5px 0 0;
  color: #909399;
  font-size: 13px;
}

.phase-list {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

.phase-card {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border: 1px solid #ebe8fa;
  border-radius: 6px;
}

.phase-number {
  min-width: 72px;
  color: #6f5cc2;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.phase-details {
  min-width: 0;
  flex: 1;
}

.phase-details h5 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.phase-details > p {
  margin: 7px 0 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-line;
}

.phase-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 12px;
}

.phase-group-chip {
  display: inline-flex;
  padding: 5px 9px;
  color: #5e4caf;
  background: #f0edff;
  border-radius: 999px;
  font-size: 12px;
}

.phase-group-chip.is-missing {
  color: #c45656;
  background: #fef0f0;
}

.phase-empty-groups {
  display: inline-block;
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
  font-style: italic;
}

.groups-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.group-wrapper {
  position: relative;
}

.group-card {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.group-card-header {
  background-color: #f0f7ff;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid #e6e6e6;
}

.group-card-content {
  padding: 20px;
}

.group-number {
  font-weight: 600;
  color: #606266;
  min-width: 100px;
}

.group-title-input {
  flex: 1;
  height: 36px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  background-color: white;
  transition: all 0.3s ease;
}

.group-title-input:hover {
  border-color: #409eff;
}

.group-title-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.group-title-input::placeholder {
  color: #c0c4cc;
}

.delete-group-btn {
  padding: 7px !important;
}

.paywall-toggle-btn {
  padding: 7px !important;
}

.knowit-lock-btn {
  background: none;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #c0c4cc;
  transition: all 0.2s;
  flex-shrink: 0;
  margin-right: 4px;
}

.knowit-lock-btn:hover {
  border-color: #e6a23c;
  color: #e6a23c;
}

.knowit-lock-btn.is-locked {
  background-color: #fdf6ec;
  border-color: #e6a23c;
  color: #e6a23c;
}

.knowit-lock-btn.is-inherited {
  opacity: 0.5;
  cursor: not-allowed;
}

.knowit-lock-btn:disabled {
  cursor: not-allowed;
}

.knowit-list {
  padding: 10px 0;
}

.knowit-item {
  display: inline-flex;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.knowit-item:hover {
  background: #f0f2f5;
}

.knowit-number {
  width: 100px;
  display: inline-block;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  padding-left: 10px;
}

.knowit-title {
  width: calc(100% - 200px);
  margin: 0 10px;
}

.knowit-delete-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f56c6c;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.knowit-delete-btn:hover {
  background: #f78989;
  transform: scale(1.05);
}

.knowit-delete-btn:active {
  transform: scale(0.95);
}

.groups-container :deep(.add-group-btn) {
  display: block;
  width: 100%;
  margin: 20px 0;
  height: auto;
  padding: 15px 20px;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.groups-container :deep(.add-group-btn:hover) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(103, 194, 58, 0.3);
}

.groups-container :deep(.add-group-btn:active) {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(103, 194, 58, 0.2);
}

.cover-upload {
  text-align: center;
}

.cover-prompt-section {
  margin-top: 18px;
  text-align: left;
}

.cover-prompt-section label {
  display: block;
  margin-bottom: 8px;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
}

.cover-prompt-section textarea {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 12px;
  resize: vertical;
  color: #606266;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.cover-prompt-section textarea:hover {
  border-color: #c0c4cc;
}

.cover-prompt-section textarea:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
}

.cover-prompt-section .cover-hint {
  margin-top: 6px;
  line-height: 1.4;
}

.cover-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 300px;
  height: 424px;
  display: inline-block;
}

.cover-uploader:hover {
  border-color: #409eff;
}

.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  margin-bottom: 8px;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  pointer-events: none;
}

.cover-clickable {
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.cover-uploader :deep(.el-upload) {
  width: 100%;
  height: 100%;
  display: block;
}

.cover-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

.top-section {
  display: flex;
  gap: 20px;
}

.cover-section {
  flex: 1;
}

.details-section {
  flex: 2;
}

.cover-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  position: absolute;
  width: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.cover-label {
  font-size: 16px;
  color: #8c939d;
}

.illustration-section {
  margin-top: 24px;
}

.illustration-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
}

.illustration-header label {
  font-weight: 500;
  color: #606266;
}

.illustration-hint {
  font-size: 12px;
  color: #909399;
}

.illustration-uploader {
  display: block;
  width: 100%;
  aspect-ratio: 10 / 1;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  background: #fafafa;
}

.illustration-uploader:hover {
  border-color: #409eff;
}

.illustration-uploader :deep(.el-upload) {
  width: 100%;
  height: 100%;
  display: block;
}

.illustration-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.illustration-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #8c939d;
}

.illustration-uploader-icon {
  font-size: 24px;
}

.illustration-label {
  font-size: 14px;
}

.no-margin {
  margin: 0 !important;
}

.el-form-item.no-margin .el-form-item__content {
  margin-left: 0 !important;
}

.title-section {
  margin-bottom: 20px;
}

.title-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.title-section input {
  width: 90%;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
}

.title-section input:hover {
  border-color: #c0c4cc;
}

.title-section input:focus {
  outline: none;
  border-color: #409eff;
}

.description-section {
  margin-bottom: 20px;
}

.description-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.description-section textarea {
  width: 90%;
  padding: 10px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 160px;
}

.description-section textarea:hover {
  border-color: #c0c4cc;
}

.description-section textarea:focus {
  outline: none;
  border-color: #409eff;
}

.price-section {
  margin-bottom: 20px;
}

.price-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.price-section input {
  width: 150px;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
}

.price-section input:hover {
  border-color: #c0c4cc;
}

.price-section input:focus {
  outline: none;
  border-color: #409eff;
}

.computed-price {
  display: inline-flex;
  align-items: center;
  min-width: 84px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
  color: #303133;
  font-weight: 600;
}

.knowit-add-button {
  width: 100%;
  height: 40px;
  margin: 10px 0;
  background-color: #f5f7fa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  color: #409eff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.knowit-add-button:hover {
  background-color: #ecf5ff;
  border-color: #409eff;
}

.knowit-add-button:active {
  transform: scale(0.98);
}

.knowit-add-button i {
  font-size: 16px;
}

.content-header {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.content-actions {
  display: flex;
  gap: 10px;
}

.content-stats {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.stat-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #606266;
  font-size: 14px;
}

.stat-item i {
  font-size: 16px;
  color: #409eff;
}

.ai-structure-btn {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ai-structure-btn i {
  font-size: 14px;
}

.drag-handle {
  cursor: move;
  padding: 0 10px;
  color: #909399;
}

.drag-handle:hover {
  color: #409eff;
}

.knowit-edit-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #409eff;
  border: none;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  margin: 0 8px;
}

.knowit-edit-btn:hover {
  background: #66b1ff;
  transform: scale(1.05);
}

.knowit-edit-btn:active {
  transform: scale(0.95);
}

.settings-container {
  padding: 20px;
  min-height: 300px;
}

.setting-item {
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ebeef5;
}

.setting-label {
  flex: 1;
  font-size: 14px;
  color: #606266;
}

.setting-info-icon {
  margin-left: 5px;
  color: #909399;
  font-size: 14px;
  cursor: help;
}

.setting-info-icon:hover {
  color: #409eff;
}

.el-radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.description-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.ai-polish-btn {
  padding: 5px 10px;
  font-size: 12px;
}

.ai-polish-btn i {
  margin-right: 4px;
}

.pricing-container {
  padding: 20px;
  max-width: 600px;
}

.price-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}

.reorder-list {
  min-height: 200px;
}

.reorder-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  margin-bottom: 8px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}

.reorder-item:hover {
  background: #f0f2f5;
}

.group-drag-handle {
  cursor: move;
  padding: 0 10px;
  color: #909399;
}

.group-drag-handle:hover {
  color: #409eff;
}

.group-number {
  font-weight: 500;
  margin-right: 15px;
  color: #606266;
  width: 100px;
}

.group-title {
  flex: 1;
  color: #303133;
}

.insert-group-container {
  position: relative;
  height: 0;
  overflow: visible;
  display: flex;
  justify-content: center;
  opacity: 0;  /* Start hidden */
  transition: opacity 0.2s ease;
}

.insert-group-container:hover {
  opacity: 1;  /* Show on hover of the container itself */
}

.group-wrapper:hover .insert-group-container,
.groups-list:hover > .insert-group-container:first-child {
  opacity: 1;  /* Show on hover of either parent */
}

.insert-group-button {
  position: absolute;
  top: -10px;
  z-index: 1;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.insert-group-button:hover {
  background-color: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.insert-group-button:active {
  transform: translateY(0);
}

.insert-group-button i {
  font-size: 14px;
}

.group-title-label {
  flex: 1;
  padding: 8px 15px;
  font-size: 18px;
  color: #606266;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.group-title-label:hover {
  background-color: #f5f7fa;
}

.group-subtitle-label {
  font-size: 14px;
  font-weight: 400;
  font-style: italic;
  color: #909399;
  margin-left: 4px;
}

.full-width {
  width: 100% !important;
  max-width: 100vw !important;
  margin: 0 auto !important;
}

/* Ensure the container respects parent bounds */
@media screen and (max-width:100%) {
  .new-collection-container {
    min-width: 100% !important;
    width: 100% !important;
  }
}

.status-pending {
  color: #e6a23c;
}

.status-processing {
  color: #409eff;
}

.status-na {
  color: #909399;
}

.dialog-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.group-section {
  margin-bottom: 30px;
}

.group-header {
  background: #f5f7fa;
  padding: 12px 20px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.group-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
  font-weight: 500;
}

/* Update dialog styles to remove shadow immediately */
:deep(.el-dialog__wrapper) {
  background: rgba(0, 0, 0, 0.2) !important;
  transition: none !important;
}

:deep(.el-dialog) {
  box-shadow: none !important;
  border: 1px solid #ebeef5;
  margin-top: 0 !important;
  transition: none !important;
}

:deep(.el-dialog__wrapper[aria-hidden="true"]) {
  background: none !important;
}

:deep(.v-modal) {
  opacity: 0 !important;
  background: none !important;
}

.publicity-status-select {
  width: 260px;
}

.status-option-title {
  float: left;
  font-weight: 600;
}

.status-option-description {
  float: right;
  color: #909399;
  font-size: 12px;
  margin-left: 18px;
}

:deep(.el-switch) {
  height: 22px;
}

.ai-suggest-button {
  background-color: #ecf5ff;
  border-color: #409eff;
  color: #409eff;
}

.ai-suggest-button:hover {
  background-color: #d9ecff;
  border-color: #409eff;
}

.version-section {
  margin-bottom: 20px;
}

.version-section label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #606266;
}

.version-section input {
  width: 90%;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;
  transition: border-color 0.2s;
}

.version-section input:hover {
  border-color: #c0c4cc;
}

.version-section input:focus {
  outline: none;
  border-color: #409eff;
}

.cover-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.ai-generate-btn {
  width: 100%;
  max-width: 300px;
}

.tag-section {
  /* This ensures the entire section is a block and respects vertical margins */
  display: block; 
  margin-bottom: 20px; /* Adds space below the whole section */
}

.tag-section label {
  display: block; /* Make sure the label is a block */
  margin-bottom: 8px; /* This will now create the 8px space you want */
  font-weight: 500;
  color: #606266;
}

.tags-input-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0px 10px;
  width: 90%;
  height:40px;
  cursor: text;
}

.tags-input-container:focus-within {
  border-color: #409eff;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  background-color: #f0f2f5;
  color: #606266;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.remove-tag-btn {
  background: none;
  border: none;
  color: #909399;
  font-size: 16px;
  font-weight: bold;
  margin-left: 6px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.remove-tag-btn:hover {
  color: #303133;
}

.tags-input-container input {
  flex-grow: 1;
  border: none;
  outline: none;
  padding: 4px 0;
  background-color: transparent;
  width: 120px;
}

.settings-flex-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start; /* or center if you want vertical centering */
  gap: 32px; /* space between settings and save button */
}

.settings-container {
  flex: 1 1 auto;
}

.setting-block {
  margin-top: 18px;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafcff;
}

.setting-block-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.setting-helper {
  color: #909399;
  font-size: 12px;
}

.narrative-mode-options .el-radio {
  display: flex;
  align-items: flex-start;
  margin-right: 0;
  white-space: normal;
}

.country-scope-mode {
  margin-bottom: 12px;
}

.country-scope-select {
  width: 100%;
}

.country-option-code {
  float: right;
  color: #909399;
  font-size: 12px;
}

.narrative-mode-label {
  display: block;
  color: #303133;
  font-weight: 600;
  line-height: 1.2;
}

.narrative-mode-helper {
  display: block;
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 1.3;
}

.audience-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.audience-checkbox-group .el-checkbox {
  display: flex;
  align-items: flex-start;
  margin-right: 0;
  white-space: normal;
}

.audience-option-label {
  display: block;
  color: #303133;
  font-weight: 600;
  line-height: 1.2;
}

.audience-option-helper {
  display: block;
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 1.3;
}

.audience-all-note {
  margin-top: 12px;
  color: #67c23a;
  font-size: 12px;
}

.save-section {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start; /* or center */
  margin-left: 24px; /* optional, for extra spacing */
}

.pricing-flex-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start; /* or center for vertical centering */
  gap: 32px; /* space between pricing and save button */
}

.pricing-container {
  flex: 1 1 auto;
}

.save-section {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start; /* or center */
  margin-left: 24px; /* optional, for extra spacing */
}

/* AI Assistant Toggle Button Styles */
.ai-assistant-toggle {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 8px;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: -2px 2px 10px rgba(0, 0, 0, 0.2);
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.ai-assistant-toggle:hover {
  padding-left: 12px;
  padding-right: 12px;
  box-shadow: -3px 3px 15px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.ai-assistant-toggle.active {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  transform: translateY(-50%) translateX(-5px);
}

.ai-assistant-toggle i {
  font-size: 20px;
  writing-mode: horizontal-tb;
}

.ai-assistant-toggle span {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
}

/* Ensure the toggle button appears above other elements */
.new-collection-container {
  position: relative;
}

/* ── Social Cards ────────────────────────────────────────────── */
.social-cards-container {
  padding: 16px 0;
  position: relative;
}

/* Download overlay */
.sns-download-overlay {
  background: #ffffff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 200px;
}
.sns-download-title {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}
.sns-download-btn {
  background: none;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
  color: #303133;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
}
.sns-download-btn:hover {
  background: #f0f7ff;
  border-color: #409EFF;
  color: #409EFF;
}

/* Card hover highlight */
.sns-card:hover {
  outline: 2px solid rgba(64, 158, 255, 0.4);
  outline-offset: 2px;
}

/* Card row layout: preview left, editor right */
.card-row {
  border-bottom: 1px solid #f0f2f5;
  padding-bottom: 24px;
}
.card-row-title {
  font-size: 14px;
  color: #303133;
  margin-bottom: 12px;
}
.card-row-content {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}
.card-row-preview {
  flex-shrink: 0;
  width: 500px;
}
.card-row-editor {
  flex: 1;
  min-width: 0;
}
.card-row-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #c0c4cc;
  font-size: 13px;
  border: 2px dashed #e4e7ed;
  border-radius: 12px;
}

@media (max-width: 768px) {
  .card-row-content {
    flex-direction: column;
  }
  .card-row-preview {
    width: 100%;
    display: flex;
    justify-content: center;
  }
}

.sns-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  background: #ffffff;
  flex-shrink: 0;
}

.sns-card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 6% 7% 20% 7%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}
/* Logo watermark — large faded LearningFirst logo near top-right corner */
.sns-card-inner::before {
  content: '';
  position: absolute;
  top: 4%;
  right: -8%;
  width: 50%;
  height: 50%;
  background-image: url('/LearningFirst_logo.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: top right;
  opacity: 0.05;
  pointer-events: none;
  z-index: 0;
}
/* Make sure all content sits above the watermark */
.sns-card-inner > * {
  position: relative;
  z-index: 1;
}

/* Universal 5:7 portrait ratio for all card types */
.sns-card-quote,
.sns-card-list,
.sns-card-review,
.sns-card-title,
.sns-card-cover {
  width: 500px;
  aspect-ratio: 5 / 7;
  container-type: inline-size;
}

/* Cover card */
.sns-card-cover {
  background: linear-gradient(165deg, #f0f4fa 0%, #ffffff 40%, #f5f8fc 100%);
  border: 1px solid #d8dfe8;
}
.sns-card-inner--cover {
  padding: 5% 5% 5% 5%;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}
.sns-card-inner--cover::before {
  display: none;
}
.sns-cover-image-area {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.sns-cover-main-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
}
.sns-cover-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 3cqi;
}
.sns-cover-brand {
  font-size: 2.4cqi;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #409EFF;
  display: flex;
  align-items: center;
  gap: 1cqi;
  line-height: 1;
}

.sns-card-quote {
  background: linear-gradient(165deg, #f0f7ff 0%, #ffffff 40%, #f5f9ff 100%);
  border: 1px solid #c3d9f5;
}

.sns-card-list,
.sns-card-review {
  background: linear-gradient(165deg, #f8faff 0%, #ffffff 50%, #fafafa 100%);
  border: 1px solid #e7ecf3;
}

.sns-card-title {
  background: linear-gradient(135deg, #1159AF 0%, #1a2a4a 100%);
  border: none;
}
.sns-card-title .sns-card-inner {
  justify-content: center;
  align-items: center;
  text-align: center;
}
.sns-card-title .sns-brand-tag {
  color: rgba(255,255,255,0.5);
}
.sns-card-title .sns-title-main {
  font-size: 5cqi;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;
  margin-bottom: 2cqi;
}
.sns-card-title .sns-title-sub {
  font-size: 2.8cqi;
  color: rgba(255,255,255,0.75);
  line-height: 1.5;
  margin-bottom: 3cqi;
}
.sns-card-title .sns-title-stats {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
  font-size: 2.6cqi;
  letter-spacing: 0.03em;
}
.sns-card-title .sns-card-footer {
  margin-top: auto;
  padding-top: 16px;
}

/* Brand tag */
.sns-brand-tag {
  font-size: 2.8cqi;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #409EFF;
  display: flex;
  align-items: center;
  gap: 1cqi;
  margin-left: auto;
  line-height: 1;
}
.sns-logo-icon {
  width: 3.6cqi;
  height: 3.6cqi;
  object-fit: contain;
}

/* Type label (top-right) — inside footer flex row */
.sns-review-label-corner {
  font-size: 2.6cqi;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #409EFF;
}

/* Quote card specifics */
.sns-quote-mark {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 10cqi;
  line-height: 1;
  color: rgba(64, 158, 255, 0.2);
  margin-bottom: 1cqi;
}
.sns-quote-mark-inline {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 7cqi;
  line-height: 1;
  color: rgba(64, 158, 255, 0.25);
  position: absolute;
  top: -3.5cqi;
  left: -1cqi;
}
.sns-quote-insight {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 3.6cqi;
  font-weight: 600;
  line-height: 1.45;
  color: #1a2332;
  margin-bottom: 2.5cqi;
  position: relative;
  padding-top: 2cqi;
}
.sns-quote-detail {
  font-size: 3.8cqi;
  line-height: 1.6;
  color: #8895a6;
}

/* List card specifics */
.sns-list-title {
  font-size: 4.5cqi;
  font-weight: 700;
  color: #1a2332;
  margin-top: 2.5cqi;
  margin-bottom: 3cqi;
}
.sns-list-items {
  flex: none;
}
.sns-list-item {
  display: flex;
  align-items: baseline;
  gap: 2cqi;
  margin-bottom: 2cqi;
  padding-bottom: 2cqi;
  border-bottom: 1px solid #f0f2f5;
}
.sns-list-item:last-child {
  border-bottom: none;
}
.sns-list-number {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 6.5cqi;
  font-weight: 700;
  color: #409EFF;
  min-width: 6cqi;
  text-align: right;
  line-height: 1;
}
.sns-list-text {
  font-size: 4cqi;
  line-height: 1.45;
  color: #303133;
}

/* Walkaway card overflow protection */
.sns-card-list .sns-card-inner {
  overflow: hidden;
}

/* Compact density: 5-6 items */
.sns-list-compact .sns-list-title {
  font-size: 3.8cqi;
  margin-bottom: 2cqi;
}
.sns-list-compact .sns-list-item {
  margin-bottom: 1.2cqi;
  padding-bottom: 1.2cqi;
}
.sns-list-compact .sns-list-number {
  font-size: 5cqi;
  min-width: 6cqi;
}
.sns-list-compact .sns-list-text {
  font-size: 3.2cqi;
  line-height: 1.35;
}

/* Dense: 7-8 items */
.sns-list-dense .sns-card-inner {
  padding: 4%;
}
.sns-list-dense .sns-list-title {
  font-size: 3.4cqi;
  margin-bottom: 1.5cqi;
}
.sns-list-dense .sns-list-item {
  margin-bottom: 0.8cqi;
  padding-bottom: 0.8cqi;
  gap: 1.2cqi;
}
.sns-list-dense .sns-list-number {
  font-size: 4.2cqi;
  min-width: 4cqi;
}
.sns-list-dense .sns-list-text {
  font-size: 2.8cqi;
  line-height: 1.3;
}

/* Review card specifics */
.sns-review-label {
  font-size: 2.2cqi;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #409EFF;
  margin-bottom: 2.5cqi;
}
.sns-review-headline {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 3.2cqi;
  font-weight: 600;
  line-height: 1.45;
  color: #1a2332;
  margin-bottom: 2cqi;
}
.sns-review-body {
  font-size: 3.8cqi;
  line-height: 1.6;
  color: #8895a6;
}
.sns-reviewer {
  font-size: 2.4cqi;
  font-weight: 600;
  color: #409EFF;
  margin-bottom: 1cqi;
}

/* Shared footer — now at top as collection title */
.sns-card-footer {
  margin-bottom: 3cqi;
  order: -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.sns-collection-title {
  font-size: 3.4cqi;
  color: #303133;
  font-weight: 600;
  font-style: italic;
}
.sns-collection-icon {
  width: 3.6cqi;
  height: 3.6cqi;
  flex-shrink: 0;
  vertical-align: middle;
  margin-right: 1cqi;
  position: relative;
  top: -0.2cqi;
}
.sns-collection-cover {
  width: 8cqi;
  height: 11.2cqi;
  border-radius: 0.6cqi;
  object-fit: cover;
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
  flex-shrink: 0;
}
/* Wrap cover + brand tag together at the bottom */
.sns-card-bottom-bar {
  margin-top: auto;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  order: 99;
  position: relative;
}
.sns-genre {
  font-size: 1.8cqi;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #c0c4cc;
  margin-top: 0.4cqi;
}
.sns-review-footer {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.sns-editorial-label {
  font-size: 2.2cqi;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #909399;
}

/* SNS Promotion Slide Cards */
.sns-card-sns-slide {
  width: 500px;
  aspect-ratio: 5 / 7;
  container-type: inline-size;
  border: 1px solid #e7ecf3;
}
/* Summary Pack — stacked bullet list card */
.sns-summary-hook {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 3.8cqi;
  font-weight: 700;
  line-height: 1.35;
  color: #1a2332;
  margin-top: 2.5cqi;
  margin-bottom: 3cqi;
  order: 1;
}
.sns-page-number {
  font-size: 2cqi;
  font-weight: 600;
  color: #c0c4cc;
  letter-spacing: 0.05em;
  order: 0;
  margin-bottom: 2.5cqi;
}
.sns-summary-continued {
  position: absolute;
  bottom: 14%;
  right: 7%;
  font-size: 2.2cqi;
  font-style: italic;
  color: #909399;
  text-align: right;
}
.sns-summary-list {
  flex: none;
  order: 2;
}
.sns-summary-item {
  display: flex;
  align-items: baseline;
  gap: 1.5cqi;
  margin-bottom: 1.8cqi;
  padding-bottom: 1.8cqi;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}
.sns-summary-item:last-child {
  border-bottom: none;
}
.sns-mode-label {
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.sns-mode-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.sns-summary-num {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 3.6cqi;
  font-weight: 700;
  color: #409EFF;
  min-width: 4cqi;
  text-align: right;
  line-height: 1;
  flex-shrink: 0;
}
.sns-summary-text {
  font-size: 4cqi;
  line-height: 1.5;
  color: #303133;
}
.sns-summary-cta {
  font-size: 3.4cqi;
  font-weight: 600;
  font-style: italic;
  color: #409EFF;
  margin-top: 2cqi;
  padding-bottom: 3cqi;
  text-align: left;
  order: 3;
}

.sns-slides-carousel {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 12px;
}
.sns-slides-carousel::-webkit-scrollbar {
  height: 6px;
}
.sns-slides-carousel::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

.sns-card-sns-slide .sns-card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 6% 7%;
  box-sizing: border-box;
  overflow: hidden;
}

/* Type-specific backgrounds */
.sns-slide-summary_pack {
  background: linear-gradient(165deg, #f0f7ff 0%, #ffffff 50%, #f5f9ff 100%);
  border-color: #c3d9f5;
}
.sns-slide-step_by_step {
  background: linear-gradient(165deg, #f0f7ff 0%, #ffffff 50%, #f5f9ff 100%);
  border-color: #c3d9f5;
}
.sns-slide-contrast {
  background: linear-gradient(165deg, #f0f7ff 0%, #ffffff 50%, #f5f9ff 100%);
  border-color: #c3d9f5;
}
.sns-slide-checklist {
  background: linear-gradient(165deg, #f0f7ff 0%, #ffffff 50%, #f5f9ff 100%);
  border-color: #c3d9f5;
}

.sns-slide-type-label {
  font-size: 2.2cqi;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #409EFF;
  margin-bottom: 2.5cqi;
}
.sns-slide-summary_pack .sns-slide-type-label { color: #409EFF; }
.sns-slide-contrast .sns-slide-type-label { color: #409EFF; }
.sns-slide-checklist .sns-slide-type-label { color: #409EFF; }

.sns-slide-hook {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 4cqi;
  font-weight: 700;
  line-height: 1.35;
  color: #1a2332;
  flex: 1;
}

.sns-slide-step-num {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 6cqi;
  font-weight: 700;
  color: #409EFF;
  margin-bottom: 1.5cqi;
  line-height: 1;
}
.sns-slide-summary_pack .sns-slide-step-num { color: #409EFF; }
.sns-slide-contrast .sns-slide-step-num { color: #409EFF; }
.sns-slide-checklist .sns-slide-step-num { color: #409EFF; }

.sns-check-icon {
  font-size: 4cqi;
  vertical-align: middle;
}

.sns-slide-content {
  font-size: 3cqi;
  line-height: 1.6;
  color: #303133;
  flex: 1;
}

.sns-slide-cta .sns-card-inner {
  justify-content: center;
  align-items: center;
  text-align: center;
}
.sns-slide-cta-text {
  font-size: 3.6cqi;
  font-weight: 700;
  line-height: 1.4;
  color: #1a2332;
}

.sns-slide-page-num {
  font-size: 2.2cqi;
  color: #c0c4cc;
  font-weight: 500;
}

.sns-card-sns-slide .sns-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

/* ===== Dynamic font density based on word count ===== */

/* XXL–MD: collection title pinned top-left, brand pinned bottom-right, content starts at 25% */
.sns-words-xxl .sns-card-inner,
.sns-words-xl .sns-card-inner,
.sns-words-lg .sns-card-inner,
.sns-words-md .sns-card-inner {
  position: relative;
  justify-content: flex-start !important;
  padding-top: 25% !important;
  padding-bottom: 14% !important;
}
/* Remove flex:1 stretch so content can center */
.sns-words-xxl .sns-slide-hook,
.sns-words-xl .sns-slide-hook,
.sns-words-lg .sns-slide-hook,
.sns-words-md .sns-slide-hook,
.sns-words-xxl .sns-slide-content,
.sns-words-xl .sns-slide-content,
.sns-words-lg .sns-slide-content,
.sns-words-md .sns-slide-content,
.sns-words-xxl .sns-list-items,
.sns-words-xl .sns-list-items,
.sns-words-lg .sns-list-items,
.sns-words-md .sns-list-items {
  flex: none !important;
}
.sns-card-bottom-bar {
  position: absolute;
  bottom: 6%;
  left: 7%;
  right: 7%;
  top: auto;
  margin: 0;
}
.sns-words-xxl .sns-card-footer,
.sns-words-xl .sns-card-footer,
.sns-words-lg .sns-card-footer,
.sns-words-md .sns-card-footer {
  position: absolute;
  top: 6%;
  left: 7%;
  right: 7%;
  bottom: auto;
  border-top: none;
}

/* XXL: ≤10 words — title-size text */
.sns-words-xxl .sns-quote-insight { font-size: 9cqi; }
.sns-words-xxl .sns-quote-detail { font-size: 4cqi; }
.sns-words-xxl .sns-review-headline { font-size: 8.5cqi; }
.sns-words-xxl .sns-review-body { font-size: 4cqi; }
.sns-words-xxl .sns-slide-hook { font-size: 9cqi; }
.sns-words-xxl .sns-slide-content { font-size: 7.5cqi; }
.sns-words-xxl .sns-slide-cta-text { font-size: 8.5cqi; }
.sns-words-xxl .sns-quote-mark-inline { font-size: 13cqi; }

/* XL: ≤20 words — big bold text */
.sns-words-xl .sns-quote-insight { font-size: 7.5cqi; }
.sns-words-xl .sns-quote-detail { font-size: 3.6cqi; }
.sns-words-xl .sns-review-headline { font-size: 7cqi; }
.sns-words-xl .sns-review-body { font-size: 3.6cqi; }
.sns-words-xl .sns-slide-hook { font-size: 7.5cqi; }
.sns-words-xl .sns-slide-content { font-size: 6cqi; }
.sns-words-xl .sns-slide-cta-text { font-size: 7cqi; }
.sns-words-xl .sns-quote-mark-inline { font-size: 11cqi; }

/* LG: 21-40 words — large text */
.sns-words-lg .sns-quote-insight { font-size: 5.5cqi; }
.sns-words-lg .sns-quote-detail { font-size: 3.6cqi; }
.sns-words-lg .sns-review-headline { font-size: 5cqi; }
.sns-words-lg .sns-review-body { font-size: 3.6cqi; }
.sns-words-lg .sns-slide-hook { font-size: 5.5cqi; }
.sns-words-lg .sns-slide-content { font-size: 5cqi; }
.sns-words-lg .sns-slide-cta-text { font-size: 5cqi; }
.sns-words-lg .sns-quote-mark-inline { font-size: 9cqi; }

/* MD: 41-70 words */
.sns-words-md .sns-quote-insight { font-size: 4.2cqi; }
.sns-words-md .sns-quote-detail { font-size: 3.2cqi; }
.sns-words-md .sns-review-headline { font-size: 4.4cqi; }
.sns-words-md .sns-review-body { font-size: 3.2cqi; }
.sns-words-md .sns-slide-hook { font-size: 4.6cqi; }
.sns-words-md .sns-slide-content { font-size: 3.8cqi; }
.sns-words-md .sns-slide-cta-text { font-size: 4cqi; }
.sns-words-md .sns-quote-mark-inline { font-size: 7cqi; }

/* SM: 71-120 words — compact */
.sns-words-sm .sns-quote-insight { font-size: 3.4cqi; }
.sns-words-sm .sns-quote-detail { font-size: 2.8cqi; }
.sns-words-sm .sns-review-headline { font-size: 3.6cqi; }
.sns-words-sm .sns-review-body { font-size: 2.8cqi; }
.sns-words-sm .sns-slide-hook { font-size: 3.8cqi; }
.sns-words-sm .sns-slide-content { font-size: 3.2cqi; }
.sns-words-sm .sns-slide-cta-text { font-size: 3.2cqi; }
.sns-words-sm .sns-quote-mark-inline { font-size: 5.5cqi; }

/* XS: 120+ words — dense */
.sns-words-xs .sns-quote-insight { font-size: 2.8cqi; }
.sns-words-xs .sns-quote-detail { font-size: 2.6cqi; }
.sns-words-xs .sns-review-headline { font-size: 3cqi; }
.sns-words-xs .sns-review-body { font-size: 2.4cqi; }
.sns-words-xs .sns-slide-hook { font-size: 3.2cqi; }
.sns-words-xs .sns-slide-content { font-size: 2.6cqi; }
.sns-words-xs .sns-slide-cta-text { font-size: 2.6cqi; }
.sns-words-xs .sns-quote-mark-inline { font-size: 4.5cqi; }

/* ── Long-form video summary block ─────────────────────── */
.video-status-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  padding: 2px 10px;
  margin-left: 10px;
  border-radius: 12px;
  text-transform: uppercase;
  vertical-align: middle;
}
.video-status-draft     { background: #f0f4ff; color: #2c5fa8; border: 1px solid #b9cdf2; }
.video-status-rendered  { background: #fff7e6; color: #b67900; border: 1px solid #f0d49b; }
.video-status-published { background: #ebf7ec; color: #2c8c4a; border: 1px solid #b6dec0; }

.video-summary-block {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 20px;
  background: #fafbfc;
}
.video-summary-row {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 24px;
  margin-bottom: 20px;
}
.video-spec-card {
  border-radius: 6px;
  padding: 24px 22px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  overflow: hidden;
}
.video-spec-card-label {
  font-size: 11px;
  letter-spacing: 0.3em;
  font-weight: 500;
  text-transform: uppercase;
  opacity: 0.85;
}
.video-spec-card-title {
  font-size: 22px;
  line-height: 1.3;
  font-weight: 500;
  flex: 1 1 auto;
}
.video-spec-swatch-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.video-spec-swatch {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.video-spec-card-meta {
  font-size: 11px;
  letter-spacing: 0.05em;
  opacity: 0.75;
}
.video-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 0;
}
.video-stat {
  border: 1px solid #ebeef5;
  background: #fff;
  padding: 18px 20px;
  text-align: left;
}
.video-stat:nth-child(1), .video-stat:nth-child(2) { border-bottom: none; }
.video-stat:nth-child(1), .video-stat:nth-child(3) { border-right: none; }
.video-stat-num {
  font-size: 32px;
  font-weight: 600;
  color: #303133;
  line-height: 1;
  margin-bottom: 8px;
}
.video-stat-label {
  font-size: 11px;
  letter-spacing: 0.18em;
  color: #909399;
  text-transform: uppercase;
}
.video-section-summary {
  border-top: 1px solid #ebeef5;
  padding: 16px 0;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}
.video-section-label {
  font-size: 11px;
  letter-spacing: 0.18em;
  color: #909399;
  text-transform: uppercase;
  margin-right: 12px;
}
.video-picked-chip {
  display: inline-block;
  background: #eff4fb;
  color: #2c5fa8;
  border: 1px solid #cdddf2;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  margin-right: 6px;
  margin-bottom: 4px;
}
.video-section-rationale {
  margin-top: 10px;
  font-style: italic;
  color: #909399;
  line-height: 1.5;
}
.video-preview-block {
  border-top: 1px solid #ebeef5;
  padding: 14px 0;
  margin-bottom: 12px;
}
.video-preview-label {
  font-size: 11px;
  letter-spacing: 0.18em;
  color: #909399;
  text-transform: uppercase;
  margin-bottom: 10px;
}
.video-preview-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.video-preview-list li {
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
  font-size: 13px;
  line-height: 1.5;
}
.video-preview-list li:last-child { border-bottom: none; }
.video-preview-from {
  display: inline-block;
  font-size: 11px;
  letter-spacing: 0.12em;
  color: #909399;
  margin-right: 10px;
  text-transform: uppercase;
  min-width: 90px;
}
.video-preview-promise {
  color: #303133;
}
.video-action-row {
  border-top: 1px solid #ebeef5;
  padding-top: 14px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.video-action-hint {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}
.video-youtube-field {
  margin: 12px 0 18px;
}
.video-youtube-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.video-youtube-field p {
  margin: 7px 0 0;
  font-size: 12px;
  color: #909399;
}
.video-bundle-preview-wrap {
  margin-top: 14px;
}
.video-bundle-preview-iframe {
  display: block;
  width: 100%;
  height: 540px;
  margin-top: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

/* ── SNS card captions ─────────────────────────────────── */
.sns-captions-block {
  margin-top: 16px;
  border-top: 1px dashed #dcdfe6;
  padding-top: 14px;
}
.sns-captions-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
}
.sns-captions-title {
  font-size: 12px;
  letter-spacing: 0.16em;
  color: #909399;
  text-transform: uppercase;
  font-weight: 500;
}
.sns-captions-hint {
  font-size: 11px;
  color: #c0c4cc;
  font-style: italic;
}
.sns-captions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.sns-caption-cell {
  background: #f7f9fc;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px 12px;
}
.sns-caption-platform-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}
.sns-caption-platform {
  font-size: 11px;
  letter-spacing: 0.12em;
  color: #606266;
  text-transform: uppercase;
  font-weight: 500;
}
.sns-caption-count {
  font-size: 10px;
  color: #909399;
  font-family: 'SF Mono', Menlo, monospace;
}
.sns-caption-count-over {
  color: #f56c6c;
  font-weight: 500;
}
.sns-caption-rendered {
  font-size: 12px;
  line-height: 1.5;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}
.sns-caption-hashtags {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.sns-caption-hashtag {
  font-size: 11px;
  color: #2c5fa8;
  background: #eff4fb;
  border-radius: 3px;
  padding: 1px 6px;
}

/* ─── Marketing tab — collapse title-bar styling ─── */
.marketing-collapse-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-right: 12px;
}
.marketing-status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  margin-right: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.marketing-status-badge.is-set {
  background: #f0f9eb;
  color: #67c23a;
}
.marketing-status-badge.is-empty {
  background: #f4f4f5;
  color: #909399;
}
.marketing-side-meta {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

/* ─── Version Updates tab ─── */
.versions-panel {
  padding: 24px 4px 8px;
}
.versions-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 24px;
}
.versions-header h3 {
  margin: 0;
  color: #303133;
  font-size: 20px;
}
.versions-header p {
  margin: 6px 0 0;
  color: #909399;
  font-size: 13px;
}
.versions-toolbar {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}
.version-loading {
  padding: 32px 0;
  color: #909399;
  text-align: center;
}
.versions-empty {
  margin-top: 0;
}
.version-list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-left: 18px;
}
.version-list::before {
  content: "";
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 5px;
  width: 2px;
  background: #d9ecff;
}
.version-item {
  position: relative;
  display: flex;
  gap: 14px;
}
.version-dot {
  position: absolute;
  left: -18px;
  top: 18px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #409eff;
  box-shadow: 0 0 0 4px #ecf5ff;
}
.version-card {
  flex: 1;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 16px 18px;
  background: #fff;
  box-shadow: 0 3px 10px rgba(64, 158, 255, 0.06);
}
.version-card-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
}
.version-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.version-select {
  margin-right: 0;
}
.version-select >>> .el-checkbox__label {
  color: #909399;
  font-size: 12px;
  padding-left: 6px;
}
.version-date {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}
.version-card h4 {
  margin: 12px 0 8px;
  color: #303133;
  font-size: 16px;
}
.version-notes {
  color: #606266;
  line-height: 1.7;
  font-size: 14px;
}
.version-notes >>> p {
  margin: 0 0 8px;
}
.version-notes >>> ul,
.version-notes >>> ol {
  margin: 0;
  padding-left: 20px;
}
.version-actions {
  flex-shrink: 0;
}
.delete-link {
  color: #f56c6c;
}
.skill-files-panel {
  max-width: 760px;
  padding: 24px;
  color: #303133;
}
.skill-files-panel p {
  color: #606266;
  line-height: 1.5;
}
.skill-file-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
}
.skill-file-picker {
  display: inline-flex;
  align-items: center;
  padding: 9px 14px;
  border: 1px solid #409eff;
  border-radius: 4px;
  color: #409eff;
  cursor: pointer;
}
.skill-file-picker input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.skill-file-status {
  padding: 12px;
  border-radius: 4px;
  background: #f0f9eb;
  overflow-wrap: anywhere;
  line-height: 1.6;
}
.skill-file-picker.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.skill-file-error {
  color: #f56c6c;
}
.expersist-block {
  padding: 16px 0;
  border-top: 1px solid #ebeef5;
}
.expersist-block h4 {
  margin: 0 0 4px;
}
</style>
